// ═══════════════════════════════════════════════════════════════════
// ACCOUNTING — البرنامج المحاسبي (v3.3.487)
// Read-only mirror of the external accounting system (Daftra).
// 4 Tabs:
//   1. Transactions — جدول كل القيود مع فلاتر
//   2. Cost Centers — ملخص مراكز التكلفة
//   3. Accounts — ملخص الحسابات (Chart of Accounts)
//   4. Settings — إعدادات API + زر المزامنة
// ═══════════════════════════════════════════════════════════════════
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { accounting, projects as projectsApi } from "../services/api";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";
import {
  FiDatabase, FiRefreshCw, FiSettings, FiList,
  FiPieChart, FiBook, FiSearch, FiCalendar,
  FiSave, FiCheckCircle, FiAlertCircle, FiX,
  FiPlus, FiTrash2, FiEdit2, FiChevronDown, FiChevronLeft, FiFolder, FiFolderPlus, FiTag, FiArrowRight, FiBookOpen,
} from "react-icons/fi";

const fmt = (n) => Math.round(Number(n) || 0).toLocaleString("en-US");
const fmtDate = (d) => {
  if (!d) return "—";
  try {
    const date = new Date(d);
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yy = date.getFullYear();
    return `${dd}/${mm}/${yy}`;
  } catch { return String(d); }
};
const fmtDateTime = (d) => {
  if (!d) return "—";
  try {
    const date = new Date(d);
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yy = date.getFullYear();
    const hh = String(date.getHours()).padStart(2, "0");
    const mn = String(date.getMinutes()).padStart(2, "0");
    return `${dd}/${mm}/${yy} ${hh}:${mn}`;
  } catch { return String(d); }
};

export default function AccountingPage() {
  const { user } = useAuth();
  // v3.3.493 — Treat accounting module as a single permission unit:
  // anyone who can view it (المحاسب) gets full access (edit tree, sync, settings).
  // Admin always has full access.
  const canSync = (user?.role === "admin" && !user?.impersonating) || user?.is_admin || user?.can_view_accounting || user?.can_sync_accounting;

  const [tab, setTab] = useState("cc_report"); // v3.3.508 — default to the CC report

  return (
    <div style={{ padding: "20px 24px", direction: "rtl", maxWidth: 1600, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
        <FiDatabase size={28} color="#0e7490" />
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, color: "#0f172a" }}>📒 البرنامج المحاسبي</div>
          <div style={{ fontSize: 12, color: "#64748b" }}>سحب القيود المحاسبية من النظام الخارجي (Daftra) ومتابعتها</div>
        </div>
      </div>

      {/* Tabs — v3.3.508 reordered: report first (default), then journals, tree, cost centers, accounts, settings */}
      {/* v3.3.510 — Added 'statement' tab (detailed account/cost-center statement with export) */}
      <div style={{ display: "flex", gap: 4, borderBottom: "2px solid #e2e8f0", marginBottom: 16, marginTop: 14, flexWrap: "wrap" }}>
        {[
          { key: "cc_report",    label: "📊 التقرير المحاسبي" },
          { key: "statement",    label: "📑 كشف الحساب المفصّل" },
          { key: "vouchers",     label: "🧾 سندات القبض/الصرف" },
          { key: "transactions", label: "📋 القيود المحاسبية" },
          { key: "cc_tree",      label: "🌳 شجرة مراكز التكلفة" },
          { key: "cost_centers", label: "🎯 مراكز التكلفة" },
          { key: "accounts",     label: "📚 دليل الحسابات" },
          { key: "settings",     label: "⚙️ الإعدادات والمزامنة" },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: "9px 16px", border: "none",
              borderRadius: "8px 8px 0 0",
              background: tab === t.key ? "#0e7490" : "transparent",
              color: tab === t.key ? "#fff" : "#475569",
              fontWeight: 700, fontSize: 13, cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "transactions" && <TransactionsTab />}
      {tab === "cost_centers" && <CostCentersTab />}
      {tab === "accounts"     && <AccountsTab />}
      {tab === "cc_tree"      && <CCTreeTab canEdit={canSync} />}
      {tab === "cc_report"    && <CCReportTab />}
      {tab === "statement"    && <StatementTab />}
      {tab === "vouchers"     && <VouchersTab canSync={canSync} />}
      {tab === "settings"     && <SettingsTab canSync={canSync} />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// v3.3.585 — VouchersTab — سندات القبض والصرف (Daftra expense entity)
// Shows all receipt/payment vouchers from Daftra with filters + sync button.
// ═══════════════════════════════════════════════════════════════════
function VouchersTab({ canSync }) {
  const [items, setItems]   = useState([]);
  const [total, setTotal]   = useState(0);
  const [sums, setSums]     = useState({ total_receipts: 0, total_payments: 0, count_receipts: 0, count_payments: 0 });
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [type, setType]       = useState("all");      // all | receipt | payment
  const [status, setStatus]   = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo]     = useState("");
  const [q, setQ]               = useState("");
  const [page, setPage]         = useState(0);
  const [selected, setSelected] = useState(null);
  // v3.3.591 — voucher waiting to have its unit picked (cascading 4-step modal)
  const [pickerVoucher, setPickerVoucher] = useState(null);
  // v3.3.592 — bulk Excel link export/import
  const [exporting, setExporting]   = useState(false);
  const [importing, setImporting]   = useState(false);
  const [linkReport, setLinkReport] = useState(null);
  const fileInputRef = React.useRef(null);
  const limit = 50;

  // Build the same filter params the list uses, so export matches the view.
  const filterParams = useCallback(() => {
    const params = { type };
    if (status)   params.status    = status;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo)   params.date_to   = dateTo;
    if (q)        params.q         = q;
    return params;
  }, [type, status, dateFrom, dateTo, q]);

  const doExport = async () => {
    setExporting(true);
    try {
      await accounting.exportVoucherLinkTemplate(filterParams());
      toast.success("✅ تم تنزيل قالب الربط");
    } catch (e) { toast.error("فشل التصدير: " + (e?.response?.data?.error || e.message)); }
    finally { setExporting(false); }
  };

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    setImporting(true);
    try {
      const r = await accounting.importVoucherLinks(file);
      const rep = r.data.report || {};
      setLinkReport(rep);
      const parts = [];
      if (rep.linked)   parts.push(`${rep.linked} مربوط`);
      if (rep.unlinked) parts.push(`${rep.unlinked} أُلغي ربطه`);
      if (rep.not_found?.length) parts.push(`${rep.not_found.length} غير معروف`);
      if (rep.ambiguous?.length) parts.push(`${rep.ambiguous.length} متعدّد`);
      toast.success(`تم الاستيراد: ${parts.join(" · ") || "لا تغييرات"}`);
      load();
    } catch (e) { toast.error("فشل الاستيراد: " + (e?.response?.data?.error || e.message)); }
    finally { setImporting(false); }
  };

  const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 });
  const fmtDate = (d) => d ? new Date(d).toISOString().slice(0,10) : "—";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { type, limit, offset: page * limit };
      if (status)   params.status = status;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo)   params.date_to   = dateTo;
      if (q)        params.q        = q;
      const r = await accounting.listVouchers(params);
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setSums(r.data.sums || {});
    } catch (e) { toast.error("فشل تحميل السندات: " + (e?.response?.data?.error || e.message)); }
    finally { setLoading(false); }
  }, [type, status, dateFrom, dateTo, q, page]);

  useEffect(() => { load(); }, [load]);

  const doSync = async () => {
    if (!canSync) { toast.error("ليس لديك صلاحية مزامنة"); return; }
    setSyncing(true);
    try {
      const r = await accounting.syncVouchers();
      toast.success(`✅ تمت مزامنة ${r.data.upserted} سند من أصل ${r.data.total}`);
      load();
    } catch (e) { toast.error("فشل المزامنة: " + (e?.response?.data?.error || e.message)); }
    finally { setSyncing(false); }
  };

  const StatusBadge = ({ s }) => {
    const m = {
      issued:    { label: "صادر",    bg: "#fef3c7", color: "#92400e" },
      approved:  { label: "معتمد",   bg: "#dcfce7", color: "#166534" },
      confirmed: { label: "مؤكد",    bg: "#dcfce7", color: "#166534" },
      draft:     { label: "مسودة",   bg: "#e2e8f0", color: "#475569" },
      cancelled: { label: "ملغي",    bg: "#fee2e2", color: "#991b1b" },
    };
    const x = m[s] || { label: s || "—", bg: "#e2e8f0", color: "#475569" };
    return <span style={{ background: x.bg, color: x.color, padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 700 }}>{x.label}</span>;
  };

  const TypeBadge = ({ t }) => (
    <span style={{
      background: t === "receipt" ? "#dcfce7" : "#fee2e2",
      color: t === "receipt" ? "#166534" : "#991b1b",
      padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 700,
    }}>
      {t === "receipt" ? "↘ قبض" : "↗ صرف"}
    </span>
  );

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: "#0f172a", margin: 0 }}>🧾 سندات القبض والصرف</h2>
          <p style={{ fontSize: 12, color: "#64748b", margin: "2px 0 0 0" }}>عرض السندات من دفترة — يشمل المعتمدة والصادرة (قيد الاعتماد)</p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {/* v3.3.592 — bulk link via Excel */}
          <button onClick={doExport} disabled={exporting} title="تنزيل قالب Excel لربط السندات بالوحدات (يشمل الفلاتر الحالية)" style={{
            padding: "8px 14px", background: "#fff", color: "#0e7490",
            border: "1px solid #0e7490", borderRadius: 8, fontFamily: "inherit", fontSize: 13, fontWeight: 700,
            cursor: exporting ? "wait" : "pointer", opacity: exporting ? 0.6 : 1,
          }}>
            {exporting ? "...جاري التصدير" : "📥 تصدير قالب الربط"}
          </button>
          {canSync && (
            <>
              <button onClick={() => fileInputRef.current?.click()} disabled={importing} title="رفع ملف Excel معبّأ لربط السندات بالوحدات" style={{
                padding: "8px 14px", background: "#fff", color: "#7c3aed",
                border: "1px solid #7c3aed", borderRadius: 8, fontFamily: "inherit", fontSize: 13, fontWeight: 700,
                cursor: importing ? "wait" : "pointer", opacity: importing ? 0.6 : 1,
              }}>
                {importing ? "...جاري الاستيراد" : "📤 استيراد الربط"}
              </button>
              <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={onImportFile} style={{ display: "none" }} />
            </>
          )}
          {canSync && (
            <button onClick={doSync} disabled={syncing} style={{
              padding: "8px 16px", background: "#0e7490", color: "#fff",
              border: "none", borderRadius: 8, fontFamily: "inherit", fontSize: 13, fontWeight: 700,
              cursor: syncing ? "wait" : "pointer", opacity: syncing ? 0.6 : 1,
            }}>
              {syncing ? "جاري المزامنة..." : "↻ مزامنة الآن"}
            </button>
          )}
        </div>
      </div>

      {/* Summary cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 16 }}>
        <div style={{ background: "#f0fdf4", borderRadius: 10, padding: 12, border: "1px solid #86efac" }}>
          <div style={{ fontSize: 11, color: "#15803d", fontWeight: 700 }}>إجمالي القبض</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#166534", marginTop: 4, fontVariantNumeric: "tabular-nums" }}>{fmt(sums.total_receipts)} <span style={{ fontSize: 10, color: "#16a34a" }}>ر.س</span></div>
          <div style={{ fontSize: 10, color: "#15803d", marginTop: 2 }}>{sums.count_receipts || 0} سند</div>
        </div>
        <div style={{ background: "#fef2f2", borderRadius: 10, padding: 12, border: "1px solid #fca5a5" }}>
          <div style={{ fontSize: 11, color: "#b91c1c", fontWeight: 700 }}>إجمالي الصرف</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#991b1b", marginTop: 4, fontVariantNumeric: "tabular-nums" }}>{fmt(sums.total_payments)} <span style={{ fontSize: 10, color: "#dc2626" }}>ر.س</span></div>
          <div style={{ fontSize: 10, color: "#b91c1c", marginTop: 2 }}>{sums.count_payments || 0} سند</div>
        </div>
        <div style={{ background: "#eef2ff", borderRadius: 10, padding: 12, border: "1px solid #c7d2fe" }}>
          <div style={{ fontSize: 11, color: "#3730a3", fontWeight: 700 }}>الصافي</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#3730a3", marginTop: 4, fontVariantNumeric: "tabular-nums" }}>{fmt((Number(sums.total_receipts)||0) - (Number(sums.total_payments)||0))} <span style={{ fontSize: 10 }}>ر.س</span></div>
          <div style={{ fontSize: 10, color: "#4338ca", marginTop: 2 }}>قبض − صرف</div>
        </div>
        <div style={{ background: "#f8fafc", borderRadius: 10, padding: 12, border: "1px solid #cbd5e1" }}>
          <div style={{ fontSize: 11, color: "#475569", fontWeight: 700 }}>إجمالي السندات</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#0f172a", marginTop: 4, fontVariantNumeric: "tabular-nums" }}>{fmt(total)}</div>
          <div style={{ fontSize: 10, color: "#475569", marginTop: 2 }}>بعد تطبيق الفلاتر</div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12, alignItems: "center", padding: 10, background: "#f8fafc", borderRadius: 8 }}>
        {[
          { k: "all",     label: "الكل" },
          { k: "receipt", label: "↘ قبض فقط" },
          { k: "payment", label: "↗ صرف فقط" },
        ].map(t => (
          <button key={t.k} onClick={() => { setType(t.k); setPage(0); }} style={{
            padding: "5px 12px", borderRadius: 16,
            border: type === t.k ? "2px solid #0e7490" : "1px solid #cbd5e1",
            background: type === t.k ? "#cffafe" : "#fff",
            color: type === t.k ? "#0e7490" : "#475569",
            fontSize: 12, fontWeight: type === t.k ? 700 : 500, cursor: "pointer", fontFamily: "inherit",
          }}>{t.label}</button>
        ))}
        <div style={{ width: 1, height: 24, background: "#cbd5e1", margin: "0 4px" }} />
        <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(0); }} placeholder="من" title="من تاريخ"
          style={{ padding: "5px 8px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 12, fontFamily: "inherit" }} />
        <input type="date" value={dateTo}   onChange={e => { setDateTo(e.target.value); setPage(0); }} placeholder="إلى" title="إلى تاريخ"
          style={{ padding: "5px 8px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 12, fontFamily: "inherit" }} />
        <select value={status} onChange={e => { setStatus(e.target.value); setPage(0); }}
          style={{ padding: "5px 8px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 12, fontFamily: "inherit" }}>
          <option value="">كل الحالات</option>
          <option value="issued">صادر</option>
          <option value="approved">معتمد</option>
          <option value="draft">مسودة</option>
          <option value="cancelled">ملغي</option>
        </select>
        <input type="text" value={q} onChange={e => { setQ(e.target.value); setPage(0); }} placeholder="بحث رقم/بيان/مستفيد..."
          style={{ flex: 1, minWidth: 180, padding: "5px 10px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 12, fontFamily: "inherit" }} />
      </div>

      {/* Table */}
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#f8fafc", color: "#475569", fontSize: 11, fontWeight: 700, textTransform: "uppercase" }}>
                {["النوع","رقم السند","التاريخ","الخزينة","الحساب","البيان","الوحدة","المبلغ","الحالة","المرفقات","المُنشئ"].map((h,i) => (
                  <th key={i} style={{ padding: "10px 12px", textAlign: i === 7 ? "left" : "right", borderBottom: "2px solid #e2e8f0", whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={11} style={{ padding: 30, textAlign: "center", color: "#94a3b8" }}>جاري التحميل…</td></tr>}
              {!loading && items.length === 0 && (
                <tr><td colSpan={11} style={{ padding: 30, textAlign: "center", color: "#94a3b8" }}>
                  لا توجد سندات. اضغط <strong>↻ مزامنة الآن</strong> لجلبها من دفترة.
                </td></tr>
              )}
              {!loading && items.map(it => (
                <tr key={it.id} style={{ borderTop: "1px solid #f1f5f9", cursor: "pointer" }}
                  onClick={() => setSelected(it)}
                  onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
                  onMouseLeave={e => e.currentTarget.style.background = "white"}>
                  <td style={{ padding: "8px 12px" }}><TypeBadge t={it.voucher_type} /></td>
                  <td style={{ padding: "8px 12px", fontFamily: "monospace", color: "#0e7490", fontWeight: 600 }}>{it.voucher_number || "—"}</td>
                  <td style={{ padding: "8px 12px", color: "#64748b", fontFamily: "monospace", fontSize: 12 }}>{fmtDate(it.date)}</td>
                  <td style={{ padding: "8px 12px", color: "#475569", fontSize: 12 }}>{it.treasury_name || "—"}</td>
                  <td style={{ padding: "8px 12px", color: "#475569", fontSize: 12 }}>
                    {it.journal_account_code && <span style={{ color: "#94a3b8", marginInlineEnd: 4, fontFamily: "monospace" }}>{it.journal_account_code}</span>}
                    {it.journal_account_name || "—"}
                  </td>
                  <td style={{ padding: "8px 12px", maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={it.note}>{it.note || "—"}</td>
                  {/* v3.3.591 — linked unit cell with cascading picker */}
                  <td style={{ padding: "8px 12px", fontSize: 12 }} onClick={e => { e.stopPropagation(); setPickerVoucher(it); }}>
                    {it.linked_unit_id ? (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", background: "#ecfeff", color: "#0e7490", borderRadius: 4, cursor: "pointer", fontWeight: 600 }}
                        title={`${it.linked_project_name || ""} • ${it.linked_building_name || ""} • الدور ${it.linked_unit_floor || "?"}`}>
                        🏢 {it.linked_unit_label || it.linked_unit_number || "?"}
                      </span>
                    ) : (
                      <button style={{ padding: "2px 10px", border: "1px dashed #cbd5e1", background: "transparent", color: "#94a3b8", borderRadius: 4, fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>
                        + ربط بوحدة
                      </button>
                    )}
                  </td>
                  <td style={{ padding: "8px 12px", textAlign: "left", fontWeight: 700, fontVariantNumeric: "tabular-nums",
                    color: it.voucher_type === "receipt" ? "#16a34a" : "#dc2626" }}>
                    {it.voucher_type === "receipt" ? "+" : "−"} {fmt(it.amount)}
                  </td>
                  <td style={{ padding: "8px 12px" }}><StatusBadge s={it.status} /></td>
                  <td style={{ padding: "8px 12px", textAlign: "center" }}>
                    {Array.isArray(it.attachments) && it.attachments.length > 0
                      ? <span title={`${it.attachments.length} مرفق`}>📎</span>
                      : "—"}
                  </td>
                  <td style={{ padding: "8px 12px", color: "#475569", fontSize: 12, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={it.staff_name || ""}>
                    {it.staff_name
                      ? <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                          <span style={{ width: 18, height: 18, borderRadius: "50%", background: "#cffafe", color: "#0e7490", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700 }}>
                            {String(it.staff_name).trim().charAt(0)}
                          </span>
                          {it.staff_name}
                        </span>
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ padding: "10px 14px", borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, color: "#64748b" }}>
          <span>عرض {items.length} من أصل {total}</span>
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0}
              style={{ padding: "4px 12px", border: "1px solid #cbd5e1", background: "#fff", borderRadius: 6, fontSize: 12, fontFamily: "inherit", cursor: page === 0 ? "not-allowed" : "pointer", opacity: page === 0 ? 0.4 : 1 }}>السابق</button>
            <span style={{ padding: "4px 8px", color: "#475569" }}>صفحة {page + 1}</span>
            <button onClick={() => setPage(page + 1)} disabled={(page + 1) * limit >= total}
              style={{ padding: "4px 12px", border: "1px solid #cbd5e1", background: "#fff", borderRadius: 6, fontSize: 12, fontFamily: "inherit", cursor: ((page + 1) * limit >= total) ? "not-allowed" : "pointer", opacity: ((page + 1) * limit >= total) ? 0.4 : 1 }}>التالي</button>
          </div>
        </div>
      </div>

      {/* Detail modal */}
      {selected && <VoucherDetailModal voucher={selected} onClose={() => setSelected(null)} />}
      {pickerVoucher && (
        <UnitPickerModal
          voucher={pickerVoucher}
          onClose={() => setPickerVoucher(null)}
          onSaved={() => { setPickerVoucher(null); load(); }}
        />
      )}
      {linkReport && <LinkImportReportModal report={linkReport} onClose={() => setLinkReport(null)} />}
    </div>
  );
}

// v3.3.592 — summary of a bulk voucher↔unit link import
function LinkImportReportModal({ report, onClose }) {
  const notFound = report.not_found || [];
  const ambiguous = report.ambiguous || [];
  const errors = report.errors || [];
  const Stat = ({ label, value, color, bg }) => (
    <div style={{ background: bg, borderRadius: 10, padding: 12, textAlign: "center" }}>
      <div style={{ fontSize: 22, fontWeight: 800, color }}>{value}</div>
      <div style={{ fontSize: 11, color, fontWeight: 700, marginTop: 2 }}>{label}</div>
    </div>
  );
  const ProblemTable = ({ title, rows, extra }) => rows.length === 0 ? null : (
    <div style={{ marginTop: 14 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", marginBottom: 6 }}>{title} ({rows.length})</div>
      <div style={{ border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden", maxHeight: 200, overflowY: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ background: "#f8fafc", color: "#475569" }}>
              <th style={{ padding: "6px 10px", textAlign: "right" }}>الصف</th>
              <th style={{ padding: "6px 10px", textAlign: "right" }}>السند</th>
              <th style={{ padding: "6px 10px", textAlign: "right" }}>المشروع</th>
              <th style={{ padding: "6px 10px", textAlign: "right" }}>الوحدة</th>
              {extra && <th style={{ padding: "6px 10px", textAlign: "right" }}>{extra}</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} style={{ borderTop: "1px solid #f1f5f9" }}>
                <td style={{ padding: "6px 10px", color: "#94a3b8" }}>{r.row}</td>
                <td style={{ padding: "6px 10px" }}>{r.label || "—"}</td>
                <td style={{ padding: "6px 10px" }}>{r.project || "—"}</td>
                <td style={{ padding: "6px 10px", fontFamily: "monospace" }}>{r.unit || "—"}</td>
                {extra && <td style={{ padding: "6px 10px" }}>{r.matches != null ? `${r.matches} تطابق` : (r.reason || "")}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.5)", zIndex: 60 }} />
      <div style={{ position: "fixed", top: "6vh", insetInlineStart: "50%", transform: "translateX(-50%)", width: "min(680px, 92vw)", maxHeight: "88vh", overflowY: "auto", background: "#fff", borderRadius: 12, zIndex: 70, boxShadow: "0 10px 40px rgba(0,0,0,.2)" }}>
        <div style={{ padding: "16px 24px", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: "#0f172a" }}>📋 نتيجة استيراد الربط</div>
          <button onClick={onClose} style={{ width: 32, height: 32, border: "none", background: "transparent", fontSize: 20, cursor: "pointer", color: "#64748b" }}>✕</button>
        </div>
        <div style={{ padding: 24 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
            <Stat label="تم ربطه" value={report.linked || 0} color="#166534" bg="#f0fdf4" />
            <Stat label="أُلغي ربطه" value={report.unlinked || 0} color="#92400e" bg="#fffbeb" />
            <Stat label="بدون تغيير" value={report.unchanged || 0} color="#475569" bg="#f8fafc" />
            <Stat label="مشاكل" value={notFound.length + ambiguous.length + errors.length} color="#991b1b" bg="#fef2f2" />
          </div>
          <ProblemTable title="🔴 وحدات غير معروفة" rows={notFound} />
          <ProblemTable title="🟠 تطابق متعدّد (حدّد المشروع)" rows={ambiguous} extra="عدد المطابقات" />
          {errors.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", marginBottom: 6 }}>⚠️ أخطاء ({errors.length})</div>
              <div style={{ border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden", maxHeight: 160, overflowY: "auto" }}>
                {errors.map((er, i) => (
                  <div key={i} style={{ padding: "6px 10px", fontSize: 12, borderTop: i ? "1px solid #f1f5f9" : "none", color: "#991b1b" }}>
                    صف {er.row}: {er.reason}
                  </div>
                ))}
              </div>
            </div>
          )}
          {notFound.length === 0 && ambiguous.length === 0 && errors.length === 0 && (
            <div style={{ marginTop: 16, padding: 12, background: "#f0fdf4", borderRadius: 8, color: "#166534", fontSize: 13, textAlign: "center", fontWeight: 700 }}>
              ✅ تمت معالجة كل الصفوف بدون مشاكل
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function VoucherDetailModal({ voucher, onClose }) {
  const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 });
  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.5)", zIndex: 40 }} />
      <div style={{ position: "fixed", top: "5vh", insetInlineStart: "50%", transform: "translateX(-50%)", width: "min(700px, 90vw)", maxHeight: "90vh", overflowY: "auto", background: "#fff", borderRadius: 12, zIndex: 50, boxShadow: "0 10px 40px rgba(0,0,0,.2)" }}>
        <div style={{ padding: "16px 24px", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: voucher.voucher_type === "receipt" ? "#166534" : "#991b1b" }}>
              {voucher.voucher_type === "receipt" ? "↘ سند قبض" : "↗ سند صرف"} #{voucher.voucher_number}
            </div>
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>{voucher.date} • {voucher.treasury_name}</div>
          </div>
          <button onClick={onClose} style={{ width: 32, height: 32, border: "none", background: "transparent", fontSize: 20, cursor: "pointer", color: "#64748b" }}>✕</button>
        </div>
        <div style={{ padding: 24 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
            <DetailRow label="المبلغ" value={`${fmt(voucher.amount)} ${voucher.currency_code || "ر.س"}`} highlight />
            <DetailRow label="الحالة" value={voucher.status} />
            <DetailRow label="رقم الحساب" value={voucher.journal_account_code} />
            <DetailRow label="اسم الحساب" value={voucher.journal_account_name} />
            <DetailRow label="الخزينة" value={voucher.treasury_name} />
            <DetailRow label="بواسطة" value={voucher.staff_name} />
            {voucher.vendor && <DetailRow label="المستفيد/المورد" value={voucher.vendor} />}
            {voucher.expense_category_name && <DetailRow label="التصنيف" value={voucher.expense_category_name} />}
          </div>
          {voucher.note && (
            <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: 12, marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: "#64748b", fontWeight: 700, marginBottom: 4 }}>البيان</div>
              <div style={{ fontSize: 13, color: "#0f172a", whiteSpace: "pre-wrap" }}>{voucher.note}</div>
            </div>
          )}
          {Array.isArray(voucher.attachments) && voucher.attachments.length > 0 && (
            <div>
              <div style={{ fontSize: 11, color: "#64748b", fontWeight: 700, marginBottom: 6 }}>المرفقات ({voucher.attachments.length})</div>
              {voucher.attachments.map((url, i) => (
                <a key={i} href={url} target="_blank" rel="noopener noreferrer" style={{ display: "block", padding: "6px 10px", background: "#eef2ff", borderRadius: 6, marginBottom: 4, fontSize: 12, color: "#4338ca", textDecoration: "none" }}>
                  📎 مرفق {i + 1} — افتح
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function DetailRow({ label, value, highlight }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: highlight ? 16 : 13, fontWeight: highlight ? 800 : 500, color: "#0f172a" }}>{value || "—"}</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// v3.3.591 — UnitPickerModal — 4-step cascading: project → building → floor → unit
// ═══════════════════════════════════════════════════════════════════
function UnitPickerModal({ voucher, onClose, onSaved }) {
  // Wizard state
  const [step, setStep] = useState(1);                       // 1..4
  const [projects, setProjects]         = useState([]);
  const [allUnits, setAllUnits]         = useState([]);      // all units of selected project (with building_name, floor_number, unit_type, unit_label)
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadingUnits, setLoadingUnits]       = useState(false);
  const [saving, setSaving] = useState(false);

  const [projectId, setProjectId]   = useState(voucher.linked_project_id || null);
  const [buildingId, setBuildingId] = useState(voucher.linked_building_id || null);
  const [floor, setFloor]           = useState(voucher.linked_unit_floor ?? null);
  const [unitId, setUnitId]         = useState(voucher.linked_unit_id || null);

  // Load all projects once
  useEffect(() => {
    setLoadingProjects(true);
    projectsApi.list()
      .then(r => setProjects(r.data?.projects || []))
      .catch(e => toast.error("فشل تحميل المشاريع: " + (e?.response?.data?.error || e.message)))
      .finally(() => setLoadingProjects(false));
  }, []);

  // Whenever a project is chosen, load its units (which already include building/floor info)
  useEffect(() => {
    if (!projectId) { setAllUnits([]); return; }
    setLoadingUnits(true);
    projectsApi.get(projectId)
      .then(r => setAllUnits(r.data?.units || []))
      .catch(e => toast.error("فشل تحميل الوحدات: " + (e?.response?.data?.error || e.message)))
      .finally(() => setLoadingUnits(false));
  }, [projectId]);

  // Derived: buildings, floors, units (cascading filters)
  const buildings = React.useMemo(() => {
    const m = new Map();
    for (const u of allUnits) {
      if (u.building_id && !m.has(u.building_id)) {
        m.set(u.building_id, { id: u.building_id, name: u.building_name || `مبنى ${u.building_id}` });
      }
    }
    return [...m.values()].sort((a, b) => String(a.name).localeCompare(String(b.name), "ar"));
  }, [allUnits]);

  const floors = React.useMemo(() => {
    if (!buildingId) return [];
    const s = new Set();
    for (const u of allUnits) {
      if (u.building_id === buildingId && u.floor_number != null) s.add(Number(u.floor_number));
    }
    return [...s].sort((a, b) => a - b);
  }, [allUnits, buildingId]);

  const unitsOnFloor = React.useMemo(() => {
    if (!buildingId || floor == null) return [];
    return allUnits
      .filter(u => u.building_id === buildingId && Number(u.floor_number) === Number(floor))
      .sort((a, b) => (a.unit_number || 0) - (b.unit_number || 0));
  }, [allUnits, buildingId, floor]);

  const selectedProject  = projects.find(p => p.id === projectId);
  const selectedBuilding = buildings.find(b => b.id === buildingId);
  const selectedUnit     = allUnits.find(u => u.id === unitId);

  const save = async () => {
    if (!unitId) { toast.error("اختر الوحدة أولاً"); return; }
    setSaving(true);
    try {
      await accounting.linkVoucherUnit(voucher.id, unitId);
      toast.success("✅ تم ربط السند بالوحدة");
      onSaved();
    } catch (e) {
      toast.error("فشل الربط: " + (e?.response?.data?.error || e.message));
    } finally { setSaving(false); }
  };

  const unlink = async () => {
    setSaving(true);
    try {
      await accounting.linkVoucherUnit(voucher.id, null);
      toast.success("✅ تم إلغاء الربط");
      onSaved();
    } catch (e) {
      toast.error("فشل إلغاء الربط: " + (e?.response?.data?.error || e.message));
    } finally { setSaving(false); }
  };

  const stepLabels = ["المشروع", "المبنى", "الدور", "الوحدة"];
  const goStep = (n) => {
    // Allow free navigation but reset downstream when going back
    if (n < step) {
      if (n <= 1) { setBuildingId(null); setFloor(null); setUnitId(null); }
      else if (n <= 2) { setFloor(null); setUnitId(null); }
      else if (n <= 3) { setUnitId(null); }
    }
    setStep(n);
  };

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.5)", zIndex: 40 }} />
      <div style={{ position: "fixed", top: "5vh", insetInlineStart: "50%", transform: "translateX(-50%)", width: "min(640px, 92vw)", maxHeight: "90vh", overflowY: "auto", background: "#fff", borderRadius: 12, zIndex: 50, boxShadow: "0 10px 40px rgba(0,0,0,.2)" }}>
        {/* Header */}
        <div style={{ padding: "16px 24px", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#0f172a" }}>🏢 ربط السند بوحدة</div>
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>سند #{voucher.voucher_number}</div>
          </div>
          <button onClick={onClose} style={{ width: 32, height: 32, border: "none", background: "transparent", fontSize: 20, cursor: "pointer", color: "#64748b" }}>✕</button>
        </div>

        {/* Step indicator */}
        <div style={{ display: "flex", gap: 4, padding: "12px 24px", borderBottom: "1px solid #f1f5f9" }}>
          {stepLabels.map((lbl, i) => {
            const n = i + 1;
            const done = (n === 1 && projectId) || (n === 2 && buildingId) || (n === 3 && floor != null) || (n === 4 && unitId);
            const active = step === n;
            return (
              <button key={n} onClick={() => goStep(n)} disabled={n > 1 && !projectId} style={{
                flex: 1, padding: "8px 6px", border: "none",
                background: active ? "#0e7490" : (done ? "#cffafe" : "#f1f5f9"),
                color: active ? "#fff" : (done ? "#0e7490" : "#64748b"),
                borderRadius: 6, cursor: "pointer", fontFamily: "inherit",
                fontWeight: 700, fontSize: 12,
              }}>
                {n}. {lbl}{done && <span style={{ marginInlineStart: 4 }}>✓</span>}
              </button>
            );
          })}
        </div>

        {/* Body */}
        <div style={{ padding: 24, minHeight: 240 }}>
          {/* Step 1: project */}
          {step === 1 && (
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", marginBottom: 12 }}>اختر المشروع:</div>
              {loadingProjects ? <div style={{ color: "#94a3b8", textAlign: "center", padding: 30 }}>جاري التحميل…</div> : (
                <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 6, maxHeight: 360, overflowY: "auto" }}>
                  {projects.length === 0 && <div style={{ color: "#94a3b8", textAlign: "center", padding: 30 }}>لا توجد مشاريع</div>}
                  {projects.map(p => (
                    <button key={p.id} onClick={() => { setProjectId(p.id); setBuildingId(null); setFloor(null); setUnitId(null); setStep(2); }}
                      style={{
                        padding: "10px 14px", textAlign: "right", fontFamily: "inherit", fontSize: 13,
                        border: "1px solid " + (projectId === p.id ? "#0e7490" : "#e2e8f0"),
                        background: projectId === p.id ? "#ecfeff" : "#fff",
                        color: "#0f172a", borderRadius: 8, cursor: "pointer", fontWeight: projectId === p.id ? 700 : 500,
                      }}>
                      <span style={{ marginInlineEnd: 8 }}>📊</span> {p.name}
                      {p.location && <span style={{ marginInlineStart: 8, fontSize: 11, color: "#94a3b8" }}>— {p.location}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 2: building */}
          {step === 2 && (
            <div>
              <div style={{ fontSize: 12, color: "#64748b", marginBottom: 6 }}>المشروع: <strong>{selectedProject?.name}</strong></div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", marginBottom: 12 }}>اختر المبنى:</div>
              {loadingUnits ? <div style={{ color: "#94a3b8", textAlign: "center", padding: 30 }}>جاري التحميل…</div> : (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 8 }}>
                  {buildings.length === 0 && <div style={{ color: "#94a3b8", padding: 30, textAlign: "center", gridColumn: "1 / -1" }}>لا توجد مبانٍ</div>}
                  {buildings.map(b => (
                    <button key={b.id} onClick={() => { setBuildingId(b.id); setFloor(null); setUnitId(null); setStep(3); }}
                      style={{
                        padding: "16px 12px", textAlign: "center", fontFamily: "inherit", fontSize: 14, fontWeight: 700,
                        border: "1px solid " + (buildingId === b.id ? "#0e7490" : "#e2e8f0"),
                        background: buildingId === b.id ? "#ecfeff" : "#fff",
                        color: buildingId === b.id ? "#0e7490" : "#0f172a",
                        borderRadius: 8, cursor: "pointer",
                      }}>
                      🏢 {b.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 3: floor */}
          {step === 3 && (
            <div>
              <div style={{ fontSize: 12, color: "#64748b", marginBottom: 6 }}>
                {selectedProject?.name} <span style={{ color: "#cbd5e1", margin: "0 6px" }}>/</span> {selectedBuilding?.name}
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", marginBottom: 12 }}>اختر الدور:</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(80px, 1fr))", gap: 8 }}>
                {floors.length === 0 && <div style={{ color: "#94a3b8", padding: 30, textAlign: "center", gridColumn: "1 / -1" }}>لا توجد أدوار</div>}
                {floors.map(f => (
                  <button key={f} onClick={() => { setFloor(f); setUnitId(null); setStep(4); }}
                    style={{
                      padding: "14px 8px", textAlign: "center", fontFamily: "inherit", fontSize: 14, fontWeight: 700,
                      border: "1px solid " + (floor === f ? "#0e7490" : "#e2e8f0"),
                      background: floor === f ? "#ecfeff" : "#fff",
                      color: floor === f ? "#0e7490" : "#0f172a",
                      borderRadius: 8, cursor: "pointer",
                    }}>
                    الدور {f}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 4: unit */}
          {step === 4 && (
            <div>
              <div style={{ fontSize: 12, color: "#64748b", marginBottom: 6 }}>
                {selectedProject?.name}
                <span style={{ color: "#cbd5e1", margin: "0 6px" }}>/</span> {selectedBuilding?.name}
                <span style={{ color: "#cbd5e1", margin: "0 6px" }}>/</span> الدور {floor}
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", marginBottom: 12 }}>اختر الوحدة:</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 8 }}>
                {unitsOnFloor.length === 0 && <div style={{ color: "#94a3b8", padding: 30, textAlign: "center", gridColumn: "1 / -1" }}>لا توجد وحدات</div>}
                {unitsOnFloor.map(u => (
                  <button key={u.id} onClick={() => setUnitId(u.id)}
                    style={{
                      padding: "10px 12px", textAlign: "right", fontFamily: "inherit", fontSize: 13,
                      border: "1px solid " + (unitId === u.id ? "#0e7490" : "#e2e8f0"),
                      background: unitId === u.id ? "#ecfeff" : "#fff",
                      color: "#0f172a", borderRadius: 8, cursor: "pointer",
                      display: "flex", flexDirection: "column", gap: 2,
                    }}>
                    <span style={{ fontWeight: 700, fontSize: 13 }}>
                      {u.unit_label || `#${u.unit_number}`}
                    </span>
                    <span style={{ fontSize: 11, color: "#64748b" }}>
                      {u.unit_type || u.section_type || "—"}
                      {u.area_sqm && <span style={{ marginInlineStart: 6 }}>• {u.area_sqm} م²</span>}
                    </span>
                  </button>
                ))}
              </div>
              {selectedUnit && (
                <div style={{ marginTop: 16, padding: 12, background: "#ecfeff", border: "1px solid #67e8f9", borderRadius: 8, fontSize: 13, color: "#0e7490" }}>
                  ✓ سيتم ربط السند بـ <strong>{selectedUnit.unit_label || `الوحدة #${selectedUnit.unit_number}`}</strong>
                  {" "}في {selectedBuilding?.name} — الدور {floor} — {selectedProject?.name}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: "12px 24px", borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
          <div>
            {voucher.linked_unit_id && (
              <button onClick={unlink} disabled={saving} style={{
                padding: "8px 14px", background: "transparent", color: "#dc2626",
                border: "1px solid #fca5a5", borderRadius: 6, fontSize: 12, fontFamily: "inherit",
                cursor: saving ? "wait" : "pointer", fontWeight: 600,
              }}>
                🗑 إلغاء الربط الحالي
              </button>
            )}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={onClose} style={{ padding: "8px 16px", background: "#f1f5f9", color: "#475569", border: "none", borderRadius: 6, fontFamily: "inherit", fontWeight: 600, cursor: "pointer" }}>إلغاء</button>
            <button onClick={save} disabled={!unitId || saving} style={{
              padding: "8px 16px",
              background: !unitId || saving ? "#94a3b8" : "#0e7490",
              color: "#fff", border: "none", borderRadius: 6, fontFamily: "inherit", fontWeight: 700,
              cursor: !unitId || saving ? "not-allowed" : "pointer",
            }}>
              {saving ? "جاري الحفظ..." : "💾 حفظ الربط"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════
// TRANSACTIONS TAB
// ═══════════════════════════════════════════════════════════════════
function TransactionsTab() {
  const [data, setData] = useState({ items: [], total: 0, total_debit: 0, total_credit: 0, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    q: "",
    cost_center_id: "",
    journal_account_id: "",
    date_from: "",
    date_to: "",
    page: 1,
    per_page: 50,
  });
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      for (const [k, v] of Object.entries(filters)) if (v !== "" && v !== 0) params[k] = v;
      const r = await accounting.listTransactions(params);
      setData(r.data || { items: [] });
    } catch (e) {
      toast.error(e?.response?.data?.error || "فشل تحميل المعاملات");
    } finally {
      setLoading(false);
    }
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const updateFilter = (key, value) => setFilters(prev => ({ ...prev, [key]: value, page: key === "page" ? value : 1 }));
  const clearFilters = () => setFilters({ q: "", cost_center_id: "", journal_account_id: "", date_from: "", date_to: "", page: 1, per_page: 50 });

  const hasFilters = filters.q || filters.cost_center_id || filters.journal_account_id || filters.date_from || filters.date_to;

  return (
    <div>
      {/* KPI strip */}
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10, marginBottom: 14,
        padding: "14px 18px",
        background: "linear-gradient(135deg, #0e7490 0%, #155e75 100%)",
        borderRadius: 12, color: "#fff",
      }}>
        <div>
          <div style={{ fontSize: 11, opacity: 0.9, fontWeight: 600 }}>📊 عدد المعاملات</div>
          <div style={{ fontSize: 24, fontWeight: 800, direction: "ltr", textAlign: "right" }}>{fmt(data.total)}</div>
        </div>
        <div>
          <div style={{ fontSize: 11, opacity: 0.9, fontWeight: 600 }}>⬆️ إجمالي المدين</div>
          <div style={{ fontSize: 20, fontWeight: 800, direction: "ltr", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmt(data.total_debit)}</div>
        </div>
        <div>
          <div style={{ fontSize: 11, opacity: 0.9, fontWeight: 600 }}>⬇️ إجمالي الدائن</div>
          <div style={{ fontSize: 20, fontWeight: 800, direction: "ltr", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmt(data.total_credit)}</div>
        </div>
        <div>
          <div style={{ fontSize: 11, opacity: 0.9, fontWeight: 600 }}>⚖️ الصافي</div>
          <div style={{ fontSize: 20, fontWeight: 800, direction: "ltr", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmt(data.total_debit - data.total_credit)}</div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12, flexWrap: "wrap", padding: 12, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: "#475569" }}>🔍 الفلاتر:</span>
        <div style={{ position: "relative" }}>
          <FiSearch style={{ position: "absolute", right: 8, top: 8, color: "#94a3b8" }} size={14} />
          <input
            type="text"
            value={filters.q}
            onChange={e => updateFilter("q", e.target.value)}
            placeholder="بحث في الوصف / الحساب / رقم القيد..."
            style={{ padding: "6px 28px 6px 10px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 12, fontFamily: "inherit", minWidth: 280 }}
          />
        </div>
        <input
          type="date" value={filters.date_from} onChange={e => updateFilter("date_from", e.target.value)}
          style={{ padding: "6px 10px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 12, fontFamily: "inherit" }}
          title="من تاريخ"
        />
        <input
          type="date" value={filters.date_to} onChange={e => updateFilter("date_to", e.target.value)}
          style={{ padding: "6px 10px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 12, fontFamily: "inherit" }}
          title="إلى تاريخ"
        />
        {hasFilters && (
          <button onClick={clearFilters} style={{ padding: "5px 10px", background: "#f1f5f9", color: "#475569", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
            🔄 إعادة تعيين
          </button>
        )}
        <div style={{ marginRight: "auto", fontSize: 11, color: "#64748b" }}>
          {loading ? "...جاري التحميل" : `عرض ${data.items.length} من ${fmt(data.total)} معاملة`}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: "#64748b" }}>...جاري التحميل</div>
      ) : data.items.length === 0 ? (
        <div style={{ padding: 40, textAlign: "center", color: "#94a3b8", fontSize: 13, background: "#f8fafc", borderRadius: 10, border: "1px dashed #cbd5e1" }}>
          لا توجد معاملات — تحقق من الفلاتر أو شغّل المزامنة من تبويب "الإعدادات والمزامنة"
        </div>
      ) : (
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, overflow: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead>
              <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0", position: "sticky", top: 0 }}>
                <th style={th}>#</th>
                <th style={th}>التاريخ</th>
                <th style={th}>رقم القيد</th>
                <th style={th}>مركز التكلفة</th>
                <th style={th}>الحساب</th>
                <th style={th}>الوصف</th>
                <th style={{ ...th, textAlign: "left" }}>مدين</th>
                <th style={{ ...th, textAlign: "left" }}>دائن</th>
                <th style={th}>عملة</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((it, idx) => (
                <tr key={it.id}
                    style={{ borderBottom: "1px solid #f1f5f9", background: idx % 2 === 0 ? "#fff" : "#fafafa", cursor: "pointer" }}
                    onClick={() => setDetail(it)}
                    title="اضغط لعرض التفاصيل الكاملة"
                >
                  <td style={{ ...td, color: "#64748b", textAlign: "center" }}>{(filters.page - 1) * filters.per_page + idx + 1}</td>
                  <td style={{ ...td, direction: "ltr", textAlign: "center", fontWeight: 700, color: "#0f172a" }}>{fmtDate(it.journal_date)}</td>
                  <td style={{ ...td, textAlign: "center", color: "#475569", fontWeight: 600 }}>{it.journal_number || "—"}</td>
                  <td style={{ ...td }}>
                    {it.cost_center_name
                      ? <span style={{ background: "#cffafe", color: "#0e7490", padding: "1px 7px", borderRadius: 4, fontSize: 10, fontWeight: 700 }}>{it.cost_center_name}</span>
                      : <span style={{ color: "#d1d5db" }}>—</span>}
                  </td>
                  <td style={{ ...td }}>
                    <div style={{ fontWeight: 600, color: "#0f172a" }}>{it.journal_account_name || "—"}</div>
                    {it.journal_account_code && <div style={{ fontSize: 9, color: "#94a3b8", direction: "ltr", textAlign: "right" }}>{it.journal_account_code}</div>}
                  </td>
                  <td style={{ ...td, maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={it.journal_description || it.related_description || ""}>
                    {it.journal_description || it.related_description || <span style={{ color: "#d1d5db" }}>—</span>}
                  </td>
                  <td style={{ ...td, textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", color: Number(it.debit) > 0 ? "#15803d" : "#cbd5e1", fontWeight: 700 }}>
                    {Number(it.debit) > 0 ? fmt(it.debit) : "—"}
                  </td>
                  <td style={{ ...td, textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", color: Number(it.credit) > 0 ? "#b45309" : "#cbd5e1", fontWeight: 700 }}>
                    {Number(it.credit) > 0 ? fmt(it.credit) : "—"}
                  </td>
                  <td style={{ ...td, textAlign: "center", color: "#64748b", fontSize: 10 }}>{it.currency_code || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {data.total_pages > 1 && (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 6, marginTop: 12 }}>
          <button
            onClick={() => updateFilter("page", Math.max(1, filters.page - 1))}
            disabled={filters.page <= 1}
            style={{ padding: "5px 12px", border: "1px solid #cbd5e1", borderRadius: 6, background: filters.page <= 1 ? "#f1f5f9" : "#fff", cursor: filters.page <= 1 ? "not-allowed" : "pointer", fontSize: 11, fontFamily: "inherit" }}
          >← السابق</button>
          <span style={{ fontSize: 11, color: "#64748b", padding: "0 10px" }}>
            صفحة {filters.page} من {data.total_pages}
          </span>
          <button
            onClick={() => updateFilter("page", Math.min(data.total_pages, filters.page + 1))}
            disabled={filters.page >= data.total_pages}
            style={{ padding: "5px 12px", border: "1px solid #cbd5e1", borderRadius: 6, background: filters.page >= data.total_pages ? "#f1f5f9" : "#fff", cursor: filters.page >= data.total_pages ? "not-allowed" : "pointer", fontSize: 11, fontFamily: "inherit" }}
          >التالي →</button>
        </div>
      )}

      {/* Detail Modal */}
      {detail && <TransactionDetailModal transaction={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function TransactionDetailModal({ transaction, onClose }) {
  const [resyncing, setResyncing] = useState(false);

  const handleResync = async () => {
    if (!confirm(`إعادة مزامنة هذا القيد (#${transaction.id}) مع Daftra؟\n\nسيتم جلب أحدث بياناته من Daftra واستبدال النسخة المحلية. لو حُذف من Daftra، سيُحذف محلياً.`)) return;
    setResyncing(true);
    try {
      const r = await accounting.resyncTransaction(transaction.id);
      toast.success(r.data?.message || "تم");
      onClose();
      // Note: the parent table will refresh on next load
    } catch (e) {
      toast.error(e?.response?.data?.error || "فشلت إعادة المزامنة");
    } finally {
      setResyncing(false);
    }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, padding: 20, maxWidth: 700, width: "100%", maxHeight: "85vh", overflow: "auto", direction: "rtl" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, paddingBottom: 10, borderBottom: "1px solid #e2e8f0" }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: "#0e7490" }}>📋 تفاصيل المعاملة #{transaction.id}</div>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            {/* v3.3.511 — Quick resync from Daftra for this single transaction */}
            <button
              onClick={handleResync}
              disabled={resyncing}
              title="جلب أحدث نسخة من هذا القيد من Daftra"
              style={{
                padding: "5px 10px",
                background: resyncing ? "#94a3b8" : "#16a34a",
                color: "#fff", border: "none", borderRadius: 6,
                fontSize: 11, fontWeight: 700,
                cursor: resyncing ? "not-allowed" : "pointer",
                fontFamily: "inherit",
              }}
            >{resyncing ? "...جاري" : "♻️ إعادة مزامنة من Daftra"}</button>
            <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#64748b" }}><FiX size={22} /></button>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, fontSize: 12 }}>
          <Field label="📅 تاريخ القيد" value={fmtDate(transaction.journal_date)} />
          <Field label="🔢 رقم القيد" value={transaction.journal_number} />
          <Field label="🎯 مركز التكلفة" value={transaction.cost_center_name} />
          <Field label="🏷 كود المركز" value={transaction.cost_center_code} />
          <Field label="📚 الحساب" value={transaction.journal_account_name} />
          <Field label="🏷 كود الحساب" value={transaction.journal_account_code} />
          <Field label="📂 نوع الحساب" value={transaction.journal_account_type} />
          <Field label="🏛️ نوع الكيان" value={transaction.entity_type} />
          <Field label="⬆️ مدين" value={fmt(transaction.debit)} highlight={Number(transaction.debit) > 0 ? "#15803d" : null} />
          <Field label="⬇️ دائن" value={fmt(transaction.credit)} highlight={Number(transaction.credit) > 0 ? "#b45309" : null} />
          <Field label="💱 العملة" value={transaction.currency_code} />
          <Field label="📊 السعر" value={transaction.rate} />
          <Field label="📅 أُنشئ" value={fmtDateTime(transaction.created_at_source)} />
          <Field label="📅 عُدّل" value={fmtDateTime(transaction.modified_at_source)} />
        </div>
        {transaction.journal_description && (
          <div style={{ marginTop: 12, padding: 10, background: "#f8fafc", borderRadius: 8, fontSize: 12 }}>
            <div style={{ fontWeight: 700, color: "#475569", marginBottom: 4 }}>📝 وصف القيد</div>
            <div style={{ color: "#0f172a", lineHeight: 1.6 }}>{transaction.journal_description}</div>
          </div>
        )}
        {transaction.related_description && transaction.related_description !== transaction.journal_description && (
          <div style={{ marginTop: 8, padding: 10, background: "#f0fdfa", borderRadius: 8, fontSize: 12 }}>
            <div style={{ fontWeight: 700, color: "#0f766e", marginBottom: 4 }}>🔗 الوصف المرتبط</div>
            <div style={{ color: "#0f172a", lineHeight: 1.6 }}>{transaction.related_description}</div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, highlight }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", padding: "6px 10px", background: "#f8fafc", borderRadius: 6 }}>
      <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 600, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 12, color: highlight || "#0f172a", fontWeight: 700, direction: "ltr", textAlign: "right" }}>{value || "—"}</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// COST CENTERS TAB
// ═══════════════════════════════════════════════════════════════════
function CostCentersTab() {
  const [items, setItems] = useState([]);
  const [source, setSource] = useState("");
  const [warning, setWarning] = useState("");
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  // v3.3.505 — Filters
  const [hideInactive, setHideInactive] = useState(false);
  const [hideNoActivity, setHideNoActivity] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const r = await accounting.listCostCenters(params);
      setItems(r.data?.items || []);
      setSource(r.data?.source || "");
      setWarning(r.data?.warning || "");
    } catch (e) {
      toast.error("فشل تحميل مراكز التكلفة");
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    return items.filter(c => {
      if (hideInactive && c.is_active === false) return false;
      if (hideNoActivity && c.has_no_activity) return false;
      return true;
    });
  }, [items, hideInactive, hideNoActivity]);

  const stats = useMemo(() => {
    const total = items.length;
    const active = items.filter(c => c.is_active !== false).length;
    const withActivity = items.filter(c => !c.has_no_activity).length;
    return { total, active, inactive: total - active, withActivity, withoutActivity: total - withActivity };
  }, [items]);

  return (
    <div>
      {/* v3.3.505 — Source banner */}
      {source === "transactions_only" && warning && (
        <div style={{ marginBottom: 12, padding: "10px 14px", background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 8, fontSize: 12, color: "#92400e", display: "flex", alignItems: "center", gap: 8 }}>
          <FiAlertCircle size={16} /> <span>{warning}</span>
        </div>
      )}
      {source === "cost_centers_list" && (
        <div style={{ marginBottom: 12, padding: "10px 14px", background: "#dcfce7", border: "1px solid #86efac", borderRadius: 8, fontSize: 12, color: "#15803d", display: "flex", alignItems: "center", gap: 8 }}>
          <FiCheckCircle size={16} />
          <span>قائمة مراكز التكلفة الكاملة من Daftra — {fmt(stats.total)} مركز ({fmt(stats.active)} نشط · {fmt(stats.withActivity)} له حركة في الفترة)</span>
        </div>
      )}

      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12, flexWrap: "wrap", padding: 12, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: "#475569" }}>🔍 الفترة:</span>
        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={inputS} title="من تاريخ" />
        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={inputS} title="إلى تاريخ" />
        {(dateFrom || dateTo) && (
          <button onClick={() => { setDateFrom(""); setDateTo(""); }} style={{ padding: "5px 10px", background: "#f1f5f9", color: "#475569", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>🔄 إعادة تعيين</button>
        )}
        {source === "cost_centers_list" && (
          <>
            <span style={{ width: 1, height: 22, background: "#cbd5e1", margin: "0 2px" }} />
            <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "#475569", cursor: "pointer", fontWeight: 600 }}>
              <input type="checkbox" checked={hideInactive} onChange={e => setHideInactive(e.target.checked)} />
              <span>إخفاء غير النشطة ({stats.inactive})</span>
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "#475569", cursor: "pointer", fontWeight: 600 }}>
              <input type="checkbox" checked={hideNoActivity} onChange={e => setHideNoActivity(e.target.checked)} />
              <span>إخفاء التي بدون حركة ({stats.withoutActivity})</span>
            </label>
          </>
        )}
        <div style={{ marginRight: "auto", fontSize: 11, color: "#64748b" }}>{filtered.length} مركز{filtered.length !== items.length ? ` (من ${items.length})` : ""}</div>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: "#64748b" }}>...جاري التحميل</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: 40, textAlign: "center", color: "#94a3b8", background: "#f8fafc", borderRadius: 10, border: "1px dashed #cbd5e1" }}>لا توجد بيانات</div>
      ) : (
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                <th style={th}>الكود</th>
                <th style={th}>مركز التكلفة</th>
                <th style={th}>الحالة</th>
                <th style={th}>عدد المعاملات</th>
                <th style={{ ...th, textAlign: "left" }}>إجمالي مدين</th>
                <th style={{ ...th, textAlign: "left" }}>إجمالي دائن</th>
                <th style={{ ...th, textAlign: "left" }}>الصافي</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, idx) => {
                const noActivity = c.has_no_activity;
                const inactive = c.is_active === false;
                return (
                  <tr key={c.cost_center_id} style={{
                    borderBottom: "1px solid #f1f5f9",
                    background: idx % 2 === 0 ? "#fff" : "#fafafa",
                    opacity: inactive ? 0.55 : 1,
                  }}>
                    <td style={{ ...td, textAlign: "center", color: "#475569", fontWeight: 600, direction: "ltr" }}>{c.cost_center_code || "—"}</td>
                    <td style={{ ...td, fontWeight: 700, color: "#0e7490" }}>{c.cost_center_name || "—"}</td>
                    <td style={{ ...td, textAlign: "center" }}>
                      {inactive
                        ? <span style={{ background: "#fee2e2", color: "#dc2626", padding: "1px 7px", borderRadius: 4, fontSize: 10, fontWeight: 700 }}>غير نشط</span>
                        : noActivity
                          ? <span style={{ background: "#f1f5f9", color: "#64748b", padding: "1px 7px", borderRadius: 4, fontSize: 10, fontWeight: 700 }}>بدون حركة</span>
                          : <span style={{ background: "#dcfce7", color: "#15803d", padding: "1px 7px", borderRadius: 4, fontSize: 10, fontWeight: 700 }}>نشط</span>}
                    </td>
                    <td style={{ ...td, textAlign: "center", color: noActivity ? "#cbd5e1" : "#0f172a" }}>{fmt(c.transactions_count)}</td>
                    <td style={{ ...td, textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", color: Number(c.total_debit) > 0 ? "#15803d" : "#cbd5e1", fontWeight: 700 }}>{Number(c.total_debit) > 0 ? fmt(c.total_debit) : "—"}</td>
                    <td style={{ ...td, textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", color: Number(c.total_credit) > 0 ? "#b45309" : "#cbd5e1", fontWeight: 700 }}>{Number(c.total_credit) > 0 ? fmt(c.total_credit) : "—"}</td>
                    <td style={{ ...td, textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", fontWeight: 800, color: Number(c.net) >= 0 ? "#0f172a" : "#dc2626" }}>{fmt(c.net)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ACCOUNTS TAB
// ═══════════════════════════════════════════════════════════════════
function AccountsTab() {
  const [items, setItems] = useState([]);
  const [source, setSource] = useState("");
  const [warning, setWarning] = useState("");
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  // v3.3.503 — Filters
  const [hideInactive, setHideInactive] = useState(false);    // hide deactivated accounts in Daftra
  const [hideNoActivity, setHideNoActivity] = useState(false); // hide accounts with no transactions in period

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const r = await accounting.listAccounts(params);
      setItems(r.data?.items || []);
      setSource(r.data?.source || "");
      setWarning(r.data?.warning || "");
    } catch (e) {
      toast.error("فشل تحميل الحسابات");
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);
  useEffect(() => { load(); }, [load]);

  // v3.3.503 — Apply client-side filters
  const filtered = useMemo(() => {
    return items.filter(a => {
      if (hideInactive && a.is_active === false) return false;
      if (hideNoActivity && a.has_no_activity) return false;
      return true;
    });
  }, [items, hideInactive, hideNoActivity]);

  const stats = useMemo(() => {
    const total = items.length;
    const active = items.filter(a => a.is_active !== false).length;
    const withActivity = items.filter(a => !a.has_no_activity).length;
    return { total, active, inactive: total - active, withActivity, withoutActivity: total - withActivity };
  }, [items]);

  return (
    <div>
      {/* v3.3.503 — Source banner */}
      {source === "transactions_only" && warning && (
        <div style={{ marginBottom: 12, padding: "10px 14px", background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 8, fontSize: 12, color: "#92400e", display: "flex", alignItems: "center", gap: 8 }}>
          <FiAlertCircle size={16} /> <span>{warning}</span>
        </div>
      )}
      {source === "chart_of_accounts" && (
        <div style={{ marginBottom: 12, padding: "10px 14px", background: "#dcfce7", border: "1px solid #86efac", borderRadius: 8, fontSize: 12, color: "#15803d", display: "flex", alignItems: "center", gap: 8 }}>
          <FiCheckCircle size={16} />
          <span>دليل الحسابات الكامل من Daftra — {fmt(stats.total)} حساب ({fmt(stats.active)} نشط · {fmt(stats.withActivity)} له حركة في الفترة)</span>
        </div>
      )}

      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12, flexWrap: "wrap", padding: 12, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: "#475569" }}>🔍 الفترة:</span>
        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={inputS} />
        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={inputS} />
        {(dateFrom || dateTo) && (
          <button onClick={() => { setDateFrom(""); setDateTo(""); }} style={{ padding: "5px 10px", background: "#f1f5f9", color: "#475569", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>🔄 إعادة تعيين</button>
        )}
        {source === "chart_of_accounts" && (
          <>
            <span style={{ width: 1, height: 22, background: "#cbd5e1", margin: "0 2px" }} />
            <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "#475569", cursor: "pointer", fontWeight: 600 }}>
              <input type="checkbox" checked={hideInactive} onChange={e => setHideInactive(e.target.checked)} />
              <span>إخفاء غير النشطة ({stats.inactive})</span>
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "#475569", cursor: "pointer", fontWeight: 600 }}>
              <input type="checkbox" checked={hideNoActivity} onChange={e => setHideNoActivity(e.target.checked)} />
              <span>إخفاء التي بدون حركة ({stats.withoutActivity})</span>
            </label>
          </>
        )}
        <div style={{ marginRight: "auto", fontSize: 11, color: "#64748b" }}>{filtered.length} حساب{filtered.length !== items.length ? ` (من ${items.length})` : ""}</div>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: "#64748b" }}>...جاري التحميل</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: 40, textAlign: "center", color: "#94a3b8", background: "#f8fafc", borderRadius: 10, border: "1px dashed #cbd5e1" }}>لا توجد بيانات</div>
      ) : (
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                <th style={th}>الكود</th>
                {/* v3.3.527 — Show the Daftra-internal account ID (useful for cross-referencing) */}
                <th style={th}>رقم Daftra</th>
                <th style={th}>اسم الحساب</th>
                <th style={th}>النوع</th>
                <th style={th}>الحالة</th>
                <th style={th}>عدد المعاملات</th>
                <th style={{ ...th, textAlign: "left" }}>إجمالي مدين</th>
                <th style={{ ...th, textAlign: "left" }}>إجمالي دائن</th>
                <th style={{ ...th, textAlign: "left" }}>الصافي</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a, idx) => {
                const noActivity = a.has_no_activity;
                const inactive = a.is_active === false;
                return (
                  <tr key={a.journal_account_id} style={{
                    borderBottom: "1px solid #f1f5f9",
                    background: idx % 2 === 0 ? "#fff" : "#fafafa",
                    opacity: inactive ? 0.55 : 1,
                  }}>
                    <td style={{ ...td, textAlign: "center", color: "#475569", fontWeight: 600, direction: "ltr" }}>{a.journal_account_code || "—"}</td>
                    {/* v3.3.527 — Daftra-internal numeric ID (different from accountant's code) */}
                    <td style={{ ...td, textAlign: "center", direction: "ltr" }}>
                      <span style={{ background: "#ede9fe", color: "#5b21b6", padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 700, fontFamily: "monospace" }}>
                        #{a.journal_account_id}
                      </span>
                    </td>
                    <td style={{ ...td, fontWeight: 700, color: "#0f172a" }}>{a.journal_account_name || "—"}</td>
                    <td style={{ ...td, textAlign: "center" }}>
                      {a.journal_account_type
                        ? <span style={{ background: "#e0e7ff", color: "#3730a3", padding: "1px 7px", borderRadius: 4, fontSize: 10, fontWeight: 700 }}>{a.journal_account_type}</span>
                        : <span style={{ color: "#d1d5db" }}>—</span>}
                    </td>
                    <td style={{ ...td, textAlign: "center" }}>
                      {inactive
                        ? <span style={{ background: "#fee2e2", color: "#dc2626", padding: "1px 7px", borderRadius: 4, fontSize: 10, fontWeight: 700 }}>غير نشط</span>
                        : noActivity
                          ? <span style={{ background: "#f1f5f9", color: "#64748b", padding: "1px 7px", borderRadius: 4, fontSize: 10, fontWeight: 700 }}>بدون حركة</span>
                          : <span style={{ background: "#dcfce7", color: "#15803d", padding: "1px 7px", borderRadius: 4, fontSize: 10, fontWeight: 700 }}>نشط</span>}
                    </td>
                    <td style={{ ...td, textAlign: "center", color: noActivity ? "#cbd5e1" : "#0f172a" }}>{fmt(a.transactions_count)}</td>
                    <td style={{ ...td, textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", color: Number(a.total_debit) > 0 ? "#15803d" : "#cbd5e1", fontWeight: 700 }}>{Number(a.total_debit) > 0 ? fmt(a.total_debit) : "—"}</td>
                    <td style={{ ...td, textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", color: Number(a.total_credit) > 0 ? "#b45309" : "#cbd5e1", fontWeight: 700 }}>{Number(a.total_credit) > 0 ? fmt(a.total_credit) : "—"}</td>
                    <td style={{ ...td, textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", fontWeight: 800, color: Number(a.net) >= 0 ? "#0f172a" : "#dc2626" }}>{fmt(a.net)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// SETTINGS TAB
// ═══════════════════════════════════════════════════════════════════
function SettingsTab({ canSync }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ provider: "daftra", base_url: "", api_key: "", list_id: 1 });
  const [saving, setSaving] = useState(false);
  // v3.3.540 — Single unified sync replaces the old partial/full split + standalone FullResyncWidget
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await accounting.getConfig();
      setConfig(r.data);
      setForm({
        provider: r.data.provider || "daftra",
        base_url: r.data.base_url || "",
        api_key: "",
        list_id: r.data.list_id || 1,
      });
    } catch (e) {
      toast.error("فشل تحميل الإعدادات");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { provider: form.provider, base_url: form.base_url, list_id: form.list_id };
      if (form.api_key && form.api_key !== "***") payload.api_key = form.api_key;
      await accounting.updateConfig(payload);
      toast.success("تم حفظ الإعدادات");
      setEditing(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || "فشل الحفظ");
    } finally {
      setSaving(false);
    }
  };

  // v3.3.540 — Unified sync: one button does everything.
  //   • Refreshes chart of accounts
  //   • Refreshes cost centers
  //   • Wipes & re-fetches all journal_transaction rows (~36k)
  //   • Cross-references cost_center_transaction for proper CC linkage
  //   • Saves drafts with is_draft flag
  // Replaces the old partial/full toggle and the standalone FullResyncWidget.
  const runSync = async () => {
    if (!confirm("ستتم مزامنة شاملة من Daftra (5-10 دقائق). سيتم استبدال جميع المعاملات المحلية بآخر إصدار من Daftra. هل تريد المتابعة؟")) return;
    setSyncing(true);
    setSyncResult(null);
    try {
      const r = await accounting.wipeAndResyncFull();
      setSyncResult(r.data);
      const parts = [
        `${fmt(r.data.transactions_saved)} معاملة`,
        `${fmt(r.data.accounts_synced)} حساب`,
        `${fmt(r.data.cost_centers_synced)} مركز`,
      ];
      if (r.data.cc_links_applied) parts.push(`${fmt(r.data.cc_links_applied)} رابط CC`);
      if (r.data.drafts_saved) parts.push(`${fmt(r.data.drafts_saved)} مسودة`);
      toast.success(`✓ اكتملت المزامنة في ${r.data.elapsed_seconds}s — ${parts.join(" · ")}`, { duration: 8000 });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || "فشلت المزامنة");
    } finally {
      setSyncing(false);
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: "#64748b" }}>...جاري التحميل</div>;

  return (
    <div style={{ maxWidth: 800 }}>
      {/* Sync status banner */}
      <div style={{
        marginBottom: 14,
        padding: "12px 16px",
        background: config?.last_sync_status === "success" ? "#dcfce7" : config?.last_sync_status === "error" ? "#fee2e2" : "#f1f5f9",
        border: `1px solid ${config?.last_sync_status === "success" ? "#86efac" : config?.last_sync_status === "error" ? "#fca5a5" : "#cbd5e1"}`,
        borderRadius: 10,
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {config?.last_sync_status === "success" ? <FiCheckCircle size={20} color="#16a34a" /> :
           config?.last_sync_status === "error" ? <FiAlertCircle size={20} color="#dc2626" /> :
           <FiDatabase size={20} color="#64748b" />}
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: config?.last_sync_status === "success" ? "#15803d" : config?.last_sync_status === "error" ? "#dc2626" : "#475569" }}>
              {config?.last_synced_at
                ? `آخر مزامنة: ${fmtDateTime(config.last_synced_at)}`
                : "لم تتم أي مزامنة بعد"}
            </div>
            {config?.last_sync_status === "success" && (
              <div style={{ fontSize: 11, color: "#15803d", marginTop: 2 }}>✓ {fmt(config.last_sync_count)} معاملة مُحدَّثة</div>
            )}
            {config?.last_sync_status === "error" && config?.last_sync_error && (
              <div style={{ fontSize: 11, color: "#dc2626", marginTop: 2 }}>❌ {config.last_sync_error}</div>
            )}
          </div>
        </div>
      </div>

      {/* API Config card */}
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 16, marginBottom: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: "#0f172a" }}>🔑 إعدادات الربط</div>
          {!editing && canSync && (
            <button onClick={() => setEditing(true)} style={{ padding: "6px 14px", background: "#0e7490", color: "#fff", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>تعديل</button>
          )}
        </div>

        <div style={{ display: "grid", gap: 10 }}>
          <Row label="🏢 مزوّد الخدمة" value={
            editing ? (
              <select value={form.provider} onChange={e => setForm({ ...form, provider: e.target.value })} style={inputS}>
                <option value="daftra">Daftra</option>
                <option value="quickbooks" disabled>QuickBooks (قريباً)</option>
                <option value="zoho" disabled>Zoho Books (قريباً)</option>
              </select>
            ) : (config?.provider || "—")
          } />
          <Row label="🌐 الرابط (Base URL)" value={
            editing ? (
              <input type="text" value={form.base_url} onChange={e => setForm({ ...form, base_url: e.target.value })} placeholder="https://yoursubdomain.daftra.com" style={{ ...inputS, minWidth: 380, direction: "ltr", textAlign: "left" }} />
            ) : (config?.base_url || "—")
          } />
          <Row label="🔐 مفتاح API" value={
            editing ? (
              <input type="text" value={form.api_key} onChange={e => setForm({ ...form, api_key: e.target.value })} placeholder={config?.has_api_key ? "اتركه فارغاً للاحتفاظ بالقيمة الحالية" : "أدخل مفتاح API"} style={{ ...inputS, minWidth: 380, direction: "ltr", textAlign: "left", fontFamily: "monospace" }} />
            ) : (
              <span style={{ direction: "ltr", textAlign: "right", fontFamily: "monospace", color: "#0e7490" }}>
                {config?.api_key_masked || "غير مُعيَّن"}
              </span>
            )
          } />
          <Row label="📋 معرّف القائمة (List ID)" value={
            editing ? (
              <input type="number" value={form.list_id} onChange={e => setForm({ ...form, list_id: parseInt(e.target.value) || 1 })} style={{ ...inputS, width: 100, direction: "ltr", textAlign: "left" }} />
            ) : (config?.list_id || "1")
          } />
        </div>

        {editing && (
          <div style={{ display: "flex", gap: 8, marginTop: 14, paddingTop: 12, borderTop: "1px solid #e2e8f0" }}>
            <button onClick={save} disabled={saving} style={{ padding: "8px 16px", background: "#16a34a", color: "#fff", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: saving ? "wait" : "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 5 }}>
              <FiSave size={14} /> {saving ? "...جاري الحفظ" : "حفظ"}
            </button>
            <button onClick={() => { setEditing(false); setForm({ provider: config.provider, base_url: config.base_url, api_key: "", list_id: config.list_id }); }} disabled={saving} style={{ padding: "8px 16px", background: "#f1f5f9", color: "#475569", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
              إلغاء
            </button>
          </div>
        )}
      </div>

      {/* v3.3.540 — Unified Sync card. Replaces the old partial/full toggle
          AND the separate FullResyncWidget. One button does everything:
          chart of accounts + cost centers + journal_transaction + CC linkage + drafts. */}
      <div style={{
        background: "linear-gradient(135deg, #0e7490 0%, #155e75 100%)",
        border: "none", borderRadius: 12, padding: 20, marginBottom: 14,
        color: "#fff",
      }}>
        <div style={{ fontSize: 17, fontWeight: 800, marginBottom: 6 }}>
          🔄 مزامنة من Daftra
        </div>
        <div style={{ fontSize: 12, opacity: 0.92, marginBottom: 14, lineHeight: 1.7 }}>
          يسحب كل البيانات من Daftra ويعيد بناء قاعدة البيانات المحلية: دليل الحسابات، مراكز التكلفة،
          جميع القيود (~36 ألف سطر)، وروابط مراكز التكلفة. المسودات تُحفظ مع علامة مميّزة.
          <br />
          <strong>المدة المتوقعة:</strong> 5-10 دقائق · يمكن إغلاق المتصفح أثناء التشغيل.
          <br />
          {/* v3.3.768 — مجدول تلقائي داخل الخادم */}
          <strong>🕐 مزامنة تلقائية:</strong> تعمل من الخادم مرتين يوميًا — الساعة 1:00 ظهرًا و10:00 مساءً (بتوقيت الرياض) دون أي تدخل منك.
        </div>

        {/* v3.3.541 — Notice about the bug fix in cost-center linkage.
            Users who synced under v3.3.539/540 have stale data and MUST resync to fix it. */}
        <div style={{
          background: "rgba(254, 240, 138, 0.15)",
          border: "1px solid rgba(254, 240, 138, 0.5)",
          borderRadius: 8, padding: "10px 12px", marginBottom: 14,
          fontSize: 11.5, lineHeight: 1.6,
        }}>
          ⚠️ <strong>إن قمت بمزامنة سابقاً تحت v3.3.539 أو v3.3.540:</strong> أرقام مراكز التكلفة كانت خاطئة بسبب
          ربط مزدوج (المدين = الدائن دائماً). تم إصلاح المنطق في v3.3.541. <strong>أعد المزامنة الآن لتصحيح البيانات.</strong>
        </div>

        <button
          onClick={runSync}
          disabled={syncing || !canSync || !config?.has_api_key}
          style={{
            padding: "12px 28px",
            background: syncing ? "rgba(255,255,255,0.2)" : (canSync && config?.has_api_key) ? "#fff" : "rgba(255,255,255,0.15)",
            color: syncing ? "#fff" : (canSync && config?.has_api_key) ? "#0e7490" : "rgba(255,255,255,0.5)",
            border: "none", borderRadius: 10,
            fontSize: 14, fontWeight: 800,
            cursor: (syncing || !canSync || !config?.has_api_key) ? "not-allowed" : "pointer",
            fontFamily: "inherit",
            display: "flex", alignItems: "center", gap: 10,
            boxShadow: syncing ? "none" : "0 2px 6px rgba(0,0,0,0.15)",
          }}
        >
          <FiRefreshCw size={18} className={syncing ? "spin" : ""} />
          {syncing ? "جاري المزامنة... (لا تُغلق المتصفح)" : "🚀 ابدأ المزامنة الآن"}
        </button>

        {!canSync && (
          <div style={{ marginTop: 12, fontSize: 11.5, color: "#fef08a" }}>
            ⚠️ لا تملك صلاحية تشغيل المزامنة
          </div>
        )}
        {canSync && !config?.has_api_key && (
          <div style={{ marginTop: 12, fontSize: 11.5, color: "#fef08a" }}>
            ⚠️ أدخل مفتاح API في "إعدادات الربط" أولاً
          </div>
        )}

        {/* Result panel — shown after a successful sync */}
        {syncResult && (
          <div style={{
            marginTop: 14, padding: 12,
            background: "rgba(255,255,255,0.12)", borderRadius: 8,
            fontSize: 12, lineHeight: 1.8,
          }}>
            <div style={{ fontWeight: 800, marginBottom: 6 }}>
              ✅ اكتملت المزامنة في {syncResult.elapsed_seconds}s
            </div>
            <ul style={{ margin: 0, paddingInlineStart: 18, opacity: 0.95 }}>
              <li>دليل الحسابات: <strong>{fmt(syncResult.accounts_synced)}</strong></li>
              <li>مراكز التكلفة: <strong>{fmt(syncResult.cost_centers_synced)}</strong></li>
              <li>المعاملات المحفوظة: <strong>{fmt(syncResult.transactions_saved)}</strong> (من أصل تم مسحه: {fmt(syncResult.transactions_wiped)})</li>
              {syncResult.cc_links_applied > 0 && (
                <li>روابط مراكز التكلفة: <strong>{fmt(syncResult.cc_links_applied)}</strong> ({fmt(syncResult.journal_cc_links)} قيد فريد)</li>
              )}
              {syncResult.drafts_saved > 0 && (
                <li>مسودات محفوظة (مخفية افتراضياً): <strong>{fmt(syncResult.drafts_saved)}</strong></li>
              )}
              {syncResult.rows_failed > 0 && (
                <li style={{ color: "#fecaca" }}>سطور فشلت: {syncResult.rows_failed}</li>
              )}
            </ul>
          </div>
        )}
      </div>

      {/* v3.4.646 — سجل المزامنات المدمج */}
      <SyncRunsLog syncing={syncing} />

      {/* v3.3.531 — Reorganized Settings layout into 3 logical groups.
          Removed deprecated WipeResyncWidget (cost_center_transaction sync) as it's
          superseded by FullResyncWidget. Grouped maintenance and developer tools
          into collapsible sections to reduce visual noise. */}

      {/* v3.3.540 — GROUP 1 (FullResyncWidget) removed. The unified sync card above
          now handles everything: chart of accounts + cost centers + transactions
          + CC linkage + drafts. No more standalone "العمليات الرئيسية" section. */}

      {/* ── GROUP 2: Diagnostic & repair tools (collapsed by default) ────────── */}
      <SettingsSection
        title="🔧 أدوات الصيانة والتشخيص"
        subtitle="فحص قيد فردي · تشخيص الأرصدة · إصلاح ربط الحسابات"
        defaultOpen={false}
        color="#0e7490"
      >
        {/* v3.3.511 — Diagnostic tools: inspect & resync a specific transaction */}
        <TransactionDiagnosticWidget canSync={canSync} />

        {/* v3.3.521 — Diagnose accounts (why are account totals zero in reports?) */}
        <AccountsDiagnosticWidget />

        {/* v3.3.522 — Auto-fix orphaned account leaves (relink wrong IDs by name match) */}
        <OrphanedAccountsFixWidget canSync={canSync} />

        {/* v3.3.532 — Compare account row count between local and Daftra */}
        <CompareAccountWidget canSync={canSync} />

        {/* v3.3.535 — Diagnose drafts (schema migration + stored count) */}
        <DraftsDiagnosticWidget canSync={canSync} />

        {/* v3.3.540 — RelinkCostCentersWidget removed. Cost-center linkage is now
            part of the unified sync (Step 3.5 in /wipe-and-resync-full).
            The /relink-cost-centers endpoint stays available in the backend for
            emergency use via direct API call, but no longer exposed in the UI. */}
      </SettingsSection>

      {/* ── GROUP 3: Developer tools (collapsed by default, can_sync only) ───── */}
      {canSync && (
        <SettingsSection
          title="🛠️ أدوات المطوّر"
          subtitle="استكشاف Daftra API — لا يُحتاج إليها في الاستخدام اليومي"
          defaultOpen={false}
          color="#7c3aed"
        >
          {/* v3.3.524 — Probe Daftra to find an entity that returns ALL transactions */}
          <DaftraEntityProbeWidget canSync={canSync} />
        </SettingsSection>
      )}

      {/* Spinner CSS */}
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .spin { animation: spin 1s linear infinite; }
      `}</style>
    </div>
  );
}

// v3.3.511 — Diagnostic widget for inspecting/resyncing a single transaction
// v3.3.531 — Collapsible section wrapper for grouping Settings widgets.
// Reduces visual noise by hiding less-frequently-used tools behind a click.
function SettingsSection({ title, subtitle, defaultOpen = false, color = "#475569", children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{
      marginTop: 14,
      background: "#fff",
      border: `2px solid ${color}30`,
      borderRadius: 10,
      overflow: "hidden",
    }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: "100%",
          padding: "14px 18px",
          background: open ? `${color}10` : "#fff",
          border: "none",
          borderBottom: open ? `1px solid ${color}30` : "none",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          textAlign: "right",
          fontFamily: "inherit",
          transition: "background 0.15s",
        }}
        onMouseEnter={e => { if (!open) e.currentTarget.style.background = "#f8fafc"; }}
        onMouseLeave={e => { if (!open) e.currentTarget.style.background = "#fff"; }}
      >
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color, marginBottom: subtitle ? 3 : 0 }}>
            {title}
          </div>
          {subtitle && (
            <div style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>
              {subtitle}
            </div>
          )}
        </div>
        <div style={{
          fontSize: 18, color,
          transform: open ? "rotate(180deg)" : "rotate(0deg)",
          transition: "transform 0.2s",
        }}>▼</div>
      </button>
      {open && (
        <div style={{ padding: "4px 14px 14px 14px" }}>
          {children}
        </div>
      )}
    </div>
  );
}

function TransactionDiagnosticWidget({ canSync }) {
  const [txId, setTxId] = useState("");
  const [inspecting, setInspecting] = useState(false);
  const [resyncing, setResyncing] = useState(false);
  const [result, setResult] = useState(null);

  const handleInspect = async () => {
    const id = parseInt(txId);
    if (!id) { toast.error("أدخل رقم القيد"); return; }
    setInspecting(true);
    setResult(null);
    try {
      const r = await accounting.inspectTransaction(id);
      setResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.error || "فشل الفحص");
    } finally {
      setInspecting(false);
    }
  };
  const handleResync = async () => {
    const id = parseInt(txId);
    if (!id) return;
    if (!confirm(`إعادة مزامنة القيد ${id} مع Daftra؟`)) return;
    setResyncing(true);
    try {
      const r = await accounting.resyncTransaction(id);
      toast.success(r.data?.message || "تم");
      // Refresh inspection result
      try {
        const r2 = await accounting.inspectTransaction(id);
        setResult(r2.data);
      } catch {}
    } catch (e) {
      toast.error(e?.response?.data?.error || "فشلت إعادة المزامنة");
    } finally {
      setResyncing(false);
    }
  };

  return (
    <div style={{ marginTop: 14, padding: 18, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: "#0f172a" }}>🔍 أداة التشخيص — فحص قيد فردي (Transaction)</div>
      </div>
      <div style={{ fontSize: 11, color: "#64748b", marginBottom: 12, lineHeight: 1.7 }}>
        <strong style={{ color: "#dc2626" }}>⚠️ مهم:</strong> هذه الأداة تفحص <strong>قيد محاسبي واحد</strong> (transaction)، وليست لفحص حساب أو مركز تكلفة.
        <br />
        <strong>الـ ID المطلوب</strong> هو ID القيد الفردي في journal_transaction (مثلاً <code>3105</code>، <code>1637</code>).
        تجدها في عمود "رقم القيد" في تبويب "القيود المحاسبية" أو في "كشف الحساب المفصّل".
        <br />
        <span style={{ color: "#94a3b8" }}>
          ❌ ليست: كود الحساب (1220001) · ID الحساب (371) · رقم مركز التكلفة
          <br />
          ✓ هي: ID القيد المحاسبي الفردي
        </span>
        <br />
        <span style={{ color: "#94a3b8" }}>⏱ الفحص قد يستغرق 10-60 ثانية لأنه يبحث في كل صفحات Daftra حتى يجد القيد.</span>
      </div>
      <div style={{ display: "flex", gap: 6, alignItems: "stretch", marginBottom: 10 }}>
        <input
          type="number"
          placeholder="أدخل ID القيد (مثلاً 3105)"
          value={txId}
          onChange={e => setTxId(e.target.value)}
          style={{ flex: 1, padding: "8px 12px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 12, fontFamily: "inherit", direction: "ltr", textAlign: "right" }}
        />
        <button
          onClick={handleInspect}
          disabled={inspecting || !txId}
          style={{
            padding: "8px 16px",
            background: inspecting ? "#94a3b8" : "#0e7490",
            color: "#fff", border: "none", borderRadius: 6,
            fontSize: 12, fontWeight: 700,
            cursor: inspecting || !txId ? "not-allowed" : "pointer",
            fontFamily: "inherit",
          }}
        >
          {inspecting ? "...جاري الفحص" : "🔍 فحص"}
        </button>
        {canSync && (
          <button
            onClick={handleResync}
            disabled={resyncing || !txId}
            style={{
              padding: "8px 16px",
              background: resyncing ? "#94a3b8" : "#16a34a",
              color: "#fff", border: "none", borderRadius: 6,
              fontSize: 12, fontWeight: 700,
              cursor: resyncing || !txId ? "not-allowed" : "pointer",
              fontFamily: "inherit",
            }}
          >
            {resyncing ? "...جاري" : "♻️ إعادة مزامنة"}
          </button>
        )}
      </div>

      {result && (
        <div style={{ marginTop: 12, padding: 12, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8 }}>
          {/* Status banner */}
          <div style={{
            padding: "8px 12px", marginBottom: 10, borderRadius: 6,
            background: !result.exists_in_daftra ? "#fee2e2" : Object.keys(result.diff || {}).length > 0 ? "#fef3c7" : "#dcfce7",
            border: `1px solid ${!result.exists_in_daftra ? "#fca5a5" : Object.keys(result.diff || {}).length > 0 ? "#fcd34d" : "#86efac"}`,
            color: !result.exists_in_daftra ? "#dc2626" : Object.keys(result.diff || {}).length > 0 ? "#92400e" : "#15803d",
            fontSize: 12, fontWeight: 700,
          }}>
            {!result.exists_in_daftra
              ? "⚠️ القيد غير موجود في Daftra (محذوف هناك)"
              : Object.keys(result.diff || {}).length > 0
                ? `⚠️ يوجد اختلاف في ${Object.keys(result.diff).length} حقل بين المحلي و Daftra`
                : "✓ القيد متطابق — لا حاجة لإجراء"}
          </div>

          <div style={{ fontSize: 11, color: "#475569", marginBottom: 8 }}>
            💡 الإجراء المقترح: <strong style={{ color: "#0f172a" }}>{result.action_needed}</strong>
            {result.pages_scanned ? <span style={{ marginRight: 10, color: "#94a3b8" }}>· تم فحص {result.pages_scanned} صفحة في Daftra</span> : null}
          </div>

          {/* Differences table */}
          {Object.keys(result.diff || {}).length > 0 && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#475569", marginBottom: 6 }}>الاختلافات:</div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                <thead>
                  <tr style={{ background: "#f1f5f9" }}>
                    <th style={{ padding: 6, textAlign: "right", borderBottom: "1px solid #cbd5e1" }}>الحقل</th>
                    <th style={{ padding: 6, textAlign: "right", borderBottom: "1px solid #cbd5e1", color: "#dc2626" }}>محلياً (قديم)</th>
                    <th style={{ padding: 6, textAlign: "right", borderBottom: "1px solid #cbd5e1", color: "#15803d" }}>في Daftra (الحقيقي)</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(result.diff).map(([field, vals]) => (
                    <tr key={field} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: 6, fontWeight: 700, color: "#0f172a", direction: "ltr", textAlign: "right" }}>{field}</td>
                      <td style={{ padding: 6, color: "#dc2626" }}>{vals.local === null || vals.local === "" ? <em>—</em> : String(vals.local)}</td>
                      <td style={{ padding: 6, color: "#15803d" }}>{vals.daftra === null || vals.daftra === "" ? <em>—</em> : String(vals.daftra)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Side-by-side full data */}
          <details style={{ marginTop: 12 }}>
            <summary style={{ cursor: "pointer", fontSize: 11, color: "#64748b", fontWeight: 600 }}>عرض كل البيانات</summary>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 8 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#dc2626", marginBottom: 4 }}>محلياً:</div>
                <pre style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 4, padding: 8, fontSize: 10, overflow: "auto", direction: "ltr", textAlign: "left" }}>
                  {JSON.stringify(result.local, null, 2)}
                </pre>
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#15803d", marginBottom: 4 }}>في Daftra:</div>
                <pre style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 4, padding: 8, fontSize: 10, overflow: "auto", direction: "ltr", textAlign: "left" }}>
                  {JSON.stringify(result.daftra_mapped, null, 2)}
                </pre>
              </div>
            </div>
          </details>
        </div>
      )}
    </div>
  );
}

// v3.3.514 — Widget to find & cleanup duplicate transactions
// Duplicates appear when Daftra deletes the original cost_center_transaction row
// and creates a new one with a different id (e.g. after editing cost-center link).
function DuplicateCleanupWidget({ canSync }) {
  const [loading, setLoading] = useState(false);
  const [cleaningUp, setCleaningUp] = useState(false);
  const [data, setData] = useState(null);
  const [strategy, setStrategy] = useState("keep_latest_id");

  const handleScan = async () => {
    setLoading(true);
    setData(null);
    try {
      const r = await accounting.findDuplicates();
      setData(r.data);
      if ((r.data?.duplicate_groups || []).length === 0) {
        toast.success("✓ لا يوجد أي قيود مكرّرة في النظام");
      }
    } catch (e) {
      toast.error(e?.response?.data?.error || "فشل الفحص");
    } finally {
      setLoading(false);
    }
  };

  const handleCleanup = async (dryRun) => {
    if (!data || !data.duplicate_groups || data.duplicate_groups.length === 0) return;
    if (!dryRun) {
      const wouldDel = data.total_duplicates;
      if (!confirm(`سيتم حذف ${wouldDel} قيد مكرّر. هذا الإجراء لا يمكن التراجع عنه.\n\nمتأكد؟`)) return;
    }
    setCleaningUp(true);
    try {
      const r = await accounting.cleanupDuplicates({ strategy, dry_run: dryRun });
      if (dryRun) {
        toast.success(`معاينة: سيتم حذف ${r.data.would_delete} قيد من ${r.data.duplicate_groups} مجموعة`);
      } else {
        toast.success(`✓ تم حذف ${r.data.deleted} قيد مكرّر`);
        // Re-scan after cleanup
        handleScan();
      }
    } catch (e) {
      toast.error(e?.response?.data?.error || "فشلت العملية");
    } finally {
      setCleaningUp(false);
    }
  };

  return (
    <div style={{ marginTop: 14, padding: 18, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: "#dc2626" }}>⚠️ كشف وتنظيف القيود المكرّرة</div>
      </div>
      <div style={{ fontSize: 11, color: "#64748b", marginBottom: 12, lineHeight: 1.7 }}>
        عند تعديل ربط مركز التكلفة في Daftra، أحياناً تُنشأ نسخة جديدة من القيد دون حذف القديمة من قاعدة البيانات لدينا.
        النتيجة: قيد واحد يظهر مرّتين في كشف الحساب → المبالغ تتضاعف. هذه الأداة تكتشف هذه الحالات وتسمح بإصلاحها.
        <br />
        <strong style={{ color: "#dc2626" }}>المعيار:</strong> صفان أو أكثر لهما نفس (journal_id + journal_account_id + debit + credit) = duplicate.
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
        <button
          onClick={handleScan}
          disabled={loading}
          style={{
            padding: "8px 16px", background: loading ? "#94a3b8" : "#dc2626", color: "#fff",
            border: "none", borderRadius: 6, fontSize: 12, fontWeight: 700,
            cursor: loading ? "not-allowed" : "pointer", fontFamily: "inherit",
          }}
        >{loading ? "...جاري الفحص" : "🔍 فحص الـ Duplicates"}</button>

        {data && data.duplicate_groups && data.duplicate_groups.length > 0 && canSync && (
          <>
            <select
              value={strategy}
              onChange={e => setStrategy(e.target.value)}
              style={{ padding: "8px 10px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 11, fontFamily: "inherit" }}
            >
              <option value="keep_latest_id">احتفظ بأحدث ID (الافتراضي)</option>
              <option value="keep_with_cc">احتفظ بالصف الذي له مركز تكلفة</option>
            </select>
            <button
              onClick={() => handleCleanup(true)}
              disabled={cleaningUp}
              style={{
                padding: "8px 14px", background: "#f59e0b", color: "#fff",
                border: "none", borderRadius: 6, fontSize: 12, fontWeight: 700,
                cursor: cleaningUp ? "not-allowed" : "pointer", fontFamily: "inherit",
              }}
            >👁 معاينة (Dry Run)</button>
            <button
              onClick={() => handleCleanup(false)}
              disabled={cleaningUp}
              style={{
                padding: "8px 14px", background: "#16a34a", color: "#fff",
                border: "none", borderRadius: 6, fontSize: 12, fontWeight: 700,
                cursor: cleaningUp ? "not-allowed" : "pointer", fontFamily: "inherit",
              }}
            >{cleaningUp ? "...جاري" : "✓ تنظيف فعلي"}</button>
          </>
        )}
      </div>

      {data && (
        <div style={{ padding: 12, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8 }}>
          {/* Summary */}
          <div style={{
            padding: "8px 12px", marginBottom: 10, borderRadius: 6,
            background: data.duplicate_groups.length === 0 ? "#dcfce7" : "#fee2e2",
            border: `1px solid ${data.duplicate_groups.length === 0 ? "#86efac" : "#fca5a5"}`,
            color: data.duplicate_groups.length === 0 ? "#15803d" : "#dc2626",
            fontSize: 12, fontWeight: 700,
          }}>
            {data.duplicate_groups.length === 0
              ? "✓ لا يوجد أي قيود مكرّرة"
              : `⚠️ تم العثور على ${data.duplicate_groups.length} مجموعة مكرّرة (إجمالي القيود الزائدة: ${data.total_duplicates})`}
          </div>

          {data.duplicate_groups.length > 0 && (
            <div style={{ maxHeight: 400, overflow: "auto", border: "1px solid #e2e8f0", borderRadius: 6 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                <thead>
                  <tr style={{ background: "#fef2f2", borderBottom: "1px solid #fca5a5", position: "sticky", top: 0 }}>
                    <th style={{ padding: 6, textAlign: "right", color: "#dc2626" }}>رقم القيد</th>
                    <th style={{ padding: 6, textAlign: "right", color: "#dc2626" }}>التاريخ</th>
                    <th style={{ padding: 6, textAlign: "right", color: "#dc2626" }}>الحساب</th>
                    <th style={{ padding: 6, textAlign: "left", color: "#dc2626" }}>مدين</th>
                    <th style={{ padding: 6, textAlign: "left", color: "#dc2626" }}>دائن</th>
                    <th style={{ padding: 6, textAlign: "center", color: "#dc2626" }}>عدد التكرارات</th>
                    <th style={{ padding: 6, textAlign: "right", color: "#dc2626" }}>IDs (الأحدث أولاً)</th>
                    <th style={{ padding: 6, textAlign: "right", color: "#dc2626" }}>مراكز التكلفة</th>
                  </tr>
                </thead>
                <tbody>
                  {data.duplicate_groups.map((g, idx) => (
                    <tr key={idx} style={{ borderBottom: "1px solid #fef2f2", background: idx % 2 === 0 ? "#fff" : "#fafafa" }}>
                      <td style={{ padding: 6, fontWeight: 700, color: "#0f172a", direction: "ltr", textAlign: "right" }}>{g.journal_number || "—"}</td>
                      <td style={{ padding: 6, direction: "ltr", textAlign: "right" }}>{g.journal_date ? new Date(g.journal_date).toISOString().slice(0,10) : "—"}</td>
                      <td style={{ padding: 6, color: "#475569" }}>{g.journal_account_name || "—"}</td>
                      <td style={{ padding: 6, textAlign: "left", direction: "ltr", color: Number(g.debit) > 0 ? "#15803d" : "#cbd5e1", fontWeight: 700 }}>{Number(g.debit) > 0 ? fmt(g.debit) : "—"}</td>
                      <td style={{ padding: 6, textAlign: "left", direction: "ltr", color: Number(g.credit) > 0 ? "#b45309" : "#cbd5e1", fontWeight: 700 }}>{Number(g.credit) > 0 ? fmt(g.credit) : "—"}</td>
                      <td style={{ padding: 6, textAlign: "center", color: "#dc2626", fontWeight: 700 }}>{g.cnt}</td>
                      <td style={{ padding: 6, direction: "ltr", textAlign: "right", fontFamily: "monospace", fontSize: 10, color: "#475569" }}>
                        {(g.ids || []).slice().reverse().join(", ")}
                      </td>
                      <td style={{ padding: 6, fontSize: 10, color: "#64748b" }}>
                        {(g.cc_names || []).map((n, i) => n || `(فارغ #${(g.cc_ids||[])[i] || "—"})`).join(" / ")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// v3.3.519 — Nuclear-option widget: wipes ALL local transactions and re-fetches from Daftra.
// Use when local data is suspected of being corrupted (e.g. by a buggy historical cleanup).
function WipeResyncWidget({ canSync }) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [confirmText, setConfirmText] = useState("");

  const CONFIRM_PHRASE = "نعم احذف وأعد المزامنة";

  const handleRun = async () => {
    if (confirmText !== CONFIRM_PHRASE) {
      toast.error("اكتب عبارة التأكيد بالضبط");
      return;
    }
    if (!confirm("سيتم حذف كل القيود المحلية وإعادة تحميلها من Daftra. قد تستغرق عدة دقائق. هل تريد المتابعة؟")) return;
    setRunning(true);
    setResult(null);
    try {
      const r = await accounting.wipeAndResync();
      setResult(r.data);
      toast.success(`✓ تم: حذف ${r.data.wiped} · حفظ ${r.data.saved} · تخطي ${r.data.skipped_drafts} مسودة (${r.data.elapsed_seconds}s)`);
      setConfirmText("");
    } catch (e) {
      toast.error(e?.response?.data?.error || "فشلت العملية");
    } finally {
      setRunning(false);
    }
  };

  if (!canSync) return null;

  return (
    <div style={{ marginTop: 14, padding: 18, background: "#fff", border: "2px solid #dc2626", borderRadius: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: "#dc2626" }}>
          ☢️ إعادة بناء كاملة من Daftra (Wipe & Resync)
        </div>
      </div>

      <div style={{ fontSize: 11, color: "#64748b", marginBottom: 12, lineHeight: 1.8 }}>
        هذا الإجراء <strong style={{ color: "#dc2626" }}>يحذف كل القيود المحلية</strong> (بما فيها القيود التي قد تكون حُذفت بالخطأ سابقاً)،
        ثم يُعيد جلب كل شيء من Daftra من الصفر.
        <br />
        <strong>متى تستخدم؟</strong>
        <ul style={{ margin: "6px 18px 0 0", padding: 0 }}>
          <li>عندما لاحظت نقصاً في عدد القيود مقارنة بـ Daftra</li>
          <li>عندما تشك أن duplicate cleanup أو orphan cleanup حذف قيوداً شرعية</li>
          <li>عند فقدان ثقتك في تطابق البيانات المحلية مع المصدر</li>
        </ul>
        <br />
        <strong style={{ color: "#dc2626" }}>تنبيه:</strong> العملية تستغرق عادة 2-5 دقائق. لا تُغلق المتصفح أثناءها.
        المسودات (Drafts) لن تُجلب — فقط القيود المُعتمدة.
      </div>

      <div style={{ padding: 12, background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 8, marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: "#7f1d1d", marginBottom: 8, fontWeight: 700 }}>
          للتأكيد، اكتب: <code style={{ background: "#fff", padding: "2px 8px", borderRadius: 4, direction: "rtl" }}>{CONFIRM_PHRASE}</code>
        </div>
        <input
          type="text"
          value={confirmText}
          onChange={e => setConfirmText(e.target.value)}
          placeholder="اكتب عبارة التأكيد..."
          style={{
            width: "100%", padding: "8px 12px", border: "1px solid #fca5a5",
            borderRadius: 6, fontSize: 12, fontFamily: "inherit",
            boxSizing: "border-box",
            direction: "rtl",
          }}
        />
      </div>

      <button
        onClick={handleRun}
        disabled={running || confirmText !== CONFIRM_PHRASE}
        style={{
          padding: "10px 20px",
          background: running || confirmText !== CONFIRM_PHRASE ? "#94a3b8" : "#dc2626",
          color: "#fff", border: "none", borderRadius: 6,
          fontSize: 13, fontWeight: 800,
          cursor: (running || confirmText !== CONFIRM_PHRASE) ? "not-allowed" : "pointer",
          fontFamily: "inherit",
        }}
      >
        {running ? "...جاري إعادة البناء (قد يستغرق دقائق)" : "☢️ احذف وأعد المزامنة الآن"}
      </button>

      {result && (
        <div style={{ marginTop: 12, padding: 12, background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#15803d", marginBottom: 6 }}>✓ تمت العملية بنجاح</div>
          <div style={{ fontSize: 11, color: "#475569", display: "grid", gap: 4 }}>
            <div>• القيود المحذوفة محلياً: <strong>{result.wiped?.toLocaleString() || 0}</strong></div>
            <div>• القيود المحفوظة بعد إعادة الجلب: <strong style={{ color: "#15803d" }}>{result.saved?.toLocaleString() || 0}</strong></div>
            <div>• مسودات تم تخطّيها: <strong>{result.skipped_drafts?.toLocaleString() || 0}</strong></div>
            <div>• عدد الصفحات الممسوحة: <strong>{result.pages_scanned}</strong></div>
            <div>• المدة: <strong>{result.elapsed_seconds} ثانية</strong></div>
            {result.saved > result.wiped && (
              <div style={{ marginTop: 6, padding: 8, background: "#fef3c7", borderRadius: 4, color: "#92400e" }}>
                💡 لوحظ زيادة <strong>{(result.saved - result.wiped).toLocaleString()}</strong> قيد عمّا كان موجوداً محلياً — هذا يعني أن البيانات المحلية كانت ناقصة فعلاً.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// v3.3.526 — FULL resync from journal_transaction.
// Unlike WipeResyncWidget which uses cost_center_transaction (only 10,364 rows = cost-center-linked
// transactions), this widget fetches from journal_transaction which is the COMPLETE journal
// (~35,976 rows = ALL journal lines, with or without cost center).
// It also refreshes the chart of accounts and cost-center list from their dedicated endpoints
// for guaranteed completeness.
function FullResyncWidget({ canSync }) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [confirmText, setConfirmText] = useState("");

  const CONFIRM_PHRASE = "نعم احذف وأعد المزامنة الكاملة";

  const handleRun = async () => {
    if (confirmText !== CONFIRM_PHRASE) {
      toast.error("اكتب عبارة التأكيد بالضبط");
      return;
    }
    if (!confirm("سيتم: (1) تحديث دليل الحسابات (2) تحديث مراكز التكلفة (3) حذف كل القيود المحلية (4) إعادة جلب كل القيود من journal_transaction.\n\nقد تستغرق العملية 5-10 دقائق. هل تريد المتابعة؟")) return;
    setRunning(true);
    setResult(null);
    try {
      const r = await accounting.wipeAndResyncFull();
      setResult(r.data);
      toast.success(`✓ تم: حفظ ${r.data.transactions_saved} قيد · ${r.data.accounts_synced} حساب · ${r.data.cost_centers_synced} مركز`);
      setConfirmText("");
    } catch (e) {
      toast.error(e?.response?.data?.error || "فشلت العملية");
    } finally {
      setRunning(false);
    }
  };

  if (!canSync) return null;

  return (
    <div style={{ marginTop: 14, padding: 18, background: "linear-gradient(135deg, #fff7ed, #fefce8)", border: "3px solid #ea580c", borderRadius: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <div style={{ fontSize: 15, fontWeight: 900, color: "#9a3412" }}>
          🚀 مزامنة شاملة من journal_transaction (الموصى بها)
        </div>
        <span style={{ padding: "2px 8px", background: "#ea580c", color: "#fff", borderRadius: 4, fontSize: 10, fontWeight: 700 }}>v3.3.526</span>
      </div>

      <div style={{ fontSize: 11, color: "#7c2d12", marginBottom: 12, lineHeight: 1.8 }}>
        هذه هي <strong>المزامنة الكاملة الموصى بها</strong> — تجلب من <code>journal_transaction</code>
        (الـ entity الذي يحوي <strong>كل سطور القيود</strong>) بدلاً من <code>cost_center_transaction</code> القديم.
        <br />
        <strong>الفائدة المتوقعة:</strong>
        <ul style={{ margin: "6px 18px 0 0", padding: 0 }}>
          <li>عدد القيود: <strong>10,364 → ~35,976</strong> (3.5×)</li>
          <li><strong>كل الـ 144 حساب</strong> ستظهر أرصدتها الصحيحة (وليس فقط المرتبطة بمراكز تكلفة)</li>
          <li>تحديث كامل لدليل الحسابات ومراكز التكلفة قبل المزامنة (ضمان السحب الكامل)</li>
          <li>المسودات (Drafts) لن تُحفظ — فقط القيود المعتمدة</li>
        </ul>
        <br />
        <strong style={{ color: "#dc2626" }}>المدة المتوقعة: 5-10 دقائق.</strong> لا تُغلق المتصفح أثناءها.
      </div>

      <div style={{ padding: 12, background: "#fff", border: "2px solid #fca5a5", borderRadius: 8, marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: "#7f1d1d", marginBottom: 8, fontWeight: 700 }}>
          للتأكيد، اكتب: <code style={{ background: "#fef2f2", padding: "2px 8px", borderRadius: 4, direction: "rtl" }}>{CONFIRM_PHRASE}</code>
        </div>
        <input
          type="text"
          value={confirmText}
          onChange={e => setConfirmText(e.target.value)}
          placeholder="اكتب عبارة التأكيد..."
          style={{
            width: "100%", padding: "8px 12px", border: "1px solid #fca5a5",
            borderRadius: 6, fontSize: 12, fontFamily: "inherit",
            boxSizing: "border-box", direction: "rtl",
          }}
        />
      </div>

      <button
        onClick={handleRun}
        disabled={running || confirmText !== CONFIRM_PHRASE}
        style={{
          padding: "12px 24px",
          background: running || confirmText !== CONFIRM_PHRASE
            ? "#94a3b8"
            : "linear-gradient(135deg, #ea580c, #dc2626)",
          color: "#fff", border: "none", borderRadius: 6,
          fontSize: 14, fontWeight: 900,
          cursor: (running || confirmText !== CONFIRM_PHRASE) ? "not-allowed" : "pointer",
          fontFamily: "inherit",
        }}
      >
        {running ? "...جاري المزامنة الكاملة (5-10 دقائق)" : "🚀 ابدأ المزامنة الكاملة"}
      </button>

      {result && (
        <div style={{ marginTop: 12, padding: 14, background: "#fff", border: "2px solid #16a34a", borderRadius: 8 }}>
          <div style={{ fontSize: 14, fontWeight: 900, color: "#15803d", marginBottom: 10 }}>
            ✅ المزامنة الكاملة اكتملت
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10, marginBottom: 10 }}>
            <div style={{ padding: 10, background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 6, textAlign: "center" }}>
              <div style={{ fontSize: 22, fontWeight: 900, color: "#15803d" }}>{(result.transactions_saved || 0).toLocaleString()}</div>
              <div style={{ fontSize: 10, color: "#15803d", marginTop: 2 }}>قيد محفوظ</div>
            </div>
            <div style={{ padding: 10, background: "#f0f9ff", border: "1px solid #7dd3fc", borderRadius: 6, textAlign: "center" }}>
              <div style={{ fontSize: 22, fontWeight: 900, color: "#0369a1" }}>{(result.accounts_synced || 0).toLocaleString()}</div>
              <div style={{ fontSize: 10, color: "#0369a1", marginTop: 2 }}>حساب محدّث</div>
            </div>
            <div style={{ padding: 10, background: "#faf5ff", border: "1px solid #d8b4fe", borderRadius: 6, textAlign: "center" }}>
              <div style={{ fontSize: 22, fontWeight: 900, color: "#7c3aed" }}>{(result.cost_centers_synced || 0).toLocaleString()}</div>
              <div style={{ fontSize: 10, color: "#7c3aed", marginTop: 2 }}>مركز تكلفة محدّث</div>
            </div>
            <div style={{ padding: 10, background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 6, textAlign: "center" }}>
              <div style={{ fontSize: 22, fontWeight: 900, color: "#92400e" }}>{(result.drafts_skipped || 0).toLocaleString()}</div>
              <div style={{ fontSize: 10, color: "#92400e", marginTop: 2 }}>مسودة متخطّاة</div>
            </div>
          </div>
          <div style={{ fontSize: 11, color: "#475569", lineHeight: 1.8 }}>
            • القيود المحذوفة محلياً قبل البدء: <strong>{(result.transactions_wiped || 0).toLocaleString()}</strong><br />
            • الصفحات الممسوحة: <strong>{result.pages_scanned}</strong><br />
            • صفوف فشلت: <strong style={{ color: result.rows_failed > 0 ? "#dc2626" : "#15803d" }}>{result.rows_failed || 0}</strong><br />
            • المدة: <strong>{result.elapsed_seconds} ثانية</strong>
          </div>
          {result.transactions_saved > (result.transactions_wiped || 0) && (
            <div style={{ marginTop: 10, padding: 10, background: "#dcfce7", borderRadius: 4, color: "#15803d", fontSize: 12, fontWeight: 700 }}>
              💡 تمت إضافة <strong>{(result.transactions_saved - (result.transactions_wiped || 0)).toLocaleString()}</strong> قيد جديد لم يكن موجوداً في المزامنة السابقة (قيود بدون مراكز تكلفة)
            </div>
          )}
          <div style={{ marginTop: 10, padding: 10, background: "#dbeafe", borderRadius: 4, color: "#1e40af", fontSize: 11 }}>
            📌 الخطوة التالية: ادخل التقرير المحاسبي وتأكد من ظهور أرصدة كل الحسابات. إذا بقيت حسابات بأرصدة صفرية،
            استخدم أداة "إصلاح ربط الحسابات" لإعادة ربط IDs الشجرة بأرقامها الصحيحة.
          </div>
        </div>
      )}
    </div>
  );
}

// v3.3.521 — Diagnose why account totals show zero in the report.
// Surfaces useful facts: count of transactions, distinct accounts, classified leaves,
// top accounts by volume, and a sample row showing whether journal_account_id is populated.
function AccountsDiagnosticWidget() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const run = async () => {
    setLoading(true);
    setData(null);
    try {
      const r = await accounting.diagnoseAccounts();
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.error || "فشل التشخيص");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ marginTop: 14, padding: 18, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: "#3730a3" }}>🔍 تشخيص الحسابات</div>
      </div>
      <div style={{ fontSize: 11, color: "#64748b", marginBottom: 12, lineHeight: 1.7 }}>
        لو لاحظت أن أوراق الحسابات في التقرير المحاسبي تُظهر مجاميع صفرية رغم وجود قيود،
        استخدم هذه الأداة لكشف السبب: هل القيود فعلاً مرتبطة بالحسابات في DB؟ هل الـ IDs متطابقة مع شجرة الحسابات؟
      </div>

      <button
        onClick={run}
        disabled={loading}
        style={{
          padding: "8px 16px", background: loading ? "#94a3b8" : "#3730a3",
          color: "#fff", border: "none", borderRadius: 6,
          fontSize: 12, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer", fontFamily: "inherit",
        }}
      >{loading ? "...جاري الفحص" : "🔍 شغّل التشخيص"}</button>

      {data && (
        <div style={{ marginTop: 14 }}>
          {/* Summary */}
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
            gap: 8, marginBottom: 12,
          }}>
            {[
              ["عدد القيود الإجمالي", data.summary.total_transactions, "#0e7490"],
              ["قيود لها حساب", data.summary.transactions_with_account, "#15803d"],
              ["قيود لها مركز تكلفة", data.summary.transactions_with_cost_center, "#16a34a"],
              ["حسابات فريدة", data.summary.distinct_accounts_in_transactions, "#9333ea"],
              ["حسابات مصنّفة بالشجرة", data.summary.classified_account_leaves, "#3730a3"],
              ["حسابات مخفية", data.summary.hidden_accounts, "#64748b"],
            ].map(([label, val, color]) => (
              <div key={label} style={{
                padding: 10, background: "#f8fafc", border: `1px solid ${color}30`, borderRadius: 6, textAlign: "center",
              }}>
                <div style={{ fontSize: 18, fontWeight: 900, color }}>{(val || 0).toLocaleString()}</div>
                <div style={{ fontSize: 10, color: "#64748b", marginTop: 2 }}>{label}</div>
              </div>
            ))}
          </div>

          {/* Banner diagnosing the most likely issue */}
          {data.summary.transactions_with_account === 0 && data.summary.total_transactions > 0 && (
            <div style={{ padding: 12, background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 6, marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#dc2626", marginBottom: 4 }}>
                ⚠️ مشكلة مكتشفة: لا توجد قيود مرتبطة بحسابات!
              </div>
              <div style={{ fontSize: 11, color: "#7f1d1d" }}>
                المعنى: Daftra لا تُرجع `journal_account_id` في صفوف الـ cost_center_transaction.
                هذا غريب — يجب فحص الـ raw_data أدناه لمعرفة السبب.
              </div>
            </div>
          )}
          {data.summary.transactions_with_account > 0 && data.summary.classified_account_leaves === 0 && (
            <div style={{ padding: 12, background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 6, marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#92400e", marginBottom: 4 }}>
                ℹ️ لا توجد حسابات مصنّفة في الشجرة بعد
              </div>
              <div style={{ fontSize: 11, color: "#92400e" }}>
                صنّف بعض الحسابات في شجرة مراكز التكلفة (tab الحسابات) لتظهر مجاميعها في التقرير.
              </div>
            </div>
          )}
          {data.summary.transactions_with_account > 0 && data.summary.classified_account_leaves > 0 && (
            (() => {
              const zeroLeaves = data.account_leaves_in_tree.filter(l => l.linked_tx_count === 0);
              const allLeavesZero = zeroLeaves.length === data.account_leaves_in_tree.length;
              if (allLeavesZero) {
                return (
                  <div style={{ padding: 12, background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 6, marginBottom: 12 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#dc2626", marginBottom: 4 }}>
                      ⚠️ كل الحسابات المصنّفة في الشجرة ID-mismatched
                    </div>
                    <div style={{ fontSize: 11, color: "#7f1d1d" }}>
                      لا يوجد قيد واحد مرتبط بأي من الـ {data.account_leaves_in_tree.length} حساب مصنّف.
                      على الأرجح الـ IDs في الشجرة لا تطابق الـ IDs في القيود (مثلاً: الشجرة فيها IDs قديمة).
                      الحل: احذف الحسابات من الشجرة وأعد تصنيفها.
                    </div>
                  </div>
                );
              }
              return null;
            })()
          )}

          {/* Top accounts by volume */}
          {data.top_20_accounts_by_volume.length > 0 && (
            <details>
              <summary style={{ fontSize: 12, fontWeight: 700, color: "#3730a3", cursor: "pointer", marginBottom: 6 }}>
                📊 أعلى 20 حساب بعدد المعاملات
              </summary>
              <div style={{ maxHeight: 200, overflow: "auto", border: "1px solid #e2e8f0", borderRadius: 6 }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                  <thead>
                    <tr style={{ background: "#f1f5f9" }}>
                      <th style={{ padding: 4, textAlign: "right" }}>ID</th>
                      <th style={{ padding: 4, textAlign: "right" }}>الاسم</th>
                      <th style={{ padding: 4, textAlign: "right" }}>الكود</th>
                      <th style={{ padding: 4, textAlign: "center" }}>عدد القيود</th>
                      <th style={{ padding: 4, textAlign: "left" }}>إجمالي مدين</th>
                      <th style={{ padding: 4, textAlign: "left" }}>إجمالي دائن</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.top_20_accounts_by_volume.map((a, i) => (
                      <tr key={a.journal_account_id} style={{ borderTop: "1px solid #f1f5f9", background: i % 2 ? "#fafafa" : "#fff" }}>
                        <td style={{ padding: 4, direction: "ltr", fontWeight: 700, color: "#3730a3" }}>{a.journal_account_id}</td>
                        <td style={{ padding: 4 }}>{a.name || "—"}</td>
                        <td style={{ padding: 4, direction: "ltr", color: "#64748b" }}>{a.code || "—"}</td>
                        <td style={{ padding: 4, textAlign: "center" }}>{Number(a.tx_count).toLocaleString()}</td>
                        <td style={{ padding: 4, direction: "ltr", textAlign: "left", color: Number(a.total_debit) > 0 ? "#15803d" : "#cbd5e1" }}>{Number(a.total_debit).toLocaleString()}</td>
                        <td style={{ padding: 4, direction: "ltr", textAlign: "left", color: Number(a.total_credit) > 0 ? "#b45309" : "#cbd5e1" }}>{Number(a.total_credit).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}

          {/* Classified account leaves */}
          {data.account_leaves_in_tree.length > 0 && (
            <details style={{ marginTop: 8 }}>
              <summary style={{ fontSize: 12, fontWeight: 700, color: "#3730a3", cursor: "pointer", marginBottom: 6 }}>
                🌳 الحسابات المصنّفة في الشجرة ({data.account_leaves_in_tree.length})
              </summary>
              <div style={{ maxHeight: 200, overflow: "auto", border: "1px solid #e2e8f0", borderRadius: 6 }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                  <thead>
                    <tr style={{ background: "#f1f5f9" }}>
                      <th style={{ padding: 4, textAlign: "right" }}>ID الحساب</th>
                      <th style={{ padding: 4, textAlign: "right" }}>الاسم في الشجرة</th>
                      <th style={{ padding: 4, textAlign: "center" }}>قيود مرتبطة</th>
                      <th style={{ padding: 4, textAlign: "left" }}>مدين</th>
                      <th style={{ padding: 4, textAlign: "left" }}>دائن</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.account_leaves_in_tree.map((l, i) => (
                      <tr key={l.node_id} style={{
                        borderTop: "1px solid #f1f5f9",
                        background: l.linked_tx_count === 0 ? "#fee2e2" : (i % 2 ? "#fafafa" : "#fff"),
                      }}>
                        <td style={{ padding: 4, direction: "ltr", fontWeight: 700, color: "#3730a3" }}>{l.journal_account_id}</td>
                        <td style={{ padding: 4 }}>{l.name}</td>
                        <td style={{ padding: 4, textAlign: "center", fontWeight: 700, color: l.linked_tx_count === 0 ? "#dc2626" : "#15803d" }}>
                          {Number(l.linked_tx_count).toLocaleString()}
                        </td>
                        <td style={{ padding: 4, direction: "ltr", textAlign: "left" }}>{Number(l.total_debit).toLocaleString()}</td>
                        <td style={{ padding: 4, direction: "ltr", textAlign: "left" }}>{Number(l.total_credit).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}

          {/* Sample transaction */}
          {data.sample_recent_transactions.length > 0 && (
            <details style={{ marginTop: 8 }}>
              <summary style={{ fontSize: 12, fontWeight: 700, color: "#3730a3", cursor: "pointer", marginBottom: 6 }}>
                🔬 عيّنة من آخر القيود (للتحقق من بنية البيانات)
              </summary>
              <pre style={{
                fontSize: 10, background: "#0f172a", color: "#dcfce7", padding: 10,
                borderRadius: 6, overflow: "auto", maxHeight: 200, direction: "ltr",
              }}>{JSON.stringify(data.sample_recent_transactions, null, 2)}</pre>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

// v3.3.522 — Find & fix orphaned account leaves.
// Symptom (from user screenshot): classified account leaves show zero balance because
// their journal_account_id doesn't match any transaction in DB. This happens when
// the leaf was classified with a stale/wrong ID. The fix: search transactions for
// rows where journal_account_name matches the leaf name exactly, and relink.
function OrphanedAccountsFixWidget({ canSync }) {
  const [loading, setLoading] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [data, setData] = useState(null);

  const scan = async () => {
    setLoading(true);
    setData(null);
    try {
      const r = await accounting.findOrphanedAccountLeaves();
      setData(r.data);
      if (r.data.orphans_count === 0) {
        toast.success("✓ لا توجد حسابات بحاجة لإعادة ربط");
      }
    } catch (e) {
      toast.error(e?.response?.data?.error || "فشل الفحص");
    } finally {
      setLoading(false);
    }
  };

  const applyFix = async () => {
    if (!data || data.auto_fixable === 0) return;
    if (!confirm(`سيتم إعادة ربط ${data.auto_fixable} حساب بأرقامها الصحيحة في Daftra. متأكد؟`)) return;
    setFixing(true);
    try {
      const r = await accounting.fixOrphanedAccountLeaves();
      toast.success(`✓ تم إصلاح ${r.data.fixed} حساب${r.data.skipped?.length ? ` · تخطي ${r.data.skipped.length}` : ""}`);
      // Re-scan after fix to update display
      await scan();
    } catch (e) {
      toast.error(e?.response?.data?.error || "فشل الإصلاح");
    } finally {
      setFixing(false);
    }
  };

  return (
    <div style={{ marginTop: 14, padding: 18, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: "#0e7490" }}>
          🔗 إصلاح ربط الحسابات (Auto-Relink)
        </div>
      </div>
      <div style={{ fontSize: 11, color: "#64748b", marginBottom: 12, lineHeight: 1.7 }}>
        لو أن بعض الحسابات في الشجرة تُظهر <strong>رصيد = 0</strong> رغم وجود حركات لها في Daftra،
        السبب الأرجح أن الـ ID المربوط في الشجرة <strong>قديم/خاطئ</strong> ولا يطابق ID الحساب الحالي في Daftra.
        <br />
        هذه الأداة تبحث في القيود عن الحسابات التي يطابق <strong>اسمها بالضبط</strong> اسم العقدة في الشجرة،
        وتُعيد ربطها تلقائياً إذا وُجد تطابق فريد.
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
        <button
          onClick={scan}
          disabled={loading}
          style={{
            padding: "8px 16px", background: loading ? "#94a3b8" : "#0e7490",
            color: "#fff", border: "none", borderRadius: 6,
            fontSize: 12, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer", fontFamily: "inherit",
          }}
        >{loading ? "...جاري الفحص" : "🔍 فحص الحسابات المعزولة"}</button>

        {data && data.auto_fixable > 0 && canSync && (
          <button
            onClick={applyFix}
            disabled={fixing}
            style={{
              padding: "8px 16px", background: fixing ? "#94a3b8" : "#16a34a",
              color: "#fff", border: "none", borderRadius: 6,
              fontSize: 12, fontWeight: 700, cursor: fixing ? "not-allowed" : "pointer", fontFamily: "inherit",
            }}
          >{fixing ? "...جاري الإصلاح" : `✓ إصلاح ${data.auto_fixable} حساب تلقائياً`}</button>
        )}
      </div>

      {data && (
        <div style={{ marginTop: 12 }}>
          {/* Summary cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8, marginBottom: 12 }}>
            <div style={{ padding: 10, background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 6, textAlign: "center" }}>
              <div style={{ fontSize: 22, fontWeight: 900, color: "#92400e" }}>{data.orphans_count}</div>
              <div style={{ fontSize: 10, color: "#92400e", marginTop: 2 }}>حسابات بدون قيود</div>
            </div>
            <div style={{ padding: 10, background: "#dcfce7", border: "1px solid #86efac", borderRadius: 6, textAlign: "center" }}>
              <div style={{ fontSize: 22, fontWeight: 900, color: "#15803d" }}>{data.auto_fixable}</div>
              <div style={{ fontSize: 10, color: "#15803d", marginTop: 2 }}>قابلة للإصلاح التلقائي</div>
            </div>
            <div style={{ padding: 10, background: "#fed7aa", border: "1px solid #fdba74", borderRadius: 6, textAlign: "center" }}>
              <div style={{ fontSize: 22, fontWeight: 900, color: "#9a3412" }}>{data.manual_required}</div>
              <div style={{ fontSize: 10, color: "#9a3412", marginTop: 2 }}>تحتاج تدخل يدوي</div>
            </div>
            <div style={{ padding: 10, background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 6, textAlign: "center" }}>
              <div style={{ fontSize: 22, fontWeight: 900, color: "#dc2626" }}>{data.no_match}</div>
              <div style={{ fontSize: 10, color: "#dc2626", marginTop: 2 }}>لا يوجد تطابق</div>
            </div>
          </div>

          {data.orphans.length === 0 && (
            <div style={{ padding: 12, background: "#dcfce7", border: "1px solid #86efac", borderRadius: 6, color: "#15803d", fontSize: 12, fontWeight: 700, textAlign: "center" }}>
              ✓ جميع الحسابات مربوطة بشكل صحيح
            </div>
          )}

          {/* Orphans detailed table */}
          {data.orphans.length > 0 && (
            <details open style={{ marginTop: 8 }}>
              <summary style={{ fontSize: 12, fontWeight: 700, color: "#0e7490", cursor: "pointer", marginBottom: 6 }}>
                📋 تفاصيل الحسابات المعزولة ({data.orphans.length})
              </summary>
              <div style={{ maxHeight: 400, overflow: "auto", border: "1px solid #e2e8f0", borderRadius: 6 }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                  <thead style={{ position: "sticky", top: 0, background: "#f1f5f9", zIndex: 1 }}>
                    <tr>
                      <th style={{ padding: 6, textAlign: "right" }}>الاسم في الشجرة</th>
                      <th style={{ padding: 6, textAlign: "center" }}>ID الحالي</th>
                      <th style={{ padding: 6, textAlign: "center" }}>عدد التطابقات</th>
                      <th style={{ padding: 6, textAlign: "right" }}>الاقتراح</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.orphans.map((o, i) => {
                      const isFixable = o.matches.length === 1;
                      const isAmbiguous = o.matches.length > 1;
                      const noMatch = o.matches.length === 0;
                      return (
                        <tr key={o.node_id} style={{
                          borderTop: "1px solid #f1f5f9",
                          background: isFixable ? "#f0fdf4" : isAmbiguous ? "#fff7ed" : "#fef2f2",
                        }}>
                          <td style={{ padding: 6, fontWeight: 700 }}>{o.leaf_name}</td>
                          <td style={{ padding: 6, textAlign: "center", direction: "ltr", color: "#dc2626", fontWeight: 700 }}>
                            {o.current_ja_id ? `#${o.current_ja_id}` : "—"}
                          </td>
                          <td style={{ padding: 6, textAlign: "center" }}>
                            {isFixable && <span style={{ color: "#15803d", fontWeight: 700 }}>1 ✓</span>}
                            {isAmbiguous && <span style={{ color: "#9a3412", fontWeight: 700 }}>{o.matches.length} ⚠</span>}
                            {noMatch && <span style={{ color: "#dc2626", fontWeight: 700 }}>0 ✗</span>}
                          </td>
                          <td style={{ padding: 6 }}>
                            {isFixable && (
                              <span style={{ color: "#15803d" }}>
                                ربط بـ <strong style={{ direction: "ltr" }}>#{o.matches[0].journal_account_id}</strong>
                                {" "}({Number(o.matches[0].tx_count).toLocaleString()} قيد)
                              </span>
                            )}
                            {isAmbiguous && (
                              <span style={{ color: "#9a3412", fontSize: 10 }}>
                                IDs: {o.matches.slice(0, 3).map(m => `#${m.journal_account_id}(${m.tx_count})`).join(" · ")}
                                {o.matches.length > 3 && " ..."}
                              </span>
                            )}
                            {noMatch && (
                              <span style={{ color: "#dc2626", fontSize: 10 }}>
                                لا يوجد قيد باسم مطابق — قد يكون الحساب لم يستقبل أي حركة بعد
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </details>
          )}

          {data.manual_required > 0 && (
            <div style={{ marginTop: 10, padding: 10, background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 6, fontSize: 11, color: "#92400e", lineHeight: 1.7 }}>
              ⚠️ <strong>{data.manual_required} حساب يحتاج تدخل يدوي:</strong> هناك أكثر من حساب في القيود يحمل نفس الاسم.
              من الشجرة، احذف هذه الحسابات وأعد تصنيفها يدوياً مع اختيار الـ ID الصحيح من القائمة المنسدلة.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// v3.3.532 — Compare account row count between local DB and Daftra.
// Use to diagnose whether a specific account is missing rows that exist in Daftra.
function CompareAccountWidget({ canSync }) {
  const [accountId, setAccountId] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const run = async () => {
    if (!accountId) {
      toast.error("أدخل رقم الحساب");
      return;
    }
    setLoading(true);
    setData(null);
    try {
      const r = await accounting.compareAccountRows(Number(accountId));
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.error || "فشلت المقارنة");
    } finally {
      setLoading(false);
    }
  };

  if (!canSync) return null;

  return (
    <div style={{ marginTop: 14, padding: 18, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10 }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: "#9333ea", marginBottom: 10 }}>
        🔬 مقارنة حساب محدد — Daftra مباشرة vs المحلي
      </div>
      <div style={{ fontSize: 11, color: "#64748b", marginBottom: 12, lineHeight: 1.7 }}>
        أدخل ID الحساب في Daftra (مثل <code>371</code> للبنك الأهلي 7710). الأداة ستجلب كل سطور journal_transaction
        لهذا الحساب مباشرة من Daftra وتقارنها مع المحفوظ محلياً، وتظهر الفروقات والـ IDs المفقودة.
      </div>

      <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
        <input
          type="number"
          placeholder="ID الحساب (مثلاً 371)"
          value={accountId}
          onChange={e => setAccountId(e.target.value)}
          style={{ flex: 1, padding: "8px 12px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 12, fontFamily: "inherit", direction: "ltr", textAlign: "right" }}
        />
        <button
          onClick={run}
          disabled={loading || !accountId}
          style={{
            padding: "8px 20px",
            background: loading || !accountId ? "#94a3b8" : "#9333ea",
            color: "#fff", border: "none", borderRadius: 6,
            fontSize: 12, fontWeight: 700,
            cursor: loading || !accountId ? "not-allowed" : "pointer",
            fontFamily: "inherit",
          }}
        >
          {loading ? "...جاري" : "🔬 قارن"}
        </button>
      </div>

      {data && (
        <div style={{ marginTop: 10 }}>
          {/* v3.3.533 — Diagnostic: which filter worked? */}
          <div style={{
            padding: 10, marginBottom: 10,
            background: data.mode === "filtered" ? "#dcfce7" : "#fef3c7",
            border: `1px solid ${data.mode === "filtered" ? "#86efac" : "#fcd34d"}`,
            borderRadius: 6, fontSize: 11, color: "#475569",
          }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>
              {data.mode === "filtered"
                ? `✓ تم الجلب بفلتر: ${data.working_filter}`
                : "⚠️ Daftra رفض كل صيغ الفلترة — تم الجلب الكامل والفلترة محلياً (أبطأ لكن يعمل)"}
            </div>
            {data.filter_attempts && data.filter_attempts.length > 0 && (
              <details>
                <summary style={{ cursor: "pointer", color: "#64748b" }}>عرض محاولات الفلترة</summary>
                <ul style={{ margin: "4px 0", padding: "0 16px", fontSize: 10, color: "#64748b" }}>
                  {data.filter_attempts.map((a, i) => (
                    <li key={i} style={{ direction: "ltr", fontFamily: "monospace" }}>
                      {a.filter} → {a.ok ? `✓ ${a.totalReported}` : `✗ ${a.status} ${a.error || ""}`}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>

          {/* Side-by-side comparison */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
            <div style={{ padding: 12, background: "#dbeafe", border: "1px solid #93c5fd", borderRadius: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#1e40af", marginBottom: 6 }}>📦 محلياً</div>
              <div style={{ fontSize: 11, color: "#1e3a8a", lineHeight: 1.8 }}>
                • عدد السطور: <strong>{data.local.row_count?.toLocaleString()}</strong><br/>
                • إجمالي مدين: <strong>{Number(data.local.total_debit).toLocaleString()}</strong><br/>
                • إجمالي دائن: <strong>{Number(data.local.total_credit).toLocaleString()}</strong><br/>
                • الرصيد: <strong>{(Number(data.local.total_debit) - Number(data.local.total_credit)).toLocaleString()}</strong>
              </div>
            </div>
            <div style={{ padding: 12, background: "#dcfce7", border: "1px solid #86efac", borderRadius: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#15803d", marginBottom: 6 }}>☁️ في Daftra</div>
              <div style={{ fontSize: 11, color: "#14532d", lineHeight: 1.8 }}>
                • عدد السطور: <strong>{data.daftra.row_count?.toLocaleString()}</strong> (API total: {data.daftra.api_reported_total?.toLocaleString()})<br/>
                • إجمالي مدين: <strong>{Number(data.daftra.total_debit).toLocaleString()}</strong><br/>
                • إجمالي دائن: <strong>{Number(data.daftra.total_credit).toLocaleString()}</strong><br/>
                • المسودات: <strong>{data.daftra.drafts}</strong>
              </div>
            </div>
          </div>

          {/* Diff summary */}
          <div style={{
            padding: 12, marginBottom: 12,
            background: Math.abs(data.diff.rows) === 0 ? "#dcfce7" : "#fef3c7",
            border: `1px solid ${Math.abs(data.diff.rows) === 0 ? "#86efac" : "#fcd34d"}`,
            borderRadius: 8,
          }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: Math.abs(data.diff.rows) === 0 ? "#15803d" : "#92400e", marginBottom: 6 }}>
              {Math.abs(data.diff.rows) === 0 ? "✓ السطور متطابقة" : `⚠️ فرق ${data.diff.rows > 0 ? "+" : ""}${data.diff.rows} سطر`}
            </div>
            <div style={{ fontSize: 11, color: "#475569", lineHeight: 1.8 }}>
              • سطور موجودة في Daftra لكن ناقصة محلياً: <strong style={{ color: "#dc2626" }}>{data.missing_in_local_count}</strong><br/>
              • سطور موجودة محلياً لكن ليست في Daftra: <strong style={{ color: "#dc2626" }}>{data.only_in_local_count}</strong><br/>
              • فرق المدين: <strong style={{ color: data.diff.debit !== 0 ? "#dc2626" : "#15803d" }}>{Number(data.diff.debit).toLocaleString()}</strong><br/>
              • فرق الدائن: <strong style={{ color: data.diff.credit !== 0 ? "#dc2626" : "#15803d" }}>{Number(data.diff.credit).toLocaleString()}</strong>
            </div>
          </div>

          {/* Missing samples */}
          {data.missing_in_local_sample.length > 0 && (
            <details open>
              <summary style={{ fontSize: 12, fontWeight: 700, color: "#dc2626", cursor: "pointer", marginBottom: 6 }}>
                📋 عيّنة من السطور المفقودة محلياً ({data.missing_in_local_sample.length} من {data.missing_in_local_count})
              </summary>
              <div style={{ maxHeight: 250, overflow: "auto", border: "1px solid #fca5a5", borderRadius: 6 }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10 }}>
                  <thead style={{ background: "#fee2e2", position: "sticky", top: 0 }}>
                    <tr>
                      <th style={{ padding: 4 }}>ID</th>
                      <th style={{ padding: 4 }}>رقم القيد</th>
                      <th style={{ padding: 4 }}>التاريخ</th>
                      <th style={{ padding: 4 }}>البيان</th>
                      <th style={{ padding: 4 }}>مدين</th>
                      <th style={{ padding: 4 }}>دائن</th>
                      <th style={{ padding: 4 }}>مسودة؟</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.missing_in_local_sample.map((r, i) => (
                      <tr key={r.id} style={{ borderTop: "1px solid #f1f5f9", background: i % 2 ? "#fef2f2" : "#fff" }}>
                        <td style={{ padding: 4, direction: "ltr", fontWeight: 700 }}>{r.id}</td>
                        <td style={{ padding: 4, direction: "ltr" }}>{r.journal_no}</td>
                        <td style={{ padding: 4 }}>{r.date}</td>
                        <td style={{ padding: 4 }}>{String(r.description || "").slice(0, 40)}</td>
                        <td style={{ padding: 4, direction: "ltr", textAlign: "left" }}>{Number(r.debit).toLocaleString()}</td>
                        <td style={{ padding: 4, direction: "ltr", textAlign: "left" }}>{Number(r.credit).toLocaleString()}</td>
                        <td style={{ padding: 4, textAlign: "center", color: r.is_draft ? "#dc2626" : "#15803d", fontWeight: 700 }}>
                          {r.is_draft ? "نعم" : "لا"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

// v3.3.535 — Diagnose drafts state: schema migration + stored count + sample from Daftra.
// Use this to verify whether v3.3.534's draft-saving fix is actually deployed and applied.
function DraftsDiagnosticWidget({ canSync }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const run = async () => {
    setLoading(true);
    setData(null);
    try {
      const r = await accounting.diagnoseDrafts();
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.error || "فشل التشخيص");
    } finally {
      setLoading(false);
    }
  };

  if (!canSync) return null;

  return (
    <div style={{ marginTop: 14, padding: 18, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10 }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: "#ea580c", marginBottom: 10 }}>
        🔬 تشخيص حالة المسودات (v3.3.535)
      </div>
      <div style={{ fontSize: 11, color: "#64748b", marginBottom: 12, lineHeight: 1.7 }}>
        تحقّق من أن تحديث v3.3.534 طُبّق بنجاح: هل عمود <code>is_draft</code> موجود في الـ DB؟
        كم مسودة محفوظة محلياً؟ هل Daftra يُرجع المسودات بشكل صحيح؟
      </div>

      <button
        onClick={run}
        disabled={loading}
        style={{
          padding: "8px 20px",
          background: loading ? "#94a3b8" : "#ea580c",
          color: "#fff", border: "none", borderRadius: 6,
          fontSize: 12, fontWeight: 700,
          cursor: loading ? "not-allowed" : "pointer",
          fontFamily: "inherit",
        }}
      >
        {loading ? "...جاري" : "🔬 شغّل التشخيص"}
      </button>

      {data && (
        <div style={{ marginTop: 14 }}>
          {/* Hint banner */}
          <div style={{
            padding: 12, marginBottom: 12,
            background: data.schema_has_is_draft
              ? (data.stats?.drafts_count > 0 ? "#dcfce7" : "#fef3c7")
              : "#fee2e2",
            border: `2px solid ${data.schema_has_is_draft
              ? (data.stats?.drafts_count > 0 ? "#86efac" : "#fcd34d")
              : "#fca5a5"}`,
            borderRadius: 8,
            fontSize: 13, fontWeight: 700,
            color: data.schema_has_is_draft
              ? (data.stats?.drafts_count > 0 ? "#15803d" : "#92400e")
              : "#7f1d1d",
          }}>
            {data.hint}
          </div>

          {/* Schema status */}
          <div style={{ padding: 12, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, marginBottom: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#0f172a", marginBottom: 6 }}>📋 حالة الـ Schema:</div>
            <div style={{ fontSize: 11, color: "#475569" }}>
              عمود <code>is_draft</code> موجود في الجدول: <strong style={{ color: data.schema_has_is_draft ? "#15803d" : "#dc2626" }}>
                {data.schema_has_is_draft ? "✓ نعم" : "✗ لا"}
              </strong>
            </div>
          </div>

          {/* Stats */}
          {data.stats && (
            <div style={{ padding: 12, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, marginBottom: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#0f172a", marginBottom: 6 }}>📊 إحصائيات محلية:</div>
              <div style={{ fontSize: 11, color: "#475569", lineHeight: 1.8 }}>
                • إجمالي السطور: <strong>{data.stats.total_rows?.toLocaleString()}</strong><br/>
                • سطور <strong style={{ color: "#ea580c" }}>مسودة (is_draft=TRUE)</strong>: <strong>{data.stats.drafts_count?.toLocaleString()}</strong><br/>
                • سطور <strong style={{ color: "#15803d" }}>معتمدة</strong>: <strong>{data.stats.posted_count?.toLocaleString()}</strong><br/>
                • سطور <strong>NULL</strong> (بدون قيمة): <strong>{data.stats.null_count?.toLocaleString()}</strong>
              </div>
            </div>
          )}

          {/* Daftra sample */}
          {data.daftra_test && data.daftra_test.samples && (
            <div style={{ padding: 12, background: "#dbeafe", border: "1px solid #93c5fd", borderRadius: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#1e40af", marginBottom: 6 }}>
                ☁️ عيّنة من Daftra ({data.daftra_test.total_api?.toLocaleString()} إجمالي):
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10, marginTop: 6 }}>
                <thead style={{ background: "#bfdbfe" }}>
                  <tr>
                    <th style={{ padding: 4 }}>ID</th>
                    <th style={{ padding: 4 }}>رقم القيد</th>
                    <th style={{ padding: 4 }}>draft (raw)</th>
                    <th style={{ padding: 4 }}>type</th>
                    <th style={{ padding: 4 }}>parsed</th>
                  </tr>
                </thead>
                <tbody>
                  {data.daftra_test.samples.map((s, i) => (
                    <tr key={s.id} style={{ borderTop: "1px solid #f1f5f9", background: i % 2 ? "#eff6ff" : "#fff" }}>
                      <td style={{ padding: 4, direction: "ltr" }}>{s.id}</td>
                      <td style={{ padding: 4, direction: "ltr" }}>{s.journal_number}</td>
                      <td style={{ padding: 4, direction: "ltr", fontFamily: "monospace" }}>{String(s.draft_field)}</td>
                      <td style={{ padding: 4, direction: "ltr" }}>{s.draft_type}</td>
                      <td style={{ padding: 4, textAlign: "center", color: s.is_draft_parsed ? "#dc2626" : "#15803d", fontWeight: 700 }}>
                        {s.is_draft_parsed ? "مسودة" : "معتمد"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// v3.3.539 — Quick fix for "all cost centers show zero" problem.
// Pulls cost_center_transaction from Daftra and updates accounting_transactions to
// link the correct cost_center_id, WITHOUT redoing the full 5-10 min sync.
// Useful after the first full sync (journal_transaction) didn't have CC linkage.
function RelinkCostCentersWidget({ canSync }) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!confirm("سيتم سحب cost_center_transaction من Daftra وربط معاملاتك المحلية بمراكز التكلفة الصحيحة (1-2 دقيقة). هل تريد المتابعة؟")) return;
    setRunning(true);
    setResult(null);
    try {
      const r = await accounting.relinkCostCenters();
      setResult(r.data);
      toast.success(`✓ تم ربط ${fmt(r.data.rows_updated)} معاملة بمراكز التكلفة`);
    } catch (e) {
      toast.error(e?.response?.data?.error || "فشل الربط");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div style={{
      marginTop: 12, padding: 14,
      background: "#fefce8", border: "2px solid #facc15", borderRadius: 10,
    }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: "#854d0e", marginBottom: 6 }}>
        🔗 ربط مراكز التكلفة بالمعاملات (v3.3.539)
      </div>
      <div style={{ fontSize: 11.5, color: "#713f12", lineHeight: 1.7, marginBottom: 10 }}>
        <strong>متى تستخدم هذه الأداة؟</strong>
        <br />
        إذا كانت <strong>مراكز التكلفة</strong> تظهر بأصفار في التقرير المحاسبي بعد المزامنة الكاملة.
        <br />
        <strong>السبب:</strong> Daftra لا تُرجع <code>cost_center_id</code> في <code>journal_transaction</code> entity،
        فيجب سحب <code>cost_center_transaction</code> منفصلاً وربطه عبر <code>journal_id</code>.
        <br />
        <strong>الميزة:</strong> سريع (1-2 دقيقة) ولا يلغي بياناتك الحالية.
      </div>

      <button
        onClick={run}
        disabled={!canSync || running}
        style={{
          padding: "8px 18px",
          background: !canSync || running ? "#fde68a" : "#ca8a04",
          color: "#fff", border: "none", borderRadius: 6,
          fontSize: 12.5, fontWeight: 700,
          cursor: !canSync || running ? "not-allowed" : "pointer", fontFamily: "inherit",
        }}
      >
        {running ? "...جاري الربط" : "🔗 ابدأ الربط الآن"}
      </button>

      {result && (
        <div style={{
          marginTop: 12, padding: 12,
          background: "#fff", border: "1px solid #fcd34d", borderRadius: 8,
          fontSize: 12, lineHeight: 1.8, color: "#451a03",
        }}>
          <div style={{ fontWeight: 800, color: "#15803d", marginBottom: 6 }}>
            ✅ اكتمل الربط في {result.elapsed_seconds}s
          </div>
          <ul style={{ margin: 0, paddingInlineStart: 18 }}>
            <li>صفوف <code>cost_center_transaction</code> تم فحصها: <strong>{fmt(result.cc_tx_rows_scanned)}</strong></li>
            <li>عدد القيود الفريدة (journal_id) المرتبطة بمراكز تكلفة: <strong>{fmt(result.unique_journals)}</strong></li>
            <li>سطور تم تحديثها في DB: <strong style={{ color: "#15803d" }}>{fmt(result.rows_updated)}</strong></li>
            {result.names_refreshed > 0 && (
              <li>أسماء/أكواد تم تحديثها: <strong>{fmt(result.names_refreshed)}</strong></li>
            )}
            {result.rows_failed > 0 && (
              <li style={{ color: "#dc2626" }}>دفعات فشلت: {result.rows_failed}</li>
            )}
          </ul>
          <div style={{ marginTop: 8, fontSize: 11, color: "#64748b" }}>
            💡 الآن افتح تبويب <strong>مراكز التكلفة</strong> أو <strong>التقرير المحاسبي</strong> وتحقّق من الأرقام.
          </div>
        </div>
      )}
    </div>
  );
}

// v3.3.524 — Probe Daftra to find an entity that returns ALL journal transactions
// (not just cost_center_transaction). The current sync only fetches transactions
// linked to cost centers — so accounts used in entries WITHOUT cost centers (e.g.
// bank-to-bank transfers) appear in the chart of accounts but show zero balance
// in the local DB. This probe helps identify the right entity name to use.
function DaftraEntityProbeWidget({ canSync }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  // v3.3.525 — sample of journal_transaction schema
  const [sampleLoading, setSampleLoading] = useState(false);
  const [sample, setSample] = useState(null);

  const run = async () => {
    setLoading(true);
    setData(null);
    try {
      const r = await accounting.probeDaftraEntities();
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.error || "فشل الاستكشاف");
    } finally {
      setLoading(false);
    }
  };

  const fetchSample = async () => {
    setSampleLoading(true);
    setSample(null);
    try {
      const r = await accounting.probeJournalTransactionSample();
      setSample(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.error || "فشل جلب العيّنة");
    } finally {
      setSampleLoading(false);
    }
  };

  if (!canSync) return null;

  return (
    <div style={{ marginTop: 14, padding: 18, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: "#7c3aed" }}>
          🛰️ استكشاف Daftra API — إيجاد endpoint القيود الكامل
        </div>
      </div>
      <div style={{ fontSize: 11, color: "#64748b", marginBottom: 12, lineHeight: 1.7 }}>
        النظام حالياً يجلب فقط القيود المرتبطة بمراكز تكلفة (<code>cost_center_transaction</code>).
        القيود التي لا تحوي مركز تكلفة (مثل التحويلات بين البنوك، أو القيود التعديلية) لا تُجلب،
        لذلك تظهر الحسابات بدون أرصدة رغم وجود حركات لها في Daftra.
        <br />
        هذه الأداة تجرّب أسماء entities مختلفة لإيجاد endpoint يُرجع كل القيود (وليس فقط المتعلقة بمراكز التكلفة).
      </div>

      <button
        onClick={run}
        disabled={loading}
        style={{
          padding: "8px 16px", background: loading ? "#94a3b8" : "#7c3aed",
          color: "#fff", border: "none", borderRadius: 6,
          fontSize: 12, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer", fontFamily: "inherit",
          marginLeft: 8,
        }}
      >{loading ? "...جاري الاستكشاف (30 ثانية)" : "🛰️ شغّل الاستكشاف"}</button>

      {/* v3.3.525 — Quick "show sample" button for journal_transaction */}
      <button
        onClick={fetchSample}
        disabled={sampleLoading}
        style={{
          padding: "8px 16px", background: sampleLoading ? "#94a3b8" : "#0e7490",
          color: "#fff", border: "none", borderRadius: 6,
          fontSize: 12, fontWeight: 700, cursor: sampleLoading ? "not-allowed" : "pointer", fontFamily: "inherit",
        }}
      >{sampleLoading ? "...جاري" : "🔬 عرض عيّنة journal_transaction"}</button>

      {sample && (
        <div style={{ marginTop: 12, padding: 12, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8 }}>
          <div style={{ fontSize: 12, color: "#475569", marginBottom: 8 }}>
            <strong>Total:</strong> {sample.total?.toLocaleString()} · <strong>Last Page:</strong> {sample.last_page}
          </div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#0e7490", marginBottom: 4 }}>
            🔑 Top-level keys ({sample.first_row_keys?.length || 0}):
          </div>
          <div style={{ direction: "ltr", fontFamily: "monospace", fontSize: 10, color: "#475569", marginBottom: 8 }}>
            {(sample.first_row_keys || []).join(", ")}
          </div>
          <details>
            <summary style={{ fontSize: 12, fontWeight: 700, color: "#0e7490", cursor: "pointer", marginBottom: 4 }}>
              📄 عرض بيانات العيّنة كاملة (JSON)
            </summary>
            <pre style={{
              fontSize: 9, background: "#0f172a", color: "#dcfce7", padding: 10,
              borderRadius: 6, overflow: "auto", maxHeight: 400, direction: "ltr",
            }}>{JSON.stringify(sample.sample_rows, null, 2)}</pre>
          </details>
        </div>
      )}

      {data && (
        <div style={{ marginTop: 14 }}>
          <div style={{
            padding: 10, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 6, marginBottom: 12,
            fontSize: 12, color: "#475569",
          }}>
            <strong>Base URL:</strong> <span style={{ direction: "ltr", fontFamily: "monospace" }}>{data.base_url}</span>
            <br />
            <strong>List ID:</strong> {data.list_id}
            <br />
            <strong>النتيجة:</strong> {data.summary.working} entity تعمل · {data.summary.failed} فشلت
          </div>

          <div style={{ border: "1px solid #e2e8f0", borderRadius: 6, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
              <thead style={{ background: "#f1f5f9" }}>
                <tr>
                  <th style={{ padding: 6, textAlign: "right" }}>Entity</th>
                  <th style={{ padding: 6, textAlign: "center" }}>Status</th>
                  <th style={{ padding: 6, textAlign: "center" }}>Total</th>
                  <th style={{ padding: 6, textAlign: "center" }}>Last Page</th>
                  <th style={{ padding: 6, textAlign: "right" }}>Sample Keys / Error</th>
                </tr>
              </thead>
              <tbody>
                {data.results.map((r, i) => (
                  <tr key={r.entity} style={{
                    borderTop: "1px solid #f1f5f9",
                    background: r.ok ? "#f0fdf4" : "#fef2f2",
                  }}>
                    <td style={{ padding: 6, direction: "ltr", fontWeight: 700, fontFamily: "monospace", color: r.ok ? "#15803d" : "#dc2626" }}>
                      {r.entity}
                    </td>
                    <td style={{ padding: 6, textAlign: "center", fontWeight: 700, color: r.ok ? "#15803d" : "#dc2626" }}>
                      {r.status || (r.error ? "ERR" : "—")}
                    </td>
                    <td style={{ padding: 6, textAlign: "center", direction: "ltr", fontWeight: 700 }}>
                      {r.total ?? "—"}
                    </td>
                    <td style={{ padding: 6, textAlign: "center", direction: "ltr" }}>
                      {r.last_page ?? "—"}
                    </td>
                    <td style={{ padding: 6, direction: "ltr", fontSize: 10, color: "#64748b", fontFamily: "monospace" }}>
                      {r.error ? r.error : (r.sample_keys || []).join(", ")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {data.summary.working > 0 && (
            <div style={{ marginTop: 10, padding: 10, background: "#dcfce7", border: "1px solid #86efac", borderRadius: 6, fontSize: 11, color: "#15803d", lineHeight: 1.7 }}>
              ✓ تم إيجاد {data.summary.working} entity تعمل. أرسل screenshot من هذه النتيجة وسنحدد أيها يُرجع كل القيود (وليس فقط المرتبطة بمراكز تكلفة) ونبني عليه آلية مزامنة جديدة.
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// Create groups, nest groups, attach cost centers as leaves
// ═══════════════════════════════════════════════════════════════════

// Build a nested tree structure from a flat list of {id, parent_id, ...}
function buildTree(flat) {
  const byId = {};
  flat.forEach(n => { byId[n.id] = { ...n, children: [] }; });
  const roots = [];
  flat.forEach(n => {
    if (n.parent_id == null) {
      roots.push(byId[n.id]);
    } else if (byId[n.parent_id]) {
      byId[n.parent_id].children.push(byId[n.id]);
    } else {
      roots.push(byId[n.id]); // orphan — treat as root
    }
  });
  // Sort children by sort_order then name
  function sortRec(node) {
    node.children.sort((a, b) => (a.sort_order - b.sort_order) || String(a.name).localeCompare(String(b.name), "ar"));
    node.children.forEach(sortRec);
  }
  roots.sort((a, b) => (a.sort_order - b.sort_order) || String(a.name).localeCompare(String(b.name), "ar"));
  roots.forEach(sortRec);
  return roots;
}

function CCTreeTab({ canEdit }) {
  const [nodes, setNodes] = useState([]);
  const [unassigned, setUnassigned] = useState([]);                  // cost centers
  const [hidden, setHidden] = useState([]);                          // hidden cost centers
  const [accountsUnassigned, setAccountsUnassigned] = useState([]);  // v3.3.515 — accounts not in tree
  const [accountsHidden, setAccountsHidden] = useState([]);          // v3.3.515 — hidden accounts
  // v3.3.515 — Top-level tab: which entity type are we managing?
  const [entityKind, setEntityKind] = useState("cost_center"); // 'cost_center' | 'account'
  const [bottomSubTab, setBottomSubTab] = useState("unassigned"); // 'unassigned' | 'hidden' | 'classified'
  const [searchUnassigned, setSearchUnassigned] = useState("");
  const [searchHidden, setSearchHidden] = useState("");
  const [searchClassified, setSearchClassified] = useState("");
  const [selectedUnassigned, setSelectedUnassigned] = useState(new Set());
  const [selectedHidden, setSelectedHidden] = useState(new Set());
  const [selectedClassified, setSelectedClassified] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [expandedIds, setExpandedIds] = useState({});
  const [addingChildOf, setAddingChildOf] = useState(null);
  const [editingNode, setEditingNode] = useState(null);
  // v3.3.556 — Editing ref_no for a tree node
  const [editingRefNoNode, setEditingRefNoNode] = useState(null);
  // v3.3.515 — addForm now tracks leaf_kind too
  const [addForm, setAddForm] = useState({ node_type: "group", leaf_kind: "cost_center", name: "", cost_center_id: "", journal_account_id: "" });
  const [draggingCC, setDraggingCC] = useState(null);
  const [dragOverNodeId, setDragOverNodeId] = useState(null);
  const [classifyingCC, setClassifyingCC] = useState(null);
  const [classifyParentId, setClassifyParentId] = useState("");
  // v3.3.517 — Bulk classify modal state
  const [bulkClassifyOpen, setBulkClassifyOpen] = useState(false);
  const [bulkClassifyParentId, setBulkClassifyParentId] = useState("");

  // v3.3.515 — Clear selections when switching entity type
  useEffect(() => {
    setSelectedUnassigned(new Set());
    setSelectedHidden(new Set());
    setSelectedClassified(new Set());
  }, [entityKind]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tR, uR, hR, auR, ahR] = await Promise.allSettled([
        accounting.ccTreeList(),
        accounting.ccTreeUnassigned(),
        accounting.ccTreeHidden(),
        accounting.ccTreeAccountsUnassigned(),    // v3.3.515
        accounting.ccTreeAccountsHidden(),        // v3.3.515
      ]);
      if (tR.status === "fulfilled") setNodes(tR.value.data?.items || []);
      else { console.error("[CCTree] tree load failed:", tR.reason); toast.error("فشل تحميل الشجرة"); }
      if (uR.status === "fulfilled") setUnassigned(uR.value.data?.items || []);
      if (hR.status === "fulfilled") setHidden(hR.value.data?.items || []);
      if (auR.status === "fulfilled") setAccountsUnassigned(auR.value.data?.items || []);
      if (ahR.status === "fulfilled") setAccountsHidden(ahR.value.data?.items || []);
    } catch (e) {
      toast.error("فشل تحميل الشجرة");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const tree = useMemo(() => buildTree(nodes), [nodes]);

  // v3.3.516 — Tree always shows everything. Leaf colors/icons differ by leaf_kind
  // so user can tell cost-center leaves (green) from account leaves (indigo) at a glance.
  const displayTree = tree;

  // v3.3.515 — Build classified list filtered by entityKind
  const classified = useMemo(() => {
    const byId = {};
    for (const n of nodes) byId[n.id] = n;
    function pathFor(n) {
      const parts = [];
      let cursor = n.parent_id != null ? byId[n.parent_id] : null;
      while (cursor) {
        parts.unshift(cursor.name);
        cursor = cursor.parent_id != null ? byId[cursor.parent_id] : null;
      }
      return parts.join(" / ");
    }
    return nodes
      .filter(n => {
        if (n.node_type !== "leaf") return false;
        if (entityKind === "cost_center") return n.cost_center_id != null;
        return n.journal_account_id != null;
      })
      .map(n => {
        if (entityKind === "cost_center") {
          return {
            id: n.id,
            cost_center_id: n.cost_center_id,
            cost_center_name: n.name,
            cost_center_code: n.cc_code,
            path: pathFor(n),
            leaf_kind: "cost_center",
          };
        } else {
          // Account leaf — alias fields to cost_center_* shape so the rest of the UI works unchanged
          return {
            id: n.id,
            cost_center_id: n.journal_account_id,  // alias for selection
            cost_center_name: n.name,
            cost_center_code: n.ja_code,
            path: pathFor(n),
            leaf_kind: "account",
          };
        }
      })
      .sort((a, b) => String(a.cost_center_name || "").localeCompare(String(b.cost_center_name || ""), "ar"));
  }, [nodes, entityKind]);

  // v3.3.515 — Current-entity aliases for unassigned/hidden so the rest of the UI works unchanged
  const currentUnassigned = useMemo(() => {
    if (entityKind === "cost_center") return unassigned;
    // Normalize accounts to the same field shape as cost centers
    return (accountsUnassigned || []).map(a => ({
      cost_center_id: a.journal_account_id,
      cost_center_name: a.journal_account_name,
      cost_center_code: a.journal_account_code,
      is_active: a.is_active,
      transactions_count: a.transactions_count,
      _account_type: a.journal_account_type,
    }));
  }, [entityKind, unassigned, accountsUnassigned]);

  const currentHidden = useMemo(() => {
    if (entityKind === "cost_center") return hidden;
    return (accountsHidden || []).map(a => ({
      cost_center_id: a.journal_account_id,
      cost_center_name: a.journal_account_name,
      cost_center_code: a.journal_account_code,
      hidden_at: a.hidden_at,
      transactions_count: a.transactions_count,
    }));
  }, [entityKind, hidden, accountsHidden]);

  // v3.3.506 — Search-filter helpers
  function ccMatches(cc, query) {
    const q = String(query || "").trim().toLowerCase();
    if (!q) return true;
    return String(cc.cost_center_name || "").toLowerCase().includes(q)
      || String(cc.cost_center_code || "").toLowerCase().includes(q)
      || String(cc.cost_center_id || "").includes(q)
      || String(cc.path || "").toLowerCase().includes(q);
  }
  const filteredUnassigned = useMemo(() => currentUnassigned.filter(c => ccMatches(c, searchUnassigned)), [currentUnassigned, searchUnassigned]);
  const filteredHidden = useMemo(() => currentHidden.filter(c => ccMatches(c, searchHidden)), [currentHidden, searchHidden]);
  const filteredClassified = useMemo(() => classified.filter(c => ccMatches(c, searchClassified)), [classified, searchClassified]);

  const toggleExpand = (id) => setExpandedIds(prev => ({ ...prev, [id]: !prev[id] }));
  const expandAll = () => {
    const all = {};
    nodes.forEach(n => { if (n.node_type === "group") all[n.id] = true; });
    setExpandedIds(all);
  };
  const collapseAll = () => setExpandedIds({});

  const handleAdd = async () => {
    const isLeaf = addForm.node_type === "leaf";
    if (!isLeaf && !addForm.name.trim()) { toast.error("اسم الفرع مطلوب"); return; }
    if (isLeaf) {
      if (addForm.leaf_kind === "account") {
        if (!addForm.journal_account_id) { toast.error("اختر الحساب"); return; }
      } else {
        if (!addForm.cost_center_id) { toast.error("اختر مركز التكلفة"); return; }
      }
    }
    try {
      let name = addForm.name.trim();
      if (isLeaf && !name) {
        if (addForm.leaf_kind === "account") {
          const a = accountsUnassigned.find(x => String(x.journal_account_id) === String(addForm.journal_account_id));
          name = a?.journal_account_name || `ACC #${addForm.journal_account_id}`;
        } else {
          const cc = unassigned.find(c => String(c.cost_center_id) === String(addForm.cost_center_id));
          name = cc?.cost_center_name || `CC #${addForm.cost_center_id}`;
        }
      }
      const body = {
        parent_id: addingChildOf === "root" ? null : addingChildOf,
        node_type: addForm.node_type,
        name,
      };
      if (isLeaf) {
        body.leaf_kind = addForm.leaf_kind || "cost_center";
        if (body.leaf_kind === "account") body.journal_account_id = parseInt(addForm.journal_account_id);
        else body.cost_center_id = parseInt(addForm.cost_center_id);
      }
      await accounting.ccTreeCreate(body);
      toast.success("تمت الإضافة");
      setAddingChildOf(null);
      setAddForm({ node_type: "group", leaf_kind: entityKind, name: "", cost_center_id: "", journal_account_id: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || "فشل الإضافة");
    }
  };

  const handleUpdate = async (id, data) => {
    try {
      await accounting.ccTreeUpdate(id, data);
      toast.success("تم التحديث");
      setEditingNode(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || "فشل التحديث");
    }
  };

  const handleDelete = async (node) => {
    const hasChildren = nodes.some(n => n.parent_id === node.id);
    const msg = hasChildren
      ? `حذف "${node.name}" وكل ما تحته من فروع وأوراق؟`
      : `حذف "${node.name}"؟`;
    if (!confirm(msg)) return;
    try {
      await accounting.ccTreeDelete(node.id);
      toast.success("تم الحذف");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || "فشل الحذف");
    }
  };

  // v3.3.499 — Export tree + unassigned + hidden to Excel
  const handleExport = async () => {
    try {
      await accounting.ccTreeExport();
      toast.success("تم تنزيل الملف");
    } catch (e) {
      // v3.3.500 — Surface backend error message (blob responses need special handling)
      let msg = "فشل التصدير";
      try {
        // If response is a blob containing JSON error
        if (e?.response?.data instanceof Blob) {
          const text = await e.response.data.text();
          const parsed = JSON.parse(text);
          if (parsed?.error) msg = parsed.error;
        } else if (e?.response?.data?.error) {
          msg = e.response.data.error;
        } else if (e?.message) {
          msg = e.message;
        }
      } catch {}
      toast.error(msg);
      console.error("[ccTreeExport]", e);
    }
  };

  // v3.3.499 — Import tree from Excel (replaces entire tree atomically)
  const fileInputRef = React.useRef(null);
  const handleImportClick = () => fileInputRef.current?.click();
  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so same file can be re-uploaded
    if (!file) return;
    if (!confirm(`⚠️ الاستيراد سيستبدل الشجرة الحالية بالكامل بمحتوى "${file.name}".\n\nتأكد أنك حمّلت نسخة احتياطية أولاً. هل تريد المتابعة؟`)) return;
    try {
      const r = await accounting.ccTreeImport(file);
      const d = r.data || {};
      toast.success(`✓ تم الاستيراد — ${d.groups || 0} فرع · ${d.leaves || 0} ورقة · ${d.hidden || 0} مخفي`, { duration: 5000 });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.error || "فشل الاستيراد");
    }
  };

  // v3.3.494 — Classify (or re-classify) a cost center under a chosen parent group
  // parentId: null = root, otherwise a node id
  // v3.3.515 — Classify (entity-kind aware)
  const classifyCostCenter = async (cc, parentId) => {
    if (!cc) return;
    try {
      const body = {
        parent_id: parentId,
        node_type: "leaf",
        leaf_kind: entityKind,
        name: cc.cost_center_name || (entityKind === "cost_center" ? `CC #${cc.cost_center_id}` : `ACC #${cc.cost_center_id}`),
      };
      if (entityKind === "cost_center") body.cost_center_id = parseInt(cc.cost_center_id);
      else body.journal_account_id = parseInt(cc.cost_center_id);
      await accounting.ccTreeCreate(body);
      toast.success("تم التصنيف");
      if (parentId) setExpandedIds(prev => ({ ...prev, [parentId]: true }));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || "فشل التصنيف");
    }
  };

  // v3.3.515 — Hide / unhide (entity-kind aware)
  const handleHideCC = async (cc) => {
    const label = entityKind === "cost_center" ? "مركز التكلفة" : "الحساب";
    if (!confirm(`إخفاء "${cc.cost_center_name}" من قائمة "غير المصنّفة"؟ سيُستبعد من كل التقارير.`)) return;
    try {
      if (entityKind === "cost_center") await accounting.ccTreeHide(cc.cost_center_id);
      else await accounting.ccTreeAccountHide(cc.cost_center_id);
      toast.success("تم الإخفاء");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || "فشل الإخفاء");
    }
  };
  const handleUnhideCC = async (cc) => {
    try {
      if (entityKind === "cost_center") await accounting.ccTreeUnhide(cc.cost_center_id);
      else await accounting.ccTreeAccountUnhide(cc.cost_center_id);
      toast.success("تمت إعادة الإظهار");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || "فشل التراجع");
    }
  };

  // v3.3.507 — Multi-select helpers
  const toggleSel = (setFn, id) => {
    setFn(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAllVisible = (setFn, items, idKey) => {
    setFn(new Set(items.map(i => i[idKey])));
  };
  const clearSel = (setFn) => setFn(new Set());

  // v3.3.515 — Bulk hide (entity-kind aware)
  const handleBulkHide = async () => {
    const ids = Array.from(selectedUnassigned);
    if (ids.length === 0) return;
    const label = entityKind === "cost_center" ? "مركز تكلفة" : "حساب";
    if (!confirm(`إخفاء ${ids.length} ${label} من القائمة؟ سيُستبعد من كل التقارير.`)) return;
    try {
      const r = entityKind === "cost_center"
        ? await accounting.ccTreeHideBulk(ids)
        : await accounting.ccTreeAccountHideBulk(ids);
      const h = r.data?.hidden || 0;
      const skipped = r.data?.skipped || 0;
      toast.success(`✓ تم إخفاء ${h} ${label}${skipped > 0 ? ` · تخطي ${skipped} مصنّف` : ""}`);
      setSelectedUnassigned(new Set());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || "فشل الإخفاء الجماعي");
    }
  };

  // v3.3.515 — Bulk unhide (entity-kind aware)
  const handleBulkUnhide = async () => {
    const ids = Array.from(selectedHidden);
    if (ids.length === 0) return;
    const label = entityKind === "cost_center" ? "مركز تكلفة" : "حساب";
    if (!confirm(`إعادة إظهار ${ids.length} ${label}؟ سيعود لقائمة "غير المصنّفة".`)) return;
    try {
      const r = entityKind === "cost_center"
        ? await accounting.ccTreeUnhideBulk(ids)
        : await accounting.ccTreeAccountUnhideBulk(ids);
      toast.success(`✓ تمت إعادة ${r.data?.restored || 0} ${label}`);
      setSelectedHidden(new Set());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || "فشل الإعادة الجماعية");
    }
  };

  // v3.3.517 — Bulk classify (attach selected unassigned items as leaves under one parent)
  const handleBulkClassify = async (parentId) => {
    const ids = Array.from(selectedUnassigned);
    if (ids.length === 0) { toast.error("لم يتم تحديد أي عنصر"); return; }
    try {
      const r = await accounting.ccTreeClassifyBulk({
        parent_id: parentId === "" || parentId == null ? null : parseInt(parentId),
        leaf_kind: entityKind,
        ids,
      });
      const c = r.data?.classified || 0;
      const skipped = r.data?.skipped || [];
      const errors = r.data?.errors || [];
      const label = entityKind === "cost_center" ? "مركز" : "حساب";
      let msg = `✓ تم تصنيف ${c} ${label}`;
      if (skipped.length > 0) msg += ` · تخطي ${skipped.length} (مصنّفة مسبقاً)`;
      if (errors.length > 0) msg += ` · ${errors.length} خطأ`;
      toast.success(msg);
      // Auto-expand the chosen parent so user sees the result
      if (parentId) setExpandedIds(prev => ({ ...prev, [parentId]: true }));
      setSelectedUnassigned(new Set());
      setBulkClassifyOpen(false);
      setBulkClassifyParentId("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || "فشل التصنيف الجماعي");
    }
  };

  // v3.3.507 — Bulk unclassify (remove leaves from tree → return to unassigned)
  const handleBulkUnclassify = async () => {
    const ids = Array.from(selectedClassified);
    if (ids.length === 0) return;
    if (!confirm(`إزالة ${ids.length} مركز تكلفة من الشجرة؟ ستعود لقائمة "غير المصنّفة".`)) return;
    try {
      const r = await accounting.ccTreeDeleteBulk(ids);
      toast.success(`✓ تمت إزالة ${r.data?.deleted || 0} من الشجرة`);
      setSelectedClassified(new Set());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || "فشل الإزالة الجماعية");
    }
  };

  // v3.3.504 — Reorder a node up or down within its parent
  const handleReorder = async (node, direction) => {
    try {
      const r = await accounting.ccTreeReorder(node.id, direction);
      if (r.data?.moved === false) {
        // already at the edge — silent
        return;
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || "فشل تغيير الترتيب");
    }
  };

  // v3.3.504 — Move a node to a different parent (state for modal)
  const [movingNode, setMovingNode] = useState(null);  // node being moved (via modal)
  const [moveTargetParentId, setMoveTargetParentId] = useState("");

  const handleMoveSubmit = async () => {
    if (!movingNode) return;
    const newParentId = moveTargetParentId === "" || moveTargetParentId === "root"
      ? null
      : parseInt(moveTargetParentId);
    try {
      const r = await accounting.ccTreeMove(movingNode.id, newParentId);
      if (r.data?.moved === false) {
        toast(r.data.message || "لا تغيير", { icon: "ℹ️" });
      } else {
        toast.success("تم النقل");
      }
      // Auto-expand the new parent so the user sees the moved node
      if (newParentId) setExpandedIds(prev => ({ ...prev, [newParentId]: true }));
      setMovingNode(null);
      setMoveTargetParentId("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || "فشل النقل");
    }
  };

  // v3.3.504 — Drag a TREE node (group or leaf) onto another group to move it
  const [draggingTreeNodeId, setDraggingTreeNodeId] = useState(null);

  const handleTreeNodeDragStart = (node) => {
    setDraggingTreeNodeId(node.id);
  };
  const handleTreeNodeDragEnd = () => {
    setDraggingTreeNodeId(null);
    setDragOverNodeId(null);
  };
  // When a TREE node is dropped on another group, move it
  const handleDropTreeNodeOn = async (targetNodeId /* or 'root' */) => {
    if (!draggingTreeNodeId) return;
    const newParentId = targetNodeId === "root" ? null : targetNodeId;
    // Don't move onto self
    if (newParentId === draggingTreeNodeId) {
      setDraggingTreeNodeId(null);
      setDragOverNodeId(null);
      return;
    }
    try {
      const r = await accounting.ccTreeMove(draggingTreeNodeId, newParentId);
      if (r.data?.moved !== false) {
        toast.success("تم النقل");
        if (newParentId) setExpandedIds(prev => ({ ...prev, [newParentId]: true }));
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || "فشل النقل");
    } finally {
      setDraggingTreeNodeId(null);
      setDragOverNodeId(null);
    }
  };

  // v3.3.494 — Drag handlers (for unassigned cards being dropped on tree)
  const handleDragStart = (cc) => {
    setDraggingCC(cc);
  };
  const handleDragEnd = () => {
    setDraggingCC(null);
    setDragOverNodeId(null);
  };

  // v3.3.499 — Auto-scroll the window when dragging near top/bottom edge.
  // HTML5 native drag doesn't auto-scroll — we drive it manually from the global dragover position.
  // v3.3.504 — Also triggers for tree-node drags (moves), not just unassigned cards.
  useEffect(() => {
    const isDragging = !!draggingCC || !!draggingTreeNodeId;
    if (!isDragging) return;
    const SCROLL_ZONE = 100;   // px from edge that triggers scroll
    const MAX_SPEED = 22;      // px per frame at the very edge
    let lastY = 0;
    let rafId = null;

    const onDragOver = (e) => {
      lastY = e.clientY;
      if (rafId == null) rafId = requestAnimationFrame(tick);
    };

    const tick = () => {
      rafId = null;
      const stillDragging = !!draggingCC || !!draggingTreeNodeId;
      if (!stillDragging) return;
      const vh = window.innerHeight;
      let delta = 0;
      if (lastY < SCROLL_ZONE) {
        const ratio = (SCROLL_ZONE - lastY) / SCROLL_ZONE;
        delta = -Math.ceil(MAX_SPEED * ratio);
      } else if (lastY > vh - SCROLL_ZONE) {
        const ratio = (lastY - (vh - SCROLL_ZONE)) / SCROLL_ZONE;
        delta = Math.ceil(MAX_SPEED * ratio);
      }
      if (delta !== 0) {
        window.scrollBy(0, delta);
        rafId = requestAnimationFrame(tick);
      }
    };

    window.addEventListener("dragover", onDragOver);
    return () => {
      window.removeEventListener("dragover", onDragOver);
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }, [draggingCC, draggingTreeNodeId]);

  const handleDragOverNode = (e, nodeId) => {
    // v3.3.504 — accept both unassigned-card drags AND tree-node drags
    if (!draggingCC && !draggingTreeNodeId) return;
    // Don't allow dropping a node onto itself
    if (draggingTreeNodeId && draggingTreeNodeId === nodeId) return;
    e.preventDefault(); // allow drop
    e.dataTransfer.dropEffect = "move";
    setDragOverNodeId(nodeId);
  };
  const handleDragLeaveNode = (nodeId) => {
    setDragOverNodeId(prev => prev === nodeId ? null : prev);
  };
  const handleDropOnNode = async (e, nodeId) => {
    e.preventDefault();
    // v3.3.504 — Branch on what was being dragged
    if (draggingTreeNodeId) {
      // A tree node (group or leaf) was dropped on this group — it's a MOVE
      await handleDropTreeNodeOn(nodeId);
      return;
    }
    const cc = draggingCC;
    setDraggingCC(null);
    setDragOverNodeId(null);
    if (!cc) return;
    // nodeId can be a group id, or 'root' to make it a root leaf
    const parentId = nodeId === "root" ? null : nodeId;
    // Verify target is a group (or root) — leaves can't have children
    if (parentId) {
      const target = nodes.find(n => n.id === parentId);
      if (target && target.node_type !== "group") {
        toast.error("لا يمكن إفلات مركز تكلفة على ورقة — اختر فرعاً");
        return;
      }
    }
    await classifyCostCenter(cc, parentId);
  };

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: "#64748b" }}>...جاري التحميل</div>;

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12, padding: 10, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, flexWrap: "wrap" }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: "#475569" }}>🌳 شجرة الحسابات والتكاليف</span>
        {canEdit && (
          <button
            onClick={() => { setAddingChildOf("root"); setAddForm({ node_type: "group", leaf_kind: entityKind, name: "", cost_center_id: "", journal_account_id: "" }); }}
            style={{ padding: "5px 12px", background: "#16a34a", color: "#fff", border: "none", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 4 }}
          ><FiFolderPlus size={13} /> فرع رئيسي جديد</button>
        )}
        <button onClick={expandAll} style={{ padding: "5px 10px", background: "#f1f5f9", color: "#475569", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>📂 توسيع الكل</button>
        <button onClick={collapseAll} style={{ padding: "5px 10px", background: "#f1f5f9", color: "#475569", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>📁 طي الكل</button>
        {/* v3.3.499 — Export / Import Excel */}
        <span style={{ width: 1, height: 22, background: "#cbd5e1", margin: "0 2px" }} />
        <button onClick={handleExport} title="تصدير الشجرة وغير المصنّفة والمخفاة لملف Excel"
          style={{ padding: "5px 10px", background: "#dcfce7", color: "#15803d", border: "1px solid #86efac", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
          📥 تصدير Excel
        </button>
        {canEdit && (
          <>
            <button onClick={handleImportClick} title="استيراد شجرة من ملف Excel (يستبدل الشجرة الحالية)"
              style={{ padding: "5px 10px", background: "#dbeafe", color: "#1e40af", border: "1px solid #93c5fd", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
              📤 استيراد Excel
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleImportFile}
              style={{ display: "none" }}
            />
          </>
        )}
        <div style={{ marginRight: "auto", fontSize: 11, color: "#64748b" }}>
          {nodes.filter(n => n.node_type === "group").length} فرع · {nodes.filter(n => n.node_type === "leaf").length} مركز تكلفة مُصنّف · <span style={{ color: unassigned.length > 0 ? "#dc2626" : "#94a3b8", fontWeight: 700 }}>{unassigned.length} غير مصنّف</span>{hidden.length > 0 && <span style={{ color: "#64748b", fontWeight: 700 }}> · {hidden.length} مخفي</span>}
        </div>
      </div>

      {/* Tree display */}
      <div
        style={{
          background: dragOverNodeId === "root" ? "#dcfce7" : "#fff",
          border: `${dragOverNodeId === "root" ? "2px dashed #16a34a" : "1px solid #e2e8f0"}`,
          borderRadius: 10,
          padding: 12,
          minHeight: 200,
          transition: "background 0.15s, border 0.15s",
        }}
        // Allow dropping on empty space (root level)
        onDragOver={(e) => {
          if (!draggingCC && !draggingTreeNodeId) return;
          // Only set root when not over a specific node
          if (e.target === e.currentTarget) handleDragOverNode(e, "root");
        }}
        onDragLeave={() => handleDragLeaveNode("root")}
        onDrop={(e) => {
          if (!draggingCC && !draggingTreeNodeId) return;
          if (e.target === e.currentTarget) handleDropOnNode(e, "root");
        }}
      >
        {displayTree.length === 0 ? (
          <div style={{ padding: 30, textAlign: "center", color: "#94a3b8", fontSize: 12 }}>
            {draggingCC
              ? "🎯 أفلت هنا لإضافة كفرع جذري"
              : draggingTreeNodeId
                ? "🎯 أفلت هنا لجعلها فرعاً جذرياً"
                : 'لا توجد فروع بعد — اضغط "فرع رئيسي جديد" للبدء'}
          </div>
        ) : (
          displayTree.map((node, idx) => (
            <CCTreeNode
              key={node.id}
              node={node}
              level={0}
              expandedIds={expandedIds}
              onToggle={toggleExpand}
              canEdit={canEdit}
              onAddChild={(parentId) => { setAddingChildOf(parentId); setAddForm({ node_type: "group", leaf_kind: entityKind, name: "", cost_center_id: "", journal_account_id: "" }); }}
              onEdit={setEditingNode}
              onEditRefNo={(n) => setEditingRefNoNode(n)}
              onDelete={handleDelete}
              // v3.3.494 — Drag & drop (unassigned cards onto tree)
              draggingCC={draggingCC}
              dragOverNodeId={dragOverNodeId}
              onDragOverNode={handleDragOverNode}
              onDragLeaveNode={handleDragLeaveNode}
              onDropOnNode={handleDropOnNode}
              // v3.3.504 — Reorder + Move
              onReorder={handleReorder}
              onMove={(n) => { setMovingNode(n); setMoveTargetParentId(""); }}
              draggingTreeNodeId={draggingTreeNodeId}
              onTreeNodeDragStart={handleTreeNodeDragStart}
              onTreeNodeDragEnd={handleTreeNodeDragEnd}
              allNodes={nodes}
              siblingIndex={idx}
              siblingCount={displayTree.length}
            />
          ))
        )}
        {/* Add new root group button */}
        {canEdit && displayTree.length > 0 && !draggingCC && !draggingTreeNodeId && (
          <button
            onClick={() => { setAddingChildOf("root"); setAddForm({ node_type: "group", leaf_kind: entityKind, name: "", cost_center_id: "", journal_account_id: "" }); }}
            style={{ marginTop: 10, padding: "6px 12px", background: "#f0fdf4", color: "#15803d", border: "1px dashed #86efac", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 4 }}
          ><FiFolderPlus size={13} /> إضافة فرع رئيسي آخر</button>
        )}
        {/* v3.3.494 — Hint when dragging an unassigned card */}
        {draggingCC && displayTree.length > 0 && (
          <div style={{ marginTop: 10, padding: "8px 12px", background: "#dcfce7", color: "#15803d", border: "1px dashed #16a34a", borderRadius: 6, fontSize: 11, fontWeight: 700, textAlign: "center" }}>
            🎯 أفلت <strong>{draggingCC.cost_center_name}</strong> على فرع لإضافته تحته · أو هنا للجذر
          </div>
        )}
        {/* v3.3.504 — Hint when dragging a tree node (move) */}
        {draggingTreeNodeId && displayTree.length > 0 && (
          <div style={{ marginTop: 10, padding: "8px 12px", background: "#dbeafe", color: "#1e40af", border: "1px dashed #3b82f6", borderRadius: 6, fontSize: 11, fontWeight: 700, textAlign: "center" }}>
            🔀 جارٍ نقل العقدة — أفلتها على فرع آخر لنقلها تحته · أو على المنطقة الفارغة للجذر
          </div>
        )}
      </div>

      {/* v3.3.516 — Entity-kind tabs (between the tree and the unassigned/classified/hidden section) */}
      <div style={{ marginTop: 18, display: "flex", gap: 0, borderBottom: "2px solid #e2e8f0" }}>
        <button
          onClick={() => { setEntityKind("cost_center"); setBottomSubTab("unassigned"); }}
          style={{
            flex: 1,
            padding: "12px 16px",
            border: "none",
            borderBottom: entityKind === "cost_center" ? "3px solid #0e7490" : "3px solid transparent",
            background: entityKind === "cost_center" ? "#ecfeff" : "transparent",
            color: entityKind === "cost_center" ? "#0e7490" : "#64748b",
            fontWeight: 800, fontSize: 14, cursor: "pointer", fontFamily: "inherit",
            marginBottom: -2,
            transition: "background 0.15s",
          }}
        >🎯 مراكز التكلفة</button>
        <button
          onClick={() => { setEntityKind("account"); setBottomSubTab("unassigned"); }}
          style={{
            flex: 1,
            padding: "12px 16px",
            border: "none",
            borderBottom: entityKind === "account" ? "3px solid #3730a3" : "3px solid transparent",
            background: entityKind === "account" ? "#eef2ff" : "transparent",
            color: entityKind === "account" ? "#3730a3" : "#64748b",
            fontWeight: 800, fontSize: 14, cursor: "pointer", fontFamily: "inherit",
            marginBottom: -2,
            transition: "background 0.15s",
          }}
        >📚 الحسابات</button>
      </div>

      {/* v3.3.496 — Bottom section with sub-tabs: Unassigned + Hidden + Classified (v3.3.506) */}
      <div style={{ marginTop: 0, padding: 14, background: "#f8fafc", border: "1px solid #e2e8f0", borderTopWidth: 0, borderRadius: "0 0 10px 10px" }}>
        {/* Sub-tabs */}
        <div style={{ display: "flex", gap: 2, marginBottom: 10, borderBottom: "1px solid #e2e8f0" }}>
          <button
            onClick={() => setBottomSubTab("unassigned")}
            style={{
              padding: "7px 14px", border: "none",
              borderBottom: bottomSubTab === "unassigned" ? "2px solid #d97706" : "2px solid transparent",
              background: "transparent",
              color: bottomSubTab === "unassigned" ? "#92400e" : "#64748b",
              fontWeight: 800, fontSize: 12, cursor: "pointer",
              fontFamily: "inherit",
              marginBottom: -1,
            }}
          >
            ⚠️ غير المصنّفة ({currentUnassigned.length})
          </button>
          <button
            onClick={() => setBottomSubTab("classified")}
            style={{
              padding: "7px 14px", border: "none",
              borderBottom: bottomSubTab === "classified" ? "2px solid #16a34a" : "2px solid transparent",
              background: "transparent",
              color: bottomSubTab === "classified" ? "#15803d" : "#64748b",
              fontWeight: 800, fontSize: 12, cursor: "pointer",
              fontFamily: "inherit",
              marginBottom: -1,
            }}
          >
            ✓ المربوطة ({classified.length})
          </button>
          <button
            onClick={() => setBottomSubTab("hidden")}
            style={{
              padding: "7px 14px", border: "none",
              borderBottom: bottomSubTab === "hidden" ? "2px solid #64748b" : "2px solid transparent",
              background: "transparent",
              color: bottomSubTab === "hidden" ? "#0f172a" : "#94a3b8",
              fontWeight: 800, fontSize: 12, cursor: "pointer",
              fontFamily: "inherit",
              marginBottom: -1,
            }}
          >
            🚫 المخفاة ({currentHidden.length})
          </button>
        </div>

        {/* v3.3.506 — Search box (shared component, value swaps per tab) */}
        <div style={{ marginBottom: 10, position: "relative" }}>
          <input
            type="text"
            placeholder={
              bottomSubTab === "unassigned" ? "🔍 ابحث بالرقم أو الاسم..." :
              bottomSubTab === "classified" ? "🔍 ابحث بالرقم أو الاسم أو الفرع..." :
              "🔍 ابحث بالرقم أو الاسم..."
            }
            value={
              bottomSubTab === "unassigned" ? searchUnassigned :
              bottomSubTab === "classified" ? searchClassified :
              searchHidden
            }
            onChange={e => {
              const v = e.target.value;
              if (bottomSubTab === "unassigned") setSearchUnassigned(v);
              else if (bottomSubTab === "classified") setSearchClassified(v);
              else setSearchHidden(v);
            }}
            style={{
              width: "100%", padding: "7px 30px 7px 10px",
              border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 12,
              fontFamily: "inherit", boxSizing: "border-box", background: "#fff",
            }}
          />
          {((bottomSubTab === "unassigned" && searchUnassigned) ||
            (bottomSubTab === "classified" && searchClassified) ||
            (bottomSubTab === "hidden" && searchHidden)) && (
            <button
              onClick={() => {
                if (bottomSubTab === "unassigned") setSearchUnassigned("");
                else if (bottomSubTab === "classified") setSearchClassified("");
                else setSearchHidden("");
              }}
              title="مسح البحث"
              style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#94a3b8", fontSize: 16, padding: 0 }}
            >×</button>
          )}
        </div>

        {/* Unassigned tab content */}
        {bottomSubTab === "unassigned" && (
          <>
            {currentUnassigned.length === 0 ? (
              <div style={{ fontSize: 12, color: "#64748b", padding: 8 }}>
                ✓ كل {entityKind === "cost_center" ? "مراكز التكلفة" : "الحسابات"} مصنّفة أو مخفاة
              </div>
            ) : filteredUnassigned.length === 0 ? (
              <div style={{ fontSize: 12, color: "#94a3b8", padding: 16, textAlign: "center" }}>لا نتائج للبحث</div>
            ) : (
              <>
                {/* v3.3.507 — Bulk action bar (shows when any are selected, or always-visible "select all" toggle) */}
                {canEdit && (
                  <div style={{
                    display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
                    marginBottom: 8, padding: "6px 10px",
                    background: selectedUnassigned.size > 0 ? "#fef3c7" : "#fff",
                    border: `1px solid ${selectedUnassigned.size > 0 ? "#fcd34d" : "#e2e8f0"}`,
                    borderRadius: 6,
                    transition: "background 0.15s",
                  }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700, color: "#475569", cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={filteredUnassigned.length > 0 && filteredUnassigned.every(cc => selectedUnassigned.has(cc.cost_center_id))}
                        ref={el => { if (el) el.indeterminate = selectedUnassigned.size > 0 && !filteredUnassigned.every(cc => selectedUnassigned.has(cc.cost_center_id)); }}
                        onChange={(e) => {
                          if (e.target.checked) selectAllVisible(setSelectedUnassigned, filteredUnassigned, "cost_center_id");
                          else clearSel(setSelectedUnassigned);
                        }}
                      />
                      <span>{selectedUnassigned.size > 0 ? `محدّد ${selectedUnassigned.size}` : "تحديد الكل"}</span>
                    </label>
                    {selectedUnassigned.size > 0 && (
                      <>
                        <span style={{ width: 1, height: 18, background: "#cbd5e1" }} />
                        {/* v3.3.517 — Bulk classify button */}
                        <button
                          onClick={() => { setBulkClassifyOpen(true); setBulkClassifyParentId(""); }}
                          style={{ padding: "4px 10px", background: "#16a34a", color: "#fff", border: "none", borderRadius: 4, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}
                        >🔗 تصنيف المحدّدة ({selectedUnassigned.size})</button>
                        <button
                          onClick={handleBulkHide}
                          style={{ padding: "4px 10px", background: "#64748b", color: "#fff", border: "none", borderRadius: 4, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}
                        >🚫 إخفاء المحدّدة ({selectedUnassigned.size})</button>
                        <button
                          onClick={() => clearSel(setSelectedUnassigned)}
                          style={{ padding: "4px 10px", background: "#f1f5f9", color: "#475569", border: "1px solid #cbd5e1", borderRadius: 4, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}
                        >إلغاء التحديد</button>
                      </>
                    )}
                    <div style={{ marginRight: "auto", fontSize: 10, color: "#92400e", fontWeight: 500 }}>
                      💡 اسحب · اضغط "تصنيف" · "🚫" للإخفاء{searchUnassigned && ` · عُرض ${filteredUnassigned.length} من ${currentUnassigned.length}`}
                    </div>
                  </div>
                )}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 6 }}>
                  {filteredUnassigned.map(cc => {
                    const isDragging = draggingCC && draggingCC.cost_center_id === cc.cost_center_id;
                    const isSel = selectedUnassigned.has(cc.cost_center_id);
                    return (
                      <div
                        key={cc.cost_center_id}
                        draggable={canEdit}
                        onDragStart={(e) => {
                          if (!canEdit) return;
                          e.dataTransfer.effectAllowed = "move";
                          handleDragStart(cc);
                        }}
                        onDragEnd={handleDragEnd}
                        style={{
                          background: isSel ? "#fde68a" : (isDragging ? "#fef3c7" : "#fffbeb"),
                          border: `${isSel ? "2px" : "1px"} solid ${isSel ? "#f59e0b" : (isDragging ? "#f59e0b" : "#fcd34d")}`,
                          borderRadius: 6,
                          padding: "6px 10px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 6,
                          cursor: canEdit ? "grab" : "default",
                          opacity: isDragging ? 0.5 : 1,
                          boxShadow: isDragging ? "0 4px 12px rgba(245, 158, 11, 0.4)" : "none",
                          transition: "opacity 0.15s, box-shadow 0.15s, background 0.15s, border 0.15s",
                        }}
                        title={canEdit ? "اسحبني إلى فرع في الشجرة" : ""}
                      >
                        {canEdit && (
                          <input
                            type="checkbox"
                            checked={isSel}
                            onChange={() => toggleSel(setSelectedUnassigned, cc.cost_center_id)}
                            onClick={(e) => e.stopPropagation()}
                            style={{ cursor: "pointer", flexShrink: 0 }}
                          />
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={cc.cost_center_name}>
                            {canEdit && "⋮⋮ "}{cc.cost_center_name || `CC #${cc.cost_center_id}`}
                          </div>
                          <div style={{ fontSize: 9, color: "#64748b", direction: "ltr", textAlign: "right" }}>
                            {cc.cost_center_code || `#${cc.cost_center_id}`} · {cc.transactions_count} معاملة
                          </div>
                        </div>
                        {canEdit && (
                          <div style={{ display: "flex", gap: 3 }}>
                            <button
                              onClick={() => { setClassifyingCC(cc); setClassifyParentId(""); }}
                              title="اختر الأب من قائمة"
                              style={{ padding: "3px 7px", background: "#0e7490", color: "#fff", border: "none", borderRadius: 4, fontSize: 10, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}
                            >+ تصنيف</button>
                            <button
                              onClick={() => handleHideCC(cc)}
                              title="إخفاء — لا يدخل في التقارير"
                              style={{ padding: "3px 7px", background: "#64748b", color: "#fff", border: "none", borderRadius: 4, fontSize: 10, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}
                            >🚫</button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </>
        )}

        {/* v3.3.506 — Classified tab content */}
        {bottomSubTab === "classified" && (
          <>
            {classified.length === 0 ? (
              <div style={{ fontSize: 12, color: "#64748b", padding: 8 }}>
                لا توجد {entityKind === "cost_center" ? "مراكز تكلفة" : "حسابات"} مصنّفة في الشجرة بعد
              </div>
            ) : filteredClassified.length === 0 ? (
              <div style={{ fontSize: 12, color: "#94a3b8", padding: 16, textAlign: "center" }}>لا نتائج للبحث</div>
            ) : (
              <>
                {/* v3.3.507 — Bulk action bar */}
                {canEdit && (
                  <div style={{
                    display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
                    marginBottom: 8, padding: "6px 10px",
                    background: selectedClassified.size > 0 ? "#dcfce7" : "#fff",
                    border: `1px solid ${selectedClassified.size > 0 ? "#86efac" : "#e2e8f0"}`,
                    borderRadius: 6,
                    transition: "background 0.15s",
                  }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700, color: "#475569", cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={filteredClassified.length > 0 && filteredClassified.every(cc => selectedClassified.has(cc.id))}
                        ref={el => { if (el) el.indeterminate = selectedClassified.size > 0 && !filteredClassified.every(cc => selectedClassified.has(cc.id)); }}
                        onChange={(e) => {
                          if (e.target.checked) selectAllVisible(setSelectedClassified, filteredClassified, "id");
                          else clearSel(setSelectedClassified);
                        }}
                      />
                      <span>{selectedClassified.size > 0 ? `محدّد ${selectedClassified.size}` : "تحديد الكل"}</span>
                    </label>
                    {selectedClassified.size > 0 && (
                      <>
                        <span style={{ width: 1, height: 18, background: "#cbd5e1" }} />
                        <button
                          onClick={handleBulkUnclassify}
                          style={{ padding: "4px 10px", background: "#dc2626", color: "#fff", border: "none", borderRadius: 4, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}
                        >× إزالة المحدّدة ({selectedClassified.size})</button>
                        <button
                          onClick={() => clearSel(setSelectedClassified)}
                          style={{ padding: "4px 10px", background: "#f1f5f9", color: "#475569", border: "1px solid #cbd5e1", borderRadius: 4, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}
                        >إلغاء التحديد</button>
                      </>
                    )}
                    <div style={{ marginRight: "auto", fontSize: 10, color: "#15803d", fontWeight: 500 }}>
                      ⓘ مرتبطة بفروع الشجرة وتدخل التقارير{searchClassified && ` · عُرض ${filteredClassified.length} من ${classified.length}`}
                    </div>
                  </div>
                )}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 6 }}>
                  {filteredClassified.map(cc => {
                    const isSel = selectedClassified.has(cc.id);
                    return (
                      <div
                        key={cc.id}
                        style={{
                          background: isSel ? "#bbf7d0" : "#dcfce7",
                          border: `${isSel ? "2px" : "1px"} solid ${isSel ? "#16a34a" : "#86efac"}`,
                          borderRadius: 6,
                          padding: "6px 10px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 6,
                          transition: "background 0.15s, border 0.15s",
                        }}
                      >
                        {canEdit && (
                          <input
                            type="checkbox"
                            checked={isSel}
                            onChange={() => toggleSel(setSelectedClassified, cc.id)}
                            style={{ cursor: "pointer", flexShrink: 0 }}
                          />
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: "#15803d", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 4 }} title={cc.cost_center_name}>
                            <FiTag size={11} />
                            <span>{cc.cost_center_name || `CC #${cc.cost_center_id}`}</span>
                          </div>
                          <div style={{ fontSize: 9, color: "#64748b", direction: "rtl", textAlign: "right", marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={cc.path}>
                            📁 {cc.path || "(الجذر)"}
                          </div>
                          <div style={{ fontSize: 9, color: "#94a3b8", direction: "ltr", textAlign: "right" }}>
                            ID: {cc.cost_center_id}{cc.cost_center_code ? ` · كود: ${cc.cost_center_code}` : ""}
                          </div>
                        </div>
                        {canEdit && (
                          <div style={{ display: "flex", gap: 3 }}>
                            <button
                              onClick={() => {
                                const node = nodes.find(n => n.id === cc.id);
                                if (node) { setMovingNode(node); setMoveTargetParentId(""); }
                              }}
                              title="نقل لفرع آخر"
                              style={{ padding: "3px 7px", background: "#e0e7ff", color: "#3730a3", border: "1px solid #a5b4fc", borderRadius: 4, fontSize: 10, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}
                            >🔀</button>
                            <button
                              onClick={() => {
                                const node = nodes.find(n => n.id === cc.id);
                                if (node) handleDelete(node);
                              }}
                              title="إزالة من الشجرة (يعود لـ غير المصنّفة)"
                              style={{ padding: "3px 7px", background: "#fee2e2", color: "#dc2626", border: "1px solid #fca5a5", borderRadius: 4, fontSize: 10, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}
                            >×</button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </>
        )}

        {/* Hidden tab content */}
        {bottomSubTab === "hidden" && (
          <>
            {currentHidden.length === 0 ? (
              <div style={{ fontSize: 12, color: "#64748b", padding: 8 }}>
                لا توجد {entityKind === "cost_center" ? "مراكز تكلفة" : "حسابات"} مخفاة
              </div>
            ) : filteredHidden.length === 0 ? (
              <div style={{ fontSize: 12, color: "#94a3b8", padding: 16, textAlign: "center" }}>لا نتائج للبحث</div>
            ) : (
              <>
                {/* v3.3.507 — Bulk action bar */}
                {canEdit && (
                  <div style={{
                    display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
                    marginBottom: 8, padding: "6px 10px",
                    background: selectedHidden.size > 0 ? "#e2e8f0" : "#fff",
                    border: `1px solid ${selectedHidden.size > 0 ? "#94a3b8" : "#e2e8f0"}`,
                    borderRadius: 6,
                    transition: "background 0.15s",
                  }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700, color: "#475569", cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={filteredHidden.length > 0 && filteredHidden.every(cc => selectedHidden.has(cc.cost_center_id))}
                        ref={el => { if (el) el.indeterminate = selectedHidden.size > 0 && !filteredHidden.every(cc => selectedHidden.has(cc.cost_center_id)); }}
                        onChange={(e) => {
                          if (e.target.checked) selectAllVisible(setSelectedHidden, filteredHidden, "cost_center_id");
                          else clearSel(setSelectedHidden);
                        }}
                      />
                      <span>{selectedHidden.size > 0 ? `محدّد ${selectedHidden.size}` : "تحديد الكل"}</span>
                    </label>
                    {selectedHidden.size > 0 && (
                      <>
                        <span style={{ width: 1, height: 18, background: "#cbd5e1" }} />
                        <button
                          onClick={handleBulkUnhide}
                          style={{ padding: "4px 10px", background: "#16a34a", color: "#fff", border: "none", borderRadius: 4, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}
                        >↩ إعادة إظهار المحدّدة ({selectedHidden.size})</button>
                        <button
                          onClick={() => clearSel(setSelectedHidden)}
                          style={{ padding: "4px 10px", background: "#f1f5f9", color: "#475569", border: "1px solid #cbd5e1", borderRadius: 4, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}
                        >إلغاء التحديد</button>
                      </>
                    )}
                    <div style={{ marginRight: "auto", fontSize: 10, color: "#475569", fontWeight: 500 }}>
                      ⓘ مستبعدة من التقارير{searchHidden && ` · عُرض ${filteredHidden.length} من ${currentHidden.length}`}
                    </div>
                  </div>
                )}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 6 }}>
                  {filteredHidden.map(cc => {
                    const isSel = selectedHidden.has(cc.cost_center_id);
                    return (
                      <div
                        key={cc.cost_center_id}
                        style={{
                          background: isSel ? "#cbd5e1" : "#f1f5f9",
                          border: `${isSel ? "2px" : "1px"} solid ${isSel ? "#64748b" : "#cbd5e1"}`,
                          borderRadius: 6,
                          padding: "6px 10px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 6,
                          opacity: isSel ? 1 : 0.85,
                          transition: "background 0.15s, border 0.15s, opacity 0.15s",
                        }}
                      >
                        {canEdit && (
                          <input
                            type="checkbox"
                            checked={isSel}
                            onChange={() => toggleSel(setSelectedHidden, cc.cost_center_id)}
                            style={{ cursor: "pointer", flexShrink: 0 }}
                          />
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: "#475569", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textDecoration: "line-through" }} title={cc.cost_center_name}>
                            {cc.cost_center_name || `CC #${cc.cost_center_id}`}
                          </div>
                          <div style={{ fontSize: 9, color: "#94a3b8", direction: "ltr", textAlign: "right" }}>
                            {cc.cost_center_code || `#${cc.cost_center_id}`} · {cc.transactions_count} معاملة
                          </div>
                        </div>
                        {canEdit && (
                          <button
                            onClick={() => handleUnhideCC(cc)}
                            title="إعادة إظهار — يعود لقائمة غير المصنّفة"
                            style={{ padding: "3px 9px", background: "#16a34a", color: "#fff", border: "none", borderRadius: 4, fontSize: 10, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}
                          >↩ إعادة</button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Add modal */}
      {addingChildOf !== null && (
        <AddNodeModal
          parentName={addingChildOf === "root" ? "(الجذر)" : (nodes.find(n => n.id === addingChildOf)?.name || "")}
          form={addForm}
          setForm={setAddForm}
          unassigned={unassigned}
          accountsUnassigned={accountsUnassigned}
          onCancel={() => setAddingChildOf(null)}
          onSave={handleAdd}
        />
      )}

      {/* Edit modal */}
      {editingNode && (
        <EditNodeModal
          node={editingNode}
          allNodes={nodes}
          onCancel={() => setEditingNode(null)}
          onSave={(data) => handleUpdate(editingNode.id, data)}
        />
      )}

      {/* v3.3.556 — Reference number modal */}
      {editingRefNoNode && (
        <RefNoModal
          node={editingRefNoNode}
          onCancel={() => setEditingRefNoNode(null)}
          onSave={async (refNo) => {
            try {
              // v3.3.652 — was `accountingApi.` (never imported) → ReferenceError → "فشلت العملية" before any request
              await accounting.setTreeNodeRefNo(editingRefNoNode.id, refNo);
              toast.success(refNo ? `تم تعيين الرقم المرجعي: ${refNo}` : "تم مسح الرقم المرجعي");
              setEditingRefNoNode(null);
              load();  // refresh tree
            } catch (e) {
              toast.error(e?.response?.data?.error || "فشلت العملية");
            }
          }}
        />
      )}

      {/* v3.3.494 — Classify modal (choose parent before adding) */}
      {classifyingCC && (
        <AssignToParentModal
          cc={classifyingCC}
          allNodes={nodes}
          parentId={classifyParentId}
          setParentId={setClassifyParentId}
          onCancel={() => { setClassifyingCC(null); setClassifyParentId(""); }}
          onSave={async () => {
            await classifyCostCenter(classifyingCC, classifyParentId ? parseInt(classifyParentId) : null);
            setClassifyingCC(null);
            setClassifyParentId("");
          }}
        />
      )}

      {/* v3.3.504 — Move modal */}
      {movingNode && (
        <MoveNodeModal
          node={movingNode}
          allNodes={nodes}
          targetParentId={moveTargetParentId}
          setTargetParentId={setMoveTargetParentId}
          onCancel={() => { setMovingNode(null); setMoveTargetParentId(""); }}
          onSave={handleMoveSubmit}
        />
      )}

      {/* v3.3.517 — Bulk classify modal */}
      {bulkClassifyOpen && (
        <BulkClassifyModal
          count={selectedUnassigned.size}
          entityKind={entityKind}
          allNodes={nodes}
          parentId={bulkClassifyParentId}
          setParentId={setBulkClassifyParentId}
          onCancel={() => { setBulkClassifyOpen(false); setBulkClassifyParentId(""); }}
          onSave={() => handleBulkClassify(bulkClassifyParentId)}
        />
      )}
    </div>
  );
}

// Recursive tree node renderer
// v3.3.494 — Drop target for groups; green color for assigned leaves
function CCTreeNode({ node, level, expandedIds, onToggle, canEdit, onAddChild, onEdit, onEditRefNo, onDelete,
                     draggingCC, dragOverNodeId, onDragOverNode, onDragLeaveNode, onDropOnNode,
                     // v3.3.504 — Reorder & move
                     onReorder, onMove, draggingTreeNodeId, onTreeNodeDragStart, onTreeNodeDragEnd, allNodes,
                     siblingIndex, siblingCount }) {
  const isGroup = node.node_type === "group";
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expandedIds[node.id];
  const indent = level * 24;
  // v3.3.504 — Drop target accepts both unassigned cards AND moving tree nodes
  const isDropTarget = isGroup && (draggingCC || (draggingTreeNodeId && draggingTreeNodeId !== node.id));
  const isDropHover = isDropTarget && dragOverNodeId === node.id;
  const isBeingDragged = draggingTreeNodeId === node.id;

  // v3.3.494 — Background color
  // v3.3.515 — Different leaf colors by leaf_kind (cost_center=green, account=indigo)
  const isAccountLeaf = !isGroup && node.leaf_kind === "account";
  let bgColor = "transparent";
  if (isDropHover) {
    bgColor = draggingTreeNodeId ? "#bfdbfe" : "#bbf7d0";
  } else if (isGroup) {
    bgColor = "#f8fafc";
  } else if (isAccountLeaf) {
    bgColor = "#e0e7ff"; // indigo tint for accounts
  } else {
    bgColor = "#dcfce7"; // green tint for cost centers
  }

  // v3.3.504 — Determine which reorder buttons to show
  const canMoveUp = (siblingIndex || 0) > 0;
  const canMoveDown = (siblingIndex || 0) < (siblingCount || 1) - 1;

  return (
    <div>
      <div
        // v3.3.504 — Make every tree node draggable (for moving between parents)
        draggable={canEdit && !draggingCC}
        onDragStart={canEdit && !draggingCC ? (e) => {
          e.dataTransfer.effectAllowed = "move";
          onTreeNodeDragStart(node);
        } : undefined}
        onDragEnd={canEdit && !draggingCC ? onTreeNodeDragEnd : undefined}
        onDragOver={isDropTarget ? (e) => onDragOverNode(e, node.id) : undefined}
        onDragLeave={isDropTarget ? () => onDragLeaveNode(node.id) : undefined}
        onDrop={isDropTarget ? (e) => onDropOnNode(e, node.id) : undefined}
        style={{
          display: "flex", alignItems: "center", gap: 6,
          padding: "6px 8px",
          paddingRight: indent + 8,
          borderRadius: 6,
          background: bgColor,
          border: isDropHover
            ? (draggingTreeNodeId ? "2px dashed #3b82f6" : "2px dashed #16a34a")
            : isGroup ? "1px solid transparent" : (isAccountLeaf ? "1px solid #a5b4fc" : "1px solid #86efac"),
          borderBottom: !isDropHover && isGroup ? "1px solid #f1f5f9" : undefined,
          marginBottom: !isGroup ? 2 : 0,
          opacity: isBeingDragged ? 0.4 : 1,
          cursor: canEdit && !draggingCC ? "grab" : "default",
          transition: "background 0.15s, border 0.15s, opacity 0.15s",
        }}
      >
        {/* Expand toggle */}
        {isGroup && hasChildren ? (
          <button onClick={() => onToggle(node.id)} style={{ background: "none", border: "none", cursor: "pointer", padding: 2, color: "#64748b" }} title={isExpanded ? "طي" : "توسيع"}>
            {isExpanded ? <FiChevronDown size={14} /> : <FiChevronLeft size={14} />}
          </button>
        ) : (
          <span style={{ width: 18 }} />
        )}

        {/* Drag handle indicator (v3.3.504) */}
        {canEdit && !draggingCC && (
          <span style={{ color: "#94a3b8", fontSize: 11, userSelect: "none" }} title="اسحب لنقل العقدة">⋮⋮</span>
        )}

        {/* Icon — v3.3.515 different for account leaves */}
        {isGroup
          ? <FiFolder size={14} color={isExpanded ? "#0e7490" : "#64748b"} />
          : isAccountLeaf
            ? <FiBookOpen size={12} color="#3730a3" />
            : <FiTag size={12} color="#15803d" />}

        {/* Name */}
        <span style={{ flex: 1, fontSize: 12, fontWeight: isGroup ? 800 : 600, color: isGroup ? "#0f172a" : (isAccountLeaf ? "#3730a3" : "#15803d") }}>
          {/* v3.3.556 — Reference number badge (user-defined identifier for cross-page lookups) */}
          {node.ref_no && (
            <span
              style={{
                marginLeft: 6,
                display: "inline-block",
                padding: "2px 8px",
                background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                color: "#fff",
                fontSize: 10,
                fontWeight: 800,
                borderRadius: 4,
                direction: "ltr",
                verticalAlign: "middle",
                boxShadow: "0 1px 2px rgba(217, 119, 6, 0.4)",
              }}
              title={`الرقم المرجعي: ${node.ref_no}`}
            >
              🔖 {node.ref_no}
            </span>
          )}
          {node.name}
          {/* v3.3.513 — For leaf nodes, show ID + code badge (v3.3.515 colored by leaf_kind) */}
          {!isGroup && (node.cost_center_id || node.journal_account_id) && (
            <>
              <span
                style={{
                  marginRight: 6,
                  display: "inline-block",
                  padding: "1px 6px",
                  background: isAccountLeaf ? "#3730a3" : "#15803d",
                  color: "#fff",
                  fontSize: 9,
                  fontWeight: 700,
                  borderRadius: 3,
                  direction: "ltr",
                  verticalAlign: "middle",
                }}
                title={isAccountLeaf ? "رقم الحساب (ID)" : "رقم مركز التكلفة (ID)"}
              >
                #{node.cost_center_id || node.journal_account_id}
              </span>
              {(node.cc_code || node.ja_code) && (
                <span
                  style={{
                    marginRight: 4,
                    fontSize: 9,
                    color: "#64748b",
                    fontWeight: 500,
                    direction: "ltr",
                  }}
                  title={isAccountLeaf ? "كود الحساب" : "كود مركز التكلفة"}
                >
                  ({node.cc_code || node.ja_code})
                </span>
              )}
            </>
          )}
        </span>

        {/* Actions */}
        {canEdit && !draggingCC && !draggingTreeNodeId && (
          <div style={{ display: "flex", gap: 4 }}>
            {/* v3.3.504 — Reorder buttons */}
            <button
              onClick={() => onReorder(node, "up")}
              disabled={!canMoveUp}
              title={canMoveUp ? "نقل لأعلى" : "العقدة في الأعلى"}
              style={{
                padding: "2px 6px",
                background: canMoveUp ? "#f1f5f9" : "#f8fafc",
                color: canMoveUp ? "#475569" : "#cbd5e1",
                border: "1px solid " + (canMoveUp ? "#cbd5e1" : "#e2e8f0"),
                borderRadius: 4, fontSize: 10, fontWeight: 700,
                cursor: canMoveUp ? "pointer" : "not-allowed",
                fontFamily: "inherit",
              }}
            >↑</button>
            <button
              onClick={() => onReorder(node, "down")}
              disabled={!canMoveDown}
              title={canMoveDown ? "نقل لأسفل" : "العقدة في الأسفل"}
              style={{
                padding: "2px 6px",
                background: canMoveDown ? "#f1f5f9" : "#f8fafc",
                color: canMoveDown ? "#475569" : "#cbd5e1",
                border: "1px solid " + (canMoveDown ? "#cbd5e1" : "#e2e8f0"),
                borderRadius: 4, fontSize: 10, fontWeight: 700,
                cursor: canMoveDown ? "pointer" : "not-allowed",
                fontFamily: "inherit",
              }}
            >↓</button>
            <button onClick={() => onMove(node)} title="نقل لأب آخر"
              style={{ padding: "2px 6px", background: "#e0e7ff", color: "#3730a3", border: "1px solid #a5b4fc", borderRadius: 4, fontSize: 10, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
              🔀
            </button>
            {isGroup && (
              <button onClick={() => onAddChild(node.id)} title="إضافة فرع/مركز تكلفة هنا"
                style={{ padding: "2px 6px", background: "#dcfce7", color: "#15803d", border: "1px solid #86efac", borderRadius: 4, fontSize: 10, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                <FiPlus size={10} />
              </button>
            )}
            <button onClick={() => onEdit(node)} title="تعديل"
              style={{ padding: "2px 6px", background: "#dbeafe", color: "#1e40af", border: "1px solid #93c5fd", borderRadius: 4, fontSize: 10, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
              <FiEdit2 size={10} />
            </button>
            {/* v3.3.556 — Set/edit reference number */}
            <button onClick={() => onEditRefNo(node)} title={node.ref_no ? `الرقم المرجعي: ${node.ref_no} (اضغط للتعديل)` : "تعيين رقم مرجعي"}
              style={{
                padding: "2px 6px",
                background: node.ref_no ? "#fef3c7" : "#f1f5f9",
                color: node.ref_no ? "#92400e" : "#64748b",
                border: "1px solid " + (node.ref_no ? "#fcd34d" : "#cbd5e1"),
                borderRadius: 4, fontSize: 10, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
              }}>
              🔖
            </button>
            <button onClick={() => onDelete(node)} title="حذف"
              style={{ padding: "2px 6px", background: "#fee2e2", color: "#dc2626", border: "1px solid #fca5a5", borderRadius: 4, fontSize: 10, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
              <FiTrash2 size={10} />
            </button>
          </div>
        )}
      </div>

      {/* Children */}
      {isGroup && isExpanded && hasChildren && (
        <div>
          {node.children.map((c, idx) => (
            <CCTreeNode key={c.id} node={c} level={level + 1} expandedIds={expandedIds} onToggle={onToggle} canEdit={canEdit} onAddChild={onAddChild} onEdit={onEdit} onEditRefNo={onEditRefNo} onDelete={onDelete}
              draggingCC={draggingCC} dragOverNodeId={dragOverNodeId} onDragOverNode={onDragOverNode} onDragLeaveNode={onDragLeaveNode} onDropOnNode={onDropOnNode}
              // v3.3.504
              onReorder={onReorder} onMove={onMove} draggingTreeNodeId={draggingTreeNodeId}
              onTreeNodeDragStart={onTreeNodeDragStart} onTreeNodeDragEnd={onTreeNodeDragEnd}
              allNodes={allNodes}
              siblingIndex={idx} siblingCount={node.children.length}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// v3.3.506 — Searchable Cost Center select with type-to-filter
// Lets the user type either a CC code (numeric) or a name fragment to filter the list.
// Highlights the matched portion and shows the code prominently.
function SearchableCCSelect({ value, onChange, options, placeholder = "ابحث بالرقم أو الاسم..." }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = React.useRef(null);

  // Close on outside click
  useEffect(() => {
    function onDocClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const selected = useMemo(() => {
    if (!value) return null;
    return options.find(o => String(o.cost_center_id) === String(value)) || null;
  }, [value, options]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(o => {
      const name = String(o.cost_center_name || "").toLowerCase();
      const code = String(o.cost_center_code || "").toLowerCase();
      const id = String(o.cost_center_id || "");
      return name.includes(q) || code.includes(q) || id.includes(q);
    });
  }, [query, options]);

  const displayLabel = selected
    ? `${selected.cost_center_name} (${selected.cost_center_code || selected.cost_center_id})`
    : "";

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      {/* Trigger / search input */}
      <div style={{ position: "relative" }}>
        <input
          type="text"
          value={open ? query : displayLabel}
          onChange={e => { setQuery(e.target.value); if (!open) setOpen(true); }}
          onFocus={() => { setOpen(true); setQuery(""); }}
          placeholder={placeholder}
          style={{
            width: "100%", padding: "7px 30px 7px 10px",
            border: `1px solid ${open ? "#0e7490" : "#cbd5e1"}`,
            borderRadius: 6, fontSize: 12, fontFamily: "inherit", boxSizing: "border-box",
            background: "#fff",
          }}
        />
        <div style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", pointerEvents: "none", fontSize: 14 }}>
          {open ? "🔍" : "▾"}
        </div>
        {selected && !open && (
          <button
            onClick={(e) => { e.stopPropagation(); onChange(""); setQuery(""); }}
            title="مسح الاختيار"
            style={{ position: "absolute", left: 28, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#94a3b8", fontSize: 14, padding: 0 }}
          >×</button>
        )}
      </div>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: "absolute", top: "100%", left: 0, right: 0, marginTop: 2,
          background: "#fff", border: "1px solid #cbd5e1", borderRadius: 6,
          boxShadow: "0 8px 24px rgba(15,23,42,0.18)",
          maxHeight: 280, overflowY: "auto", zIndex: 10,
        }}>
          {filtered.length === 0 ? (
            <div style={{ padding: 14, textAlign: "center", color: "#94a3b8", fontSize: 11 }}>
              لا نتائج للبحث "{query}"
            </div>
          ) : (
            <>
              <div style={{ padding: "5px 10px", fontSize: 9, color: "#94a3b8", background: "#f8fafc", borderBottom: "1px solid #f1f5f9", textAlign: "right" }}>
                {filtered.length} نتيجة{filtered.length !== options.length ? ` (من ${options.length})` : ""}
              </div>
              {filtered.slice(0, 200).map(o => {
                const isSel = selected && selected.cost_center_id === o.cost_center_id;
                return (
                  <div
                    key={o.cost_center_id}
                    onClick={() => { onChange(String(o.cost_center_id)); setOpen(false); setQuery(""); }}
                    style={{
                      padding: "7px 10px",
                      cursor: "pointer",
                      borderBottom: "1px solid #f1f5f9",
                      background: isSel ? "#cffafe" : "#fff",
                      display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8,
                    }}
                    onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = "#f8fafc"; }}
                    onMouseLeave={e => { if (!isSel) e.currentTarget.style.background = "#fff"; }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {o.cost_center_name || `CC #${o.cost_center_id}`}
                      </div>
                      <div style={{ fontSize: 9, color: "#64748b", direction: "ltr", textAlign: "right", marginTop: 1 }}>
                        كود: {o.cost_center_code || "—"} · ID: {o.cost_center_id} · {o.transactions_count || 0} معاملة
                      </div>
                    </div>
                    {isSel && <span style={{ color: "#0e7490", fontSize: 14 }}>✓</span>}
                  </div>
                );
              })}
              {filtered.length > 200 && (
                <div style={{ padding: 8, textAlign: "center", color: "#94a3b8", fontSize: 10, background: "#f8fafc", borderTop: "1px solid #f1f5f9" }}>
                  عُرضت أول 200 نتيجة — اكتب لتضييق البحث
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// v3.3.515 — AddNodeModal: supports 3 kinds of nodes
//   group = branch (collects other nodes)
//   leaf cost_center = links to a Daftra cost_center
//   leaf account = links to a Daftra journal_account
function AddNodeModal({ parentName, form, setForm, unassigned, accountsUnassigned, onCancel, onSave }) {
  // The currently-displayed picker depends on form.leaf_kind
  const isLeaf = form.node_type === "leaf";
  const isAcct = isLeaf && form.leaf_kind === "account";
  const pickerOptions = isAcct
    ? (accountsUnassigned || []).map(a => ({
        cost_center_id: a.journal_account_id,
        cost_center_name: a.journal_account_name,
        cost_center_code: a.journal_account_code,
        transactions_count: a.transactions_count,
      }))
    : unassigned;
  const pickerValue = isAcct ? form.journal_account_id : form.cost_center_id;
  const setPickerValue = (v) => {
    if (isAcct) setForm({ ...form, journal_account_id: v });
    else setForm({ ...form, cost_center_id: v });
  };

  return (
    <div onClick={onCancel} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, padding: 20, maxWidth: 520, width: "100%", direction: "rtl" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, paddingBottom: 10, borderBottom: "1px solid #e2e8f0" }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: "#0e7490" }}>➕ إضافة عقدة</div>
          <button onClick={onCancel} style={{ background: "none", border: "none", cursor: "pointer", color: "#64748b" }}><FiX size={20} /></button>
        </div>
        <div style={{ fontSize: 11, color: "#64748b", marginBottom: 12 }}>الإضافة تحت: <strong style={{ color: "#0f172a" }}>{parentName}</strong></div>
        <div style={{ display: "grid", gap: 10 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: "#475569", display: "block", marginBottom: 4 }}>النوع:</label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
              {[
                { v: "group", lk: null,            l: "📁 فرع", color: "#0e7490", bg: "#cffafe" },
                { v: "leaf",  lk: "cost_center",   l: "🎯 مركز تكلفة", color: "#15803d", bg: "#dcfce7" },
                { v: "leaf",  lk: "account",       l: "📚 حساب", color: "#3730a3", bg: "#e0e7ff" },
              ].map(o => {
                const selected = form.node_type === o.v && (o.v === "group" ? true : form.leaf_kind === o.lk);
                return (
                  <button key={`${o.v}-${o.lk}`}
                    onClick={() => setForm({ ...form, node_type: o.v, leaf_kind: o.lk })}
                    style={{
                      padding: "8px 6px",
                      border: `2px solid ${selected ? o.color : "#cbd5e1"}`,
                      borderRadius: 6,
                      background: selected ? o.bg : "#fff",
                      color: selected ? o.color : "#475569",
                      fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                    }}>{o.l}</button>
                );
              })}
            </div>
          </div>
          {isLeaf ? (
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: "#475569", display: "block", marginBottom: 4 }}>
                {isAcct ? "الحساب:" : "مركز التكلفة:"}
              </label>
              <SearchableCCSelect
                value={pickerValue}
                onChange={setPickerValue}
                options={pickerOptions}
                placeholder="🔍 ابحث بالرقم أو الاسم..."
              />
              {pickerOptions.length === 0 && (
                <div style={{ fontSize: 10, color: "#dc2626", marginTop: 4 }}>
                  {isAcct ? "كل الحسابات مُصنّفة بالفعل" : "كل مراكز التكلفة مُصنّفة بالفعل"}
                </div>
              )}
            </div>
          ) : null}
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: "#475569", display: "block", marginBottom: 4 }}>
              الاسم {isLeaf ? "(اتركه فارغاً لاستخدام الاسم الأصلي)" : "*"}:
            </label>
            <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
              placeholder={!isLeaf ? "مثال: مشاريع الأراضي" : "اختياري"}
              style={{ width: "100%", padding: "7px 10px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 12, fontFamily: "inherit", boxSizing: "border-box" }} />
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 16, paddingTop: 12, borderTop: "1px solid #e2e8f0" }}>
          <button onClick={onSave} style={{ flex: 1, padding: "9px", background: "#16a34a", color: "#fff", border: "none", borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>💾 حفظ</button>
          <button onClick={onCancel} style={{ flex: 1, padding: "9px", background: "#f1f5f9", color: "#475569", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>إلغاء</button>
        </div>
      </div>
    </div>
  );
}

// ─── v3.3.556 — Modal to set/clear the reference number for a tree node ───
function RefNoModal({ node, onCancel, onSave }) {
  const [refNo, setRefNo] = useState(node.ref_no || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    const trimmed = refNo.trim();
    await onSave(trimmed || null);  // null = clear
    setSaving(false);
  };

  return (
    <div onClick={onCancel} style={{
      position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.6)",
      zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center",
      padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} dir="rtl" style={{
        background: "#fff", borderRadius: 12, padding: 24, maxWidth: 480, width: "100%",
        boxShadow: "0 25px 50px rgba(0,0,0,0.25)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14, gap: 10 }}>
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 800, margin: 0, color: "#0f172a", display: "flex", alignItems: "center", gap: 6 }}>
              🔖 الرقم المرجعي
            </h3>
            <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>
              فرع: <strong style={{ color: "#0f172a" }}>{node.name}</strong>
            </div>
          </div>
          <button onClick={onCancel} disabled={saving} style={{
            background: "none", border: "none", cursor: saving ? "not-allowed" : "pointer",
            color: "#64748b", fontSize: 18,
          }}>✕</button>
        </div>

        <div style={{
          background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8,
          padding: 10, marginBottom: 14, fontSize: 11, color: "#78350f", lineHeight: 1.7,
        }}>
          💡 الرقم المرجعي يسمح لك بـ:
          <ul style={{ margin: "4px 0", paddingRight: 18 }}>
            <li>تعيين هوية ثابتة لكل فرع (مثلاً: <code>101</code>, <code>ASSETS-1</code>)</li>
            <li>استرجاع الفرع وكل ما تحته عبر هذا الرقم</li>
            <li>حساب إجمالي الفرع (مباشر + الفروع التابعة)</li>
            <li>استخدامه في صفحات أخرى أو تقارير خارجية</li>
          </ul>
        </div>

        <label style={{ fontSize: 12, fontWeight: 700, color: "#0f172a", display: "block", marginBottom: 6 }}>
          الرقم المرجعي:
        </label>
        <input
          type="text"
          value={refNo}
          onChange={e => setRefNo(e.target.value)}
          placeholder="مثلاً: 101 أو ASSETS-1 أو الأصول-1"
          maxLength={50}
          autoFocus
          disabled={saving}
          style={{
            width: "100%", padding: "10px 12px",
            border: "1px solid #e2e8f0", borderRadius: 6,
            fontFamily: "inherit", fontSize: 14, direction: "ltr",
            boxSizing: "border-box",
          }}
          onKeyDown={(e) => { if (e.key === "Enter") handleSave(); }}
        />
        <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 4 }}>
          (أقصى 50 حرف · يجب أن يكون فريداً في الشجرة كاملة)
        </div>

        {node.ref_no && (
          <div style={{
            marginTop: 12, padding: 10,
            background: "#f1f5f9", borderRadius: 6, fontSize: 11, color: "#475569",
          }}>
            الرقم الحالي: <strong style={{ color: "#92400e" }}>🔖 {node.ref_no}</strong>
            <br />
            <span style={{ fontSize: 10 }}>اترك الحقل فارغاً واضغط حفظ لمسحه.</span>
          </div>
        )}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
          <button onClick={onCancel} disabled={saving} style={{
            padding: "8px 16px", background: "#f1f5f9", color: "#475569",
            border: "none", borderRadius: 6, fontSize: 12, cursor: saving ? "not-allowed" : "pointer",
            fontFamily: "inherit",
          }}>إلغاء</button>
          <button onClick={handleSave} disabled={saving} style={{
            padding: "8px 18px",
            background: saving ? "#cbd5e1" : "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
            color: "#fff", border: "none", borderRadius: 6,
            fontSize: 12, fontWeight: 700,
            cursor: saving ? "not-allowed" : "pointer",
            fontFamily: "inherit",
          }}>
            {saving ? "جاري الحفظ..." : "حفظ"}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditNodeModal({ node, allNodes, onCancel, onSave }) {
  const [name, setName] = useState(node.name);
  const [parentId, setParentId] = useState(node.parent_id || "");
  const [sortOrder, setSortOrder] = useState(node.sort_order || 0);

  // Possible parents = all group nodes EXCEPT this node and its descendants
  const descendantIds = useMemo(() => {
    const set = new Set([node.id]);
    let changed = true;
    while (changed) {
      changed = false;
      for (const n of allNodes) {
        if (n.parent_id != null && set.has(n.parent_id) && !set.has(n.id)) {
          set.add(n.id);
          changed = true;
        }
      }
    }
    return set;
  }, [node.id, allNodes]);

  const eligibleParents = allNodes.filter(n => n.node_type === "group" && !descendantIds.has(n.id));

  return (
    <div onClick={onCancel} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, padding: 20, maxWidth: 480, width: "100%", direction: "rtl" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, paddingBottom: 10, borderBottom: "1px solid #e2e8f0" }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: "#1e40af" }}>✏️ تعديل العقدة</div>
          <button onClick={onCancel} style={{ background: "none", border: "none", cursor: "pointer", color: "#64748b" }}><FiX size={20} /></button>
        </div>
        <div style={{ display: "grid", gap: 10 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: "#475569", display: "block", marginBottom: 4 }}>الاسم:</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)} style={{ width: "100%", padding: "7px 10px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 12, fontFamily: "inherit", boxSizing: "border-box" }} />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: "#475569", display: "block", marginBottom: 4 }}>الأب:</label>
            <select value={parentId || ""} onChange={e => setParentId(e.target.value || null)} style={{ width: "100%", padding: "7px 10px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 12, fontFamily: "inherit" }}>
              <option value="">(الجذر)</option>
              {eligibleParents.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: "#475569", display: "block", marginBottom: 4 }}>ترتيب العرض:</label>
            <input type="number" value={sortOrder} onChange={e => setSortOrder(e.target.value)} style={{ width: 120, padding: "7px 10px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 12, fontFamily: "inherit" }} />
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 16, paddingTop: 12, borderTop: "1px solid #e2e8f0" }}>
          <button onClick={() => onSave({ name, parent_id: parentId || null, sort_order: sortOrder })} style={{ flex: 1, padding: "9px", background: "#1e40af", color: "#fff", border: "none", borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>💾 حفظ</button>
          <button onClick={onCancel} style={{ flex: 1, padding: "9px", background: "#f1f5f9", color: "#475569", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>إلغاء</button>
        </div>
      </div>
    </div>
  );
}

// v3.3.494 — Modal for classifying a cost center: prompts the user to pick its parent group.
// Builds a hierarchical select where each group is displayed at its proper depth.
function AssignToParentModal({ cc, allNodes, parentId, setParentId, onCancel, onSave }) {
  // Build hierarchical select options (groups only) ordered by tree position with indent prefix
  const groupOptions = useMemo(() => {
    const childrenOf = {};
    allNodes.forEach(n => {
      const p = n.parent_id;
      if (!childrenOf[p == null ? "root" : p]) childrenOf[p == null ? "root" : p] = [];
      childrenOf[p == null ? "root" : p].push(n);
    });
    Object.values(childrenOf).forEach(arr => arr.sort((a, b) => (a.sort_order - b.sort_order) || String(a.name).localeCompare(String(b.name), "ar")));
    const out = [];
    function walk(parent, depth) {
      const kids = childrenOf[parent == null ? "root" : parent] || [];
      for (const n of kids) {
        if (n.node_type === "group") {
          out.push({ id: n.id, name: n.name, depth });
          walk(n.id, depth + 1);
        }
      }
    }
    walk(null, 0);
    return out;
  }, [allNodes]);

  return (
    <div onClick={onCancel} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, padding: 20, maxWidth: 480, width: "100%", direction: "rtl" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, paddingBottom: 10, borderBottom: "1px solid #e2e8f0" }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: "#0e7490" }}>🏷 تصنيف مركز التكلفة</div>
          <button onClick={onCancel} style={{ background: "none", border: "none", cursor: "pointer", color: "#64748b" }}><FiX size={20} /></button>
        </div>

        <div style={{ marginBottom: 14, padding: 10, background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 6 }}>
          <div style={{ fontSize: 10, color: "#92400e", fontWeight: 600, marginBottom: 2 }}>مركز التكلفة:</div>
          <div style={{ fontSize: 13, color: "#0f172a", fontWeight: 800 }}>{cc.cost_center_name || `CC #${cc.cost_center_id}`}</div>
          {cc.cost_center_code && (
            <div style={{ fontSize: 10, color: "#92400e", direction: "ltr", textAlign: "right", marginTop: 2 }}>كود: {cc.cost_center_code}</div>
          )}
        </div>

        <div>
          <label style={{ fontSize: 11, fontWeight: 700, color: "#475569", display: "block", marginBottom: 4 }}>اختر الفرع الأب:</label>
          <select
            value={parentId || ""}
            onChange={e => setParentId(e.target.value)}
            style={{ width: "100%", padding: "8px 10px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 13, fontFamily: "inherit", boxSizing: "border-box" }}
          >
            <option value="">📁 (الجذر — مستوى أعلى)</option>
            {groupOptions.length === 0 ? (
              <option value="" disabled>(لا توجد فروع — أنشئ فرعاً رئيسياً أولاً)</option>
            ) : (
              groupOptions.map(g => (
                <option key={g.id} value={g.id}>
                  {"— ".repeat(g.depth)}📁 {g.name}
                </option>
              ))
            )}
          </select>
          {groupOptions.length === 0 && (
            <div style={{ fontSize: 10, color: "#dc2626", marginTop: 4 }}>
              💡 لا توجد فروع بعد — يمكنك إضافته للجذر، أو إنشاء فرع رئيسي أولاً
            </div>
          )}
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 16, paddingTop: 12, borderTop: "1px solid #e2e8f0" }}>
          <button onClick={onSave} style={{ flex: 1, padding: "9px", background: "#16a34a", color: "#fff", border: "none", borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>✓ تصنيف</button>
          <button onClick={onCancel} style={{ flex: 1, padding: "9px", background: "#f1f5f9", color: "#475569", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>إلغاء</button>
        </div>
      </div>
    </div>
  );
}

// v3.3.517 — Bulk-classify modal: pick one parent group, attach all selected items as leaves
function BulkClassifyModal({ count, entityKind, allNodes, parentId, setParentId, onCancel, onSave }) {
  const groupOptions = useMemo(() => {
    const childrenOf = {};
    allNodes.forEach(n => {
      const p = n.parent_id;
      if (!childrenOf[p == null ? "root" : p]) childrenOf[p == null ? "root" : p] = [];
      childrenOf[p == null ? "root" : p].push(n);
    });
    Object.values(childrenOf).forEach(arr => arr.sort((a, b) => (a.sort_order - b.sort_order) || String(a.name).localeCompare(String(b.name), "ar")));
    const out = [];
    function walk(parent, depth) {
      const kids = childrenOf[parent == null ? "root" : parent] || [];
      for (const n of kids) {
        if (n.node_type === "group") {
          out.push({ id: n.id, name: n.name, depth });
          walk(n.id, depth + 1);
        }
      }
    }
    walk(null, 0);
    return out;
  }, [allNodes]);

  const isAcc = entityKind === "account";
  const colorBg = isAcc ? "#eef2ff" : "#dcfce7";
  const colorBorder = isAcc ? "#a5b4fc" : "#86efac";
  const colorText = isAcc ? "#3730a3" : "#15803d";
  const headerColor = isAcc ? "#3730a3" : "#0e7490";

  return (
    <div onClick={onCancel} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, padding: 20, maxWidth: 520, width: "100%", direction: "rtl" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, paddingBottom: 10, borderBottom: "1px solid #e2e8f0" }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: headerColor }}>
            🔗 تصنيف جماعي — {isAcc ? "حسابات" : "مراكز تكلفة"}
          </div>
          <button onClick={onCancel} style={{ background: "none", border: "none", cursor: "pointer", color: "#64748b" }}><FiX size={20} /></button>
        </div>

        <div style={{ marginBottom: 14, padding: 12, background: colorBg, border: `1px solid ${colorBorder}`, borderRadius: 6, textAlign: "center" }}>
          <div style={{ fontSize: 22, color: colorText, fontWeight: 900, marginBottom: 2 }}>{count}</div>
          <div style={{ fontSize: 12, color: colorText, fontWeight: 600 }}>
            {isAcc ? "حساب محدّد" : "مركز تكلفة محدّد"} — سيتم تصنيفها كلها تحت الفرع المختار
          </div>
        </div>

        <div>
          <label style={{ fontSize: 11, fontWeight: 700, color: "#475569", display: "block", marginBottom: 4 }}>اختر الفرع الأب:</label>
          <select
            value={parentId || ""}
            onChange={e => setParentId(e.target.value)}
            style={{ width: "100%", padding: "8px 10px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 13, fontFamily: "inherit", boxSizing: "border-box" }}
          >
            <option value="">📁 (الجذر — مستوى أعلى)</option>
            {groupOptions.length === 0 ? (
              <option value="" disabled>(لا توجد فروع — أنشئ فرعاً رئيسياً أولاً)</option>
            ) : (
              groupOptions.map(g => (
                <option key={g.id} value={g.id}>
                  {"— ".repeat(g.depth)}📁 {g.name}
                </option>
              ))
            )}
          </select>
          {groupOptions.length === 0 && (
            <div style={{ fontSize: 10, color: "#dc2626", marginTop: 4 }}>
              💡 لا توجد فروع بعد — يمكنك إضافة الكل للجذر، أو أنشئ فرعاً رئيسياً أولاً
            </div>
          )}
        </div>

        <div style={{ marginTop: 12, padding: 10, background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 6, fontSize: 11, color: "#92400e", lineHeight: 1.6 }}>
          ⚠️ <strong>ملاحظة:</strong> العناصر المصنّفة مسبقاً ستُتخطّى تلقائياً (لن تتكرر).
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 16, paddingTop: 12, borderTop: "1px solid #e2e8f0" }}>
          <button onClick={onSave} style={{ flex: 1, padding: "10px", background: "#16a34a", color: "#fff", border: "none", borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
            ✓ تصنيف {count} عنصر
          </button>
          <button onClick={onCancel} style={{ flex: 1, padding: "10px", background: "#f1f5f9", color: "#475569", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>إلغاء</button>
        </div>
      </div>
    </div>
  );
}

// v3.3.504 — Move modal for relocating a tree node (group or leaf) to a new parent
function MoveNodeModal({ node, allNodes, targetParentId, setTargetParentId, onCancel, onSave }) {
  // Build the set of descendant IDs (so we can exclude them as valid targets, preventing cycles)
  const descendantIds = useMemo(() => {
    const result = new Set([node.id]); // include self
    const childrenOf = {};
    allNodes.forEach(n => {
      const p = n.parent_id;
      if (!childrenOf[p == null ? "root" : p]) childrenOf[p == null ? "root" : p] = [];
      childrenOf[p == null ? "root" : p].push(n);
    });
    function dfs(id) {
      const kids = childrenOf[id] || [];
      for (const k of kids) {
        if (!result.has(k.id)) {
          result.add(k.id);
          dfs(k.id);
        }
      }
    }
    dfs(node.id);
    return result;
  }, [node, allNodes]);

  // Build hierarchical select options (groups only, not in descendants)
  const groupOptions = useMemo(() => {
    const childrenOf = {};
    allNodes.forEach(n => {
      const p = n.parent_id;
      if (!childrenOf[p == null ? "root" : p]) childrenOf[p == null ? "root" : p] = [];
      childrenOf[p == null ? "root" : p].push(n);
    });
    Object.values(childrenOf).forEach(arr => arr.sort((a, b) => (a.sort_order - b.sort_order) || String(a.name).localeCompare(String(b.name), "ar")));
    const out = [];
    function walk(parent, depth) {
      const kids = childrenOf[parent == null ? "root" : parent] || [];
      for (const n of kids) {
        if (n.node_type === "group" && !descendantIds.has(n.id)) {
          out.push({ id: n.id, name: n.name, depth });
          walk(n.id, depth + 1);
        }
      }
    }
    walk(null, 0);
    return out;
  }, [allNodes, descendantIds]);

  const currentParent = useMemo(() => {
    if (node.parent_id == null) return null;
    return allNodes.find(n => n.id === node.parent_id);
  }, [node, allNodes]);

  const isGroup = node.node_type === "group";

  return (
    <div onClick={onCancel} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, padding: 20, maxWidth: 480, width: "100%", direction: "rtl" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, paddingBottom: 10, borderBottom: "1px solid #e2e8f0" }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: "#3730a3" }}>🔀 نقل {isGroup ? "الفرع" : "مركز التكلفة"}</div>
          <button onClick={onCancel} style={{ background: "none", border: "none", cursor: "pointer", color: "#64748b" }}><FiX size={20} /></button>
        </div>

        <div style={{ marginBottom: 14, padding: 10, background: "#e0e7ff", border: "1px solid #a5b4fc", borderRadius: 6 }}>
          <div style={{ fontSize: 10, color: "#3730a3", fontWeight: 600, marginBottom: 2 }}>العقدة المنقولة:</div>
          <div style={{ fontSize: 13, color: "#0f172a", fontWeight: 800, display: "flex", alignItems: "center", gap: 5 }}>
            {isGroup ? <FiFolder size={14} color="#3730a3" /> : <FiTag size={12} color="#15803d" />}
            <span>{node.name}</span>
          </div>
          <div style={{ fontSize: 10, color: "#3730a3", marginTop: 4 }}>
            الأب الحالي: <strong>{currentParent ? currentParent.name : "📁 (الجذر — مستوى أعلى)"}</strong>
          </div>
          {isGroup && (
            <div style={{ fontSize: 10, color: "#7c3aed", marginTop: 2 }}>
              ⓘ كل ما بداخل هذا الفرع سيُنقل معه
            </div>
          )}
        </div>

        <div>
          <label style={{ fontSize: 11, fontWeight: 700, color: "#475569", display: "block", marginBottom: 4 }}>الأب الجديد:</label>
          <select
            value={targetParentId || ""}
            onChange={e => setTargetParentId(e.target.value)}
            style={{ width: "100%", padding: "8px 10px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 13, fontFamily: "inherit", boxSizing: "border-box" }}
          >
            <option value="">📁 (الجذر — مستوى أعلى)</option>
            {groupOptions.map(g => (
              <option key={g.id} value={g.id}>
                {"— ".repeat(g.depth)}📁 {g.name}
              </option>
            ))}
          </select>
          {groupOptions.length === 0 && (
            <div style={{ fontSize: 10, color: "#64748b", marginTop: 4 }}>
              ⓘ لا توجد فروع أخرى — يمكن النقل للجذر فقط
            </div>
          )}
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 16, paddingTop: 12, borderTop: "1px solid #e2e8f0" }}>
          <button onClick={onSave} style={{ flex: 1, padding: "9px", background: "#3730a3", color: "#fff", border: "none", borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>🔀 نقل</button>
          <button onClick={onCancel} style={{ flex: 1, padding: "9px", background: "#f1f5f9", color: "#475569", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>إلغاء</button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// v3.3.510 — DETAILED STATEMENT TAB (كشف الحساب المفصّل)
// Two sub-tabs: cost_center | account. User picks one entity, optionally
// filters by date range, then sees all transactions with running balance
// + Print, Excel export, and PDF (browser print-to-PDF) buttons.
// ═══════════════════════════════════════════════════════════════════
function StatementTab() {
  const [subTab, setSubTab] = useState("cost_center"); // 'cost_center' | 'account'
  const [selectedId, setSelectedId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [includeDrafts, setIncludeDrafts] = useState(false);  // v3.3.536 — default = false (hide)
  // Lists for the picker
  const [ccList, setCcList] = useState([]);
  const [acctList, setAcctList] = useState([]);

  // Load both lists once on mount (they're cached locally for picker UX)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [cc, ac] = await Promise.allSettled([
          accounting.listCostCenters(),
          accounting.listAccounts(),
        ]);
        if (!alive) return;
        if (cc.status === "fulfilled") setCcList(cc.value.data?.items || []);
        if (ac.status === "fulfilled") {
          const items = (ac.value.data?.items || []).map(a => ({
            cost_center_id: a.journal_account_id,  // reuse the field name for SearchableCCSelect
            cost_center_name: a.journal_account_name,
            cost_center_code: a.journal_account_code,
            transactions_count: a.transactions_count,
          }));
          setAcctList(items);
        }
      } catch (e) { /* silent */ }
    })();
    return () => { alive = false; };
  }, []);

  const load = useCallback(async () => {
    if (!selectedId) {
      setData(null);
      return;
    }
    setLoading(true);
    try {
      const params = { type: subTab, id: selectedId };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (includeDrafts) params.include_drafts = 1;  // v3.3.536
      const r = await accounting.statement(params);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.error || "فشل تحميل كشف الحساب");
    } finally {
      setLoading(false);
    }
  }, [subTab, selectedId, dateFrom, dateTo, includeDrafts]);
  useEffect(() => { load(); }, [load]);

  // Reset selection when switching sub-tabs
  useEffect(() => {
    setSelectedId("");
    setData(null);
  }, [subTab]);

  const handleExportExcel = async () => {
    if (!selectedId) return;
    try {
      const params = { type: subTab, id: selectedId };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (includeDrafts) params.include_drafts = 1;  // v3.3.536
      await accounting.statementExport(params);
      toast.success("تم تنزيل الملف");
    } catch (e) {
      let msg = "فشل التصدير";
      try {
        if (e?.response?.data instanceof Blob) {
          const txt = await e.response.data.text();
          const parsed = JSON.parse(txt);
          if (parsed?.error) msg = parsed.error;
        }
      } catch {}
      toast.error(msg);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const pickerOptions = subTab === "cost_center" ? ccList.filter(c => c.is_active !== false) : acctList;
  const entityTypeLabel = subTab === "cost_center" ? "مركز التكلفة" : "الحساب";

  return (
    <div>
      {/* Print stylesheet — hides everything except the statement when printing */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          .statement-print, .statement-print * { visibility: visible; }
          .statement-print { position: absolute; right: 0; top: 0; width: 100%; padding: 14px; }
          .statement-no-print { display: none !important; }
          @page { size: A4 landscape; margin: 14mm; }
        }
      `}</style>

      <div className="statement-no-print">
        {/* Sub-tabs */}
        <div style={{ display: "flex", gap: 2, marginBottom: 12, borderBottom: "1px solid #e2e8f0" }}>
          <button
            onClick={() => setSubTab("cost_center")}
            style={{
              padding: "8px 16px", border: "none",
              borderBottom: subTab === "cost_center" ? "2px solid #0e7490" : "2px solid transparent",
              background: "transparent",
              color: subTab === "cost_center" ? "#0e7490" : "#64748b",
              fontWeight: 800, fontSize: 13, cursor: "pointer", fontFamily: "inherit", marginBottom: -1,
            }}
          >🎯 مراكز التكلفة</button>
          <button
            onClick={() => setSubTab("account")}
            style={{
              padding: "8px 16px", border: "none",
              borderBottom: subTab === "account" ? "2px solid #3730a3" : "2px solid transparent",
              background: "transparent",
              color: subTab === "account" ? "#3730a3" : "#64748b",
              fontWeight: 800, fontSize: 13, cursor: "pointer", fontFamily: "inherit", marginBottom: -1,
            }}
          >📚 الحسابات</button>
        </div>

        {/* Picker + filters */}
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr auto", gap: 8, alignItems: "end", marginBottom: 14, padding: 12, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: "#475569", display: "block", marginBottom: 4 }}>
              اختر {entityTypeLabel}:
            </label>
            <SearchableCCSelect
              value={selectedId}
              onChange={setSelectedId}
              options={pickerOptions}
              placeholder={`🔍 ابحث بالرقم أو الاسم في ${pickerOptions.length} ${subTab === "cost_center" ? "مركز" : "حساب"}...`}
            />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: "#475569", display: "block", marginBottom: 4 }}>من تاريخ:</label>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ width: "100%", padding: "7px 10px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 12, fontFamily: "inherit", boxSizing: "border-box" }} />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: "#475569", display: "block", marginBottom: 4 }}>إلى تاريخ:</label>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ width: "100%", padding: "7px 10px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 12, fontFamily: "inherit", boxSizing: "border-box" }} />
          </div>
          {(dateFrom || dateTo) && (
            <button onClick={() => { setDateFrom(""); setDateTo(""); }} style={{ padding: "8px 12px", background: "#f1f5f9", color: "#475569", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>🔄</button>
          )}
        </div>

        {/* v3.3.534 — Drafts info + toggle
            v3.3.536 — Default is to HIDE drafts; checkbox to include them */}
        {data && data.totals && (
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12, padding: 10, background: includeDrafts && data.totals.drafts_count > 0 ? "#fef3c7" : "#f0fdf4", border: `1px solid ${includeDrafts && data.totals.drafts_count > 0 ? "#fcd34d" : "#86efac"}`, borderRadius: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 12, color: includeDrafts && data.totals.drafts_count > 0 ? "#78350f" : "#15803d", fontWeight: 700 }}>
              {includeDrafts
                ? (data.totals.drafts_count > 0
                  ? `⚠️ تضمين ${data.totals.drafts_count} قيد مسودة (غير معتمد) في الكشف`
                  : "✓ لا توجد مسودات في هذا الكشف")
                : "✓ المسودات مخفية افتراضياً — الأرقام معتمدة"}
            </span>
            <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "#475569", cursor: "pointer", fontWeight: 600 }}>
              <input type="checkbox" checked={includeDrafts} onChange={e => setIncludeDrafts(e.target.checked)} />
              <span>تضمين المسودات في الكشف</span>
            </label>
            {includeDrafts && data.totals.drafts_count > 0 && (
              <span style={{ marginRight: "auto", fontSize: 10, color: "#64748b" }}>
                المسودات تظهر بخلفية صفراء وعلامة "مسودة"
              </span>
            )}
          </div>
        )}

        {/* Action buttons */}
        {data && data.items && (
          <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
            <button onClick={handlePrint} style={{ padding: "7px 14px", background: "#0e7490", color: "#fff", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
              🖨️ طباعة
            </button>
            <button onClick={handlePrint} title="استخدم 'حفظ كـ PDF' من نافذة الطباعة" style={{ padding: "7px 14px", background: "#dc2626", color: "#fff", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
              📄 PDF
            </button>
            <button onClick={handleExportExcel} style={{ padding: "7px 14px", background: "#16a34a", color: "#fff", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
              📊 تصدير Excel
            </button>
          </div>
        )}
      </div>

      {/* Content (this section gets printed) */}
      {!selectedId ? (
        <div style={{ padding: 50, textAlign: "center", color: "#94a3b8", background: "#f8fafc", borderRadius: 10, border: "1px dashed #cbd5e1" }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>📑</div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>اختر {entityTypeLabel} لعرض كشف الحساب المفصّل</div>
          <div style={{ fontSize: 11, marginTop: 6 }}>يمكنك البحث بالرقم أو الاسم</div>
        </div>
      ) : loading ? (
        <div style={{ padding: 40, textAlign: "center", color: "#64748b" }}>...جاري التحميل</div>
      ) : data && data.items ? (
        <div className="statement-print">
          {/* Header card */}
          <div style={{ padding: "16px 20px", background: subTab === "cost_center" ? "#cffafe" : "#e0e7ff", border: `2px solid ${subTab === "cost_center" ? "#0e7490" : "#3730a3"}`, borderRadius: 10, marginBottom: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: subTab === "cost_center" ? "#0e7490" : "#3730a3", marginBottom: 4 }}>
                  كشف حساب مفصّل — {entityTypeLabel}
                </div>
                <div style={{ fontSize: 18, fontWeight: 800, color: "#0f172a" }}>
                  {data.header.name || `—`}
                </div>
                <div style={{ fontSize: 12, color: "#475569", marginTop: 3, direction: "ltr", textAlign: "right" }}>
                  {data.header.code && <span>الكود: <strong>{data.header.code}</strong></span>}
                  {data.header.code && <span> · </span>}
                  <span>ID: <strong>{data.header.id}</strong></span>
                  {data.header.account_type && <span> · النوع: <strong>{data.header.account_type}</strong></span>}
                </div>
              </div>
              <div style={{ fontSize: 11, color: "#475569", textAlign: "left" }}>
                <div>📅 الفترة:</div>
                <div><strong>{data.filters.date_from || "منذ البداية"}</strong> ← <strong>{data.filters.date_to || "حتى الآن"}</strong></div>
                <div style={{ marginTop: 4, color: "#94a3b8" }}>تاريخ التقرير: {new Date().toLocaleDateString("en-GB")}</div>
              </div>
            </div>
          </div>

          {/* Totals KPI */}
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 8, marginBottom: 14,
            padding: "12px 16px", background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
            borderRadius: 10, color: "#fff",
          }}>
            <div>
              <div style={{ fontSize: 10, opacity: 0.85 }}>📊 عدد القيود</div>
              <div style={{ fontSize: 18, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>{fmt(data.totals.transactions_count)}</div>
            </div>
            <div>
              <div style={{ fontSize: 10, opacity: 0.85 }}>⬆️ إجمالي المدين</div>
              <div style={{ fontSize: 18, fontWeight: 800, direction: "ltr", textAlign: "right", fontVariantNumeric: "tabular-nums", color: "#86efac" }}>{fmt(data.totals.total_debit)}</div>
            </div>
            <div>
              <div style={{ fontSize: 10, opacity: 0.85 }}>⬇️ إجمالي الدائن</div>
              <div style={{ fontSize: 18, fontWeight: 800, direction: "ltr", textAlign: "right", fontVariantNumeric: "tabular-nums", color: "#fcd34d" }}>{fmt(data.totals.total_credit)}</div>
            </div>
            <div>
              <div style={{ fontSize: 10, opacity: 0.85 }}>⚖️ الرصيد النهائي</div>
              <div style={{ fontSize: 18, fontWeight: 800, direction: "ltr", textAlign: "right", fontVariantNumeric: "tabular-nums", color: Number(data.totals.final_balance) >= 0 ? "#86efac" : "#fca5a5" }}>{fmt(data.totals.final_balance)}</div>
            </div>
          </div>

          {/* Transactions table */}
          {data.items.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center", color: "#94a3b8", background: "#f8fafc", borderRadius: 10, border: "1px dashed #cbd5e1" }}>
              لا توجد قيود لـ {entityTypeLabel} في هذه الفترة
            </div>
          ) : (
            <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, overflow: "hidden" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                <thead>
                  <tr style={{ background: "#f1f5f9", borderBottom: "2px solid #cbd5e1" }}>
                    <th style={{ ...th, fontSize: 10 }}>#</th>
                    <th style={{ ...th, fontSize: 10 }}>رقم القيد</th>
                    <th style={{ ...th, fontSize: 10 }}>التاريخ</th>
                    <th style={{ ...th, fontSize: 10 }}>البيان</th>
                    <th style={{ ...th, fontSize: 10 }}>{subTab === "cost_center" ? "الحساب" : "مركز التكلفة"}</th>
                    <th style={{ ...th, textAlign: "left", fontSize: 10 }}>مدين</th>
                    <th style={{ ...th, textAlign: "left", fontSize: 10 }}>دائن</th>
                    <th style={{ ...th, textAlign: "left", fontSize: 10 }}>الرصيد</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((r, idx) => {
                    const debit = Number(r.debit) || 0;
                    const credit = Number(r.credit) || 0;
                    // v3.3.534 — Visually mark draft rows so users can see what's not finalized
                    const isDraft = !!r.is_draft;
                    return (
                      <tr key={r.id} style={{
                        borderBottom: "1px solid #f1f5f9",
                        background: isDraft ? "#fef3c7" : (idx % 2 === 0 ? "#fff" : "#fafafa"),
                      }}>
                        <td style={{ ...td, textAlign: "center", color: "#94a3b8", fontSize: 10 }}>{idx + 1}</td>
                        <td style={{ ...td, textAlign: "center", color: "#475569", fontWeight: 700, direction: "ltr" }}>
                          {isDraft && <span style={{ background: "#fcd34d", color: "#78350f", padding: "1px 5px", borderRadius: 3, fontSize: 9, fontWeight: 800, marginLeft: 4 }}>مسودة</span>}
                          {r.journal_number || "—"}
                        </td>
                        <td style={{ ...td, textAlign: "center", color: "#0f172a", direction: "ltr" }}>{r.journal_date ? new Date(r.journal_date).toISOString().slice(0,10) : "—"}</td>
                        <td style={{ ...td, color: "#0f172a", maxWidth: 250, overflow: "hidden", textOverflow: "ellipsis" }} title={r.journal_description}>{r.journal_description || "—"}</td>
                        <td style={{ ...td, color: "#475569" }}>
                          {subTab === "cost_center" ? (r.journal_account_name || "—") : (r.cost_center_name || "—")}
                          {(subTab === "cost_center" ? r.journal_account_code : r.cost_center_code) && (
                            <span style={{ fontSize: 9, color: "#94a3b8", marginRight: 4, direction: "ltr" }}>
                              ({subTab === "cost_center" ? r.journal_account_code : r.cost_center_code})
                            </span>
                          )}
                        </td>
                        <td style={{ ...td, textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", color: debit > 0 ? "#15803d" : "#cbd5e1", fontWeight: 700 }}>{debit > 0 ? fmt(debit) : "—"}</td>
                        <td style={{ ...td, textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", color: credit > 0 ? "#b45309" : "#cbd5e1", fontWeight: 700 }}>{credit > 0 ? fmt(credit) : "—"}</td>
                        <td style={{ ...td, textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", fontWeight: 800, color: Number(r.running_balance) >= 0 ? "#0f172a" : "#dc2626" }}>{fmt(r.running_balance)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ background: "#0f172a", color: "#fff", fontWeight: 800 }}>
                    <td colSpan={5} style={{ ...td, textAlign: "right" }}>الإجمالي</td>
                    <td style={{ ...td, textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", color: "#86efac" }}>{fmt(data.totals.total_debit)}</td>
                    <td style={{ ...td, textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", color: "#fcd34d" }}>{fmt(data.totals.total_credit)}</td>
                    <td style={{ ...td, textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", color: Number(data.totals.final_balance) >= 0 ? "#86efac" : "#fca5a5" }}>{fmt(data.totals.final_balance)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// v3.3.488 — COST-CENTER REPORT TAB (read-only aggregated view)
// ═══════════════════════════════════════════════════════════════════
function CCReportTab() {
  const [data, setData] = useState({ nodes: [], unassigned: { items: [], total_debit: 0, total_credit: 0, net: 0, transactions_count: 0 }, drafts_count: 0, include_drafts: false });
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [expandedIds, setExpandedIds] = useState({});
  // v3.3.502 — Unassigned section is collapsible (default: collapsed for cleaner view)
  const [unassignedExpanded, setUnassignedExpanded] = useState(false);
  // v3.3.538 — Drafts hidden by default (matches Daftra UI); checkbox to include them
  const [includeDrafts, setIncludeDrafts] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (includeDrafts) params.include_drafts = 1;  // v3.3.538
      const r = await accounting.ccTreeReport(params);
      setData(r.data || { nodes: [], unassigned: { items: [], total_debit: 0, total_credit: 0, net: 0, transactions_count: 0 }, drafts_count: 0, include_drafts: false });
    } catch (e) {
      toast.error("فشل تحميل التقرير");
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, includeDrafts]);
  useEffect(() => { load(); }, [load]);

  const tree = useMemo(() => buildTree(data.nodes), [data.nodes]);

  const toggleExpand = (id) => setExpandedIds(prev => ({ ...prev, [id]: !prev[id] }));
  const expandAll = () => {
    const all = {};
    data.nodes.forEach(n => { if (n.node_type === "group") all[n.id] = true; });
    setExpandedIds(all);
  };
  const collapseAll = () => setExpandedIds({});

  // v3.3.502 — Separate totals: classified tree vs unassigned
  const classifiedTotals = useMemo(() => {
    return tree.reduce((s, n) => ({
      total_debit: s.total_debit + Number(n.total_debit || 0),
      total_credit: s.total_credit + Number(n.total_credit || 0),
      net: s.net + Number(n.net || 0),
      transactions_count: s.transactions_count + Number(n.transactions_count || 0),
    }), { total_debit: 0, total_credit: 0, net: 0, transactions_count: 0 });
  }, [tree]);

  const unassignedTotals = useMemo(() => ({
    total_debit: Number(data.unassigned?.total_debit || 0),
    total_credit: Number(data.unassigned?.total_credit || 0),
    net: Number(data.unassigned?.net || 0),
    transactions_count: Number(data.unassigned?.transactions_count || 0),
    count: data.unassigned?.items?.length || 0,
  }), [data.unassigned]);

  const grandTotal = useMemo(() => ({
    total_debit: classifiedTotals.total_debit + unassignedTotals.total_debit,
    total_credit: classifiedTotals.total_credit + unassignedTotals.total_credit,
    net: classifiedTotals.net + unassignedTotals.net,
    transactions_count: classifiedTotals.transactions_count + unassignedTotals.transactions_count,
  }), [classifiedTotals, unassignedTotals]);

  return (
    <div>
      {/* Filters */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12, padding: 12, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, flexWrap: "wrap" }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: "#475569" }}>🔍 الفترة:</span>
        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={inputS} />
        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={inputS} />
        {(dateFrom || dateTo) && (
          <button onClick={() => { setDateFrom(""); setDateTo(""); }} style={{ padding: "5px 10px", background: "#f1f5f9", color: "#475569", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>🔄 إعادة تعيين</button>
        )}
        <button onClick={expandAll} style={{ padding: "5px 10px", background: "#f1f5f9", color: "#475569", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>📂 توسيع الكل</button>
        <button onClick={collapseAll} style={{ padding: "5px 10px", background: "#f1f5f9", color: "#475569", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>📁 طي الكل</button>
      </div>

      {/* v3.3.538 — Drafts banner + toggle (matches StatementTab pattern).
          Default: hide drafts → numbers match Daftra UI export. */}
      <div style={{
        display: "flex", alignItems: "center", gap: 12, marginBottom: 12, padding: 10,
        background: includeDrafts && data.drafts_count > 0 ? "#fef3c7" : "#f0fdf4",
        border: `1px solid ${includeDrafts && data.drafts_count > 0 ? "#fcd34d" : "#86efac"}`,
        borderRadius: 8, flexWrap: "wrap"
      }}>
        <span style={{ fontSize: 12, color: includeDrafts && data.drafts_count > 0 ? "#78350f" : "#15803d", fontWeight: 700 }}>
          {includeDrafts
            ? (data.drafts_count > 0
              ? `⚠️ تضمين ${fmt(data.drafts_count)} قيد مسودة (غير معتمد) في التقرير`
              : "✓ لا توجد مسودات في هذه الفترة")
            : (data.drafts_count > 0
              ? `✓ المسودات مخفية افتراضياً — الأرقام معتمدة (${fmt(data.drafts_count)} مسودة مستبعدة)`
              : "✓ المسودات مخفية افتراضياً — الأرقام معتمدة")}
        </span>
        <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "#475569", cursor: "pointer", fontWeight: 600 }}>
          <input type="checkbox" checked={includeDrafts} onChange={e => setIncludeDrafts(e.target.checked)} />
          <span>تضمين المسودات في التقرير</span>
        </label>
      </div>

      {/* Grand-total KPI strip */}
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10, marginBottom: 14,
        padding: "14px 18px",
        background: "linear-gradient(135deg, #0e7490 0%, #155e75 100%)",
        borderRadius: 12, color: "#fff",
      }}>
        <div>
          <div style={{ fontSize: 11, opacity: 0.9, fontWeight: 600 }}>📊 الإجمالي الكلي</div>
          <div style={{ fontSize: 11, opacity: 0.85, marginTop: 2 }}>المعاملات: {fmt(grandTotal.transactions_count)}</div>
        </div>
        <div>
          <div style={{ fontSize: 11, opacity: 0.9, fontWeight: 600 }}>⬆️ إجمالي المدين</div>
          <div style={{ fontSize: 20, fontWeight: 800, direction: "ltr", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmt(grandTotal.total_debit)}</div>
        </div>
        <div>
          <div style={{ fontSize: 11, opacity: 0.9, fontWeight: 600 }}>⬇️ إجمالي الدائن</div>
          <div style={{ fontSize: 20, fontWeight: 800, direction: "ltr", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmt(grandTotal.total_credit)}</div>
        </div>
        <div>
          <div style={{ fontSize: 11, opacity: 0.9, fontWeight: 600 }}>⚖️ الصافي</div>
          <div style={{ fontSize: 20, fontWeight: 800, direction: "ltr", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmt(grandTotal.net)}</div>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: "#64748b" }}>...جاري التحميل</div>
      ) : (
        <>
          {/* ═══════ القسم 1: المصنّفة (Classified) ═══════ */}
          <div style={{ background: "#fff", border: "1px solid #86efac", borderRadius: 10, overflow: "hidden", marginBottom: 16 }}>
            {/* Section header */}
            <div style={{
              display: "grid", gridTemplateColumns: "1fr 110px 130px 130px 130px",
              padding: "10px 14px",
              background: "linear-gradient(135deg, #16a34a 0%, #15803d 100%)",
              color: "#fff",
              fontSize: 12, fontWeight: 800,
            }}>
              <div>🟢 المصنّفة — الشجرة ({tree.length} فرع رئيسي · {fmt(classifiedTotals.transactions_count)} معاملة)</div>
              <div style={{ textAlign: "center" }}>{fmt(classifiedTotals.transactions_count)}</div>
              <div style={{ textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums" }}>{fmt(classifiedTotals.total_debit)}</div>
              <div style={{ textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums" }}>{fmt(classifiedTotals.total_credit)}</div>
              <div style={{ textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums" }}>{fmt(classifiedTotals.net)}</div>
            </div>
            {/* Column headers */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 110px 130px 130px 130px", padding: "7px 14px", background: "#f8fafc", borderBottom: "1px solid #e2e8f0", fontSize: 11, fontWeight: 700, color: "#475569" }}>
              <div>الفرع / مركز التكلفة</div>
              <div style={{ textAlign: "center" }}>عدد المعاملات</div>
              <div style={{ textAlign: "left" }}>إجمالي مدين</div>
              <div style={{ textAlign: "left" }}>إجمالي دائن</div>
              <div style={{ textAlign: "left" }}>الصافي</div>
            </div>
            {tree.length === 0 ? (
              <div style={{ padding: 30, textAlign: "center", color: "#94a3b8", fontSize: 12 }}>
                لا توجد فروع — أنشئ شجرة مراكز التكلفة من تبويب "🌳 شجرة مراكز التكلفة"
              </div>
            ) : (
              tree.map(node => (
                <CCReportNode key={node.id} node={node} level={0} expandedIds={expandedIds} onToggle={toggleExpand} />
              ))
            )}
          </div>

          {/* ═══════ القسم 2: غير المصنّفة (Unassigned) — Collapsible ═══════ */}
          <div style={{ background: "#fff", border: "1px solid #fcd34d", borderRadius: 10, overflow: "hidden" }}>
            {/* Section header — clickable to toggle collapse */}
            <div
              onClick={() => setUnassignedExpanded(!unassignedExpanded)}
              style={{
                display: "grid", gridTemplateColumns: "1fr 110px 130px 130px 130px",
                padding: "10px 14px",
                background: "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                color: "#fff",
                fontSize: 12, fontWeight: 800,
                cursor: unassignedTotals.count > 0 ? "pointer" : "default",
                userSelect: "none",
              }}
              title={unassignedTotals.count > 0 ? (unassignedExpanded ? "اضغط للطي" : "اضغط لعرض القائمة") : ""}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                {unassignedTotals.count > 0 && (
                  <span style={{ fontSize: 14 }}>{unassignedExpanded ? "▼" : "◀"}</span>
                )}
                <span>🟡 غير المصنّفة ({unassignedTotals.count} مركز · {fmt(unassignedTotals.transactions_count)} معاملة)</span>
              </div>
              <div style={{ textAlign: "center" }}>{fmt(unassignedTotals.transactions_count)}</div>
              <div style={{ textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums" }}>{fmt(unassignedTotals.total_debit)}</div>
              <div style={{ textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums" }}>{fmt(unassignedTotals.total_credit)}</div>
              <div style={{ textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums" }}>{fmt(unassignedTotals.net)}</div>
            </div>
            {/* Expandable detail */}
            {unassignedExpanded && unassignedTotals.count > 0 && (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 110px 130px 130px 130px", padding: "7px 14px", background: "#fef3c7", borderBottom: "1px solid #fcd34d", fontSize: 11, fontWeight: 700, color: "#92400e" }}>
                  <div>مركز التكلفة</div>
                  <div style={{ textAlign: "center" }}>عدد المعاملات</div>
                  <div style={{ textAlign: "left" }}>إجمالي مدين</div>
                  <div style={{ textAlign: "left" }}>إجمالي دائن</div>
                  <div style={{ textAlign: "left" }}>الصافي</div>
                </div>
                {data.unassigned.items.map((cc, idx) => {
                  const isAcc = cc.kind === "account";
                  const itemKey = isAcc ? `a-${cc.journal_account_id}` : `c-${cc.cost_center_id}`;
                  const itemName = isAcc ? cc.ja_name : cc.cc_name;
                  const itemCode = isAcc ? cc.ja_code : cc.cc_code;
                  const itemId   = isAcc ? cc.journal_account_id : cc.cost_center_id;
                  return (
                  <div key={itemKey} style={{
                    display: "grid", gridTemplateColumns: "1fr 110px 130px 130px 130px",
                    padding: "6px 14px",
                    fontSize: 11,
                    background: idx % 2 === 0 ? "#fffbeb" : "#fff",
                    borderBottom: "1px solid #f1f5f9",
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 5, color: "#0f172a", fontWeight: 600 }}>
                      {isAcc ? <FiBookOpen size={11} color="#3730a3" /> : <FiTag size={11} color="#d97706" />}
                      <span>{itemName || `#${itemId}`}</span>
                      <span style={{ marginRight: 4, padding: "1px 5px", background: isAcc ? "#3730a3" : "#9333ea", color: "#fff", fontSize: 9, fontWeight: 700, borderRadius: 3, direction: "ltr" }}>
                        #{itemId}
                      </span>
                      {itemCode && <span style={{ marginRight: 3, fontSize: 9, color: "#94a3b8", direction: "ltr" }}>({itemCode})</span>}
                    </div>
                    <div style={{ textAlign: "center", color: "#64748b" }}>{fmt(cc.transactions_count)}</div>
                    <div style={{ textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", color: Number(cc.total_debit) > 0 ? "#15803d" : "#cbd5e1", fontWeight: 700 }}>{Number(cc.total_debit) > 0 ? fmt(cc.total_debit) : "—"}</div>
                    <div style={{ textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", color: Number(cc.total_credit) > 0 ? "#b45309" : "#cbd5e1", fontWeight: 700 }}>{Number(cc.total_credit) > 0 ? fmt(cc.total_credit) : "—"}</div>
                    <div style={{ textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", fontWeight: 800, color: Number(cc.net) >= 0 ? "#0f172a" : "#dc2626" }}>{fmt(cc.net)}</div>
                  </div>
                  );
                })}
              </>
            )}
            {unassignedTotals.count === 0 && (
              <div style={{ padding: 16, textAlign: "center", color: "#64748b", fontSize: 11, background: "#fffbeb" }}>
                ✓ كل مراكز التكلفة مصنّفة أو مخفاة
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function CCReportNode({ node, level, expandedIds, onToggle }) {
  const isGroup = node.node_type === "group";
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expandedIds[node.id];
  const indent = level * 22;

  // Group color intensity by level
  const groupBg = isGroup ? (
    level === 0 ? "#cffafe" :
    level === 1 ? "#ecfeff" :
    "#f0f9ff"
  ) : "#fff";
  const groupColor = level === 0 ? "#155e75" : level === 1 ? "#0e7490" : "#475569";

  return (
    <div>
      <div style={{
        display: "grid",
        gridTemplateColumns: "1fr 110px 130px 130px 130px",
        padding: "7px 12px",
        paddingRight: indent + 12,
        borderBottom: "1px solid #f1f5f9",
        background: groupBg,
        fontSize: 12,
        fontWeight: isGroup ? 700 : 500,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 5, color: isGroup ? groupColor : "#0f172a" }}>
          {isGroup && hasChildren ? (
            <button onClick={() => onToggle(node.id)} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, color: groupColor }}>
              {isExpanded ? <FiChevronDown size={13} /> : <FiChevronLeft size={13} />}
            </button>
          ) : (
            <span style={{ width: 14 }} />
          )}
          {isGroup
            ? <FiFolder size={13} />
            : node.leaf_kind === "account"
              ? <FiBookOpen size={11} color="#3730a3" />
              : <FiTag size={11} color="#9333ea" />}
          <span>{node.name}</span>
          {/* v3.3.650 — الرقم المرجعي للفرع (يُستخدم لسحب مجموعه في التدفقات النقدية) */}
          {node.ref_no && (
            <span style={{
              marginRight: 5, padding: "1px 7px", background: "#fef3c7", color: "#92400e",
              border: "1px solid #fcd34d", borderRadius: 4, fontSize: 9.5, fontWeight: 700, direction: "ltr",
            }} title={`الرقم المرجعي: ${node.ref_no} — استخدمه في التدفقات النقدية لسحب مجموع هذا الفرع`}>
              🔖 {node.ref_no}
            </span>
          )}
          {/* v3.3.515 — Show ID badge + code (color depends on leaf_kind) */}
          {!isGroup && (node.cost_center_id || node.journal_account_id) && (
            <>
              <span style={{
                marginRight: 5, padding: "1px 6px",
                background: node.leaf_kind === "account" ? "#3730a3" : "#9333ea",
                color: "#fff",
                fontSize: 9, fontWeight: 700, borderRadius: 3, direction: "ltr",
              }} title={node.leaf_kind === "account" ? "رقم الحساب" : "رقم مركز التكلفة"}>
                #{node.cost_center_id || node.journal_account_id}
              </span>
              {(node.cc_code || node.ja_code) && (
                <span style={{ marginRight: 3, fontSize: 9, color: "#94a3b8", direction: "ltr" }} title={node.leaf_kind === "account" ? "كود الحساب" : "كود مركز التكلفة"}>
                  ({node.cc_code || node.ja_code})
                </span>
              )}
            </>
          )}
        </div>
        <div style={{ textAlign: "center", color: "#64748b", fontWeight: 700 }}>{fmt(node.transactions_count)}</div>
        <div style={{ textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", color: Number(node.total_debit) > 0 ? "#15803d" : "#cbd5e1", fontWeight: 700 }}>{Number(node.total_debit) > 0 ? fmt(node.total_debit) : "—"}</div>
        <div style={{ textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", color: Number(node.total_credit) > 0 ? "#b45309" : "#cbd5e1", fontWeight: 700 }}>{Number(node.total_credit) > 0 ? fmt(node.total_credit) : "—"}</div>
        <div style={{ textAlign: "left", direction: "ltr", fontVariantNumeric: "tabular-nums", fontWeight: 800, color: Number(node.net) >= 0 ? "#0f172a" : "#dc2626" }}>{fmt(node.net)}</div>
      </div>
      {isGroup && isExpanded && hasChildren && node.children.map(c => (
        <CCReportNode key={c.id} node={c} level={level + 1} expandedIds={expandedIds} onToggle={onToggle} />
      ))}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: "flex", alignItems: "center", padding: "6px 0" }}>
      <div style={{ minWidth: 180, fontSize: 12, fontWeight: 700, color: "#475569" }}>{label}</div>
      <div style={{ flex: 1, fontSize: 12, color: "#0f172a" }}>{value || "—"}</div>
    </div>
  );
}

const th = { padding: "8px 10px", textAlign: "right", fontWeight: 700, fontSize: 11, color: "#475569", whiteSpace: "nowrap" };
const td = { padding: "7px 10px", textAlign: "right", fontSize: 11 };
const inputS = { padding: "6px 10px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 12, fontFamily: "inherit" };

/* ═══════ v3.4.646 — سجل المزامنات: آخر المزامنات (تلقائية/يدوية) ونجاحها أو فشلها ونتائجها وسطور خطواتها ═══════ */
const SYNC_KIND = { full: "مزامنة كاملة", vouchers: "مزامنة السندات" };
const SYNC_ST = { running: ["⏳ جارية", "#fef3c7", "#92400e"], success: ["✅ نجحت", "#dcfce7", "#166534"], error: ["❌ فشلت", "#fee2e2", "#991b1b"] };
const riyadhTime = (d) => (d ? new Date(d).toLocaleString("en-GB", { timeZone: "Asia/Riyadh", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }).replace(",", "") : "—");
const durTxt = (s) => { const n = Number(s); if (!isFinite(n) || s == null) return "—"; return n >= 60 ? `${Math.floor(n / 60)}د ${Math.round(n % 60)}ث` : `${n.toFixed(n < 10 ? 1 : 0)}ث`; };
function syncSummary(r) {
  const s = r.stats || {}; const f = (n) => Number(n || 0).toLocaleString("en-US");
  if (r.kind === "vouchers") return s.total != null ? `${f(s.upserted)} من ${f(s.total)} سند${s.failed ? ` · ${f(s.failed)} فشل` : ""}` : "";
  if (s.transactions_saved == null) return "";
  const parts = [`${f(s.transactions_saved)} قيد`, `${f(s.accounts_synced)} حساب`, `${f(s.cost_centers_synced)} مركز تكلفة`];
  if (s.drafts_saved) parts.push(`${f(s.drafts_saved)} مسودة`);
  if (s.transactions_wiped) parts.push(`${f(s.transactions_wiped)} محذوف من دفترة`);
  if (s.rows_failed) parts.push(`${f(s.rows_failed)} سطر فشل`);
  return parts.join(" · ");
}
function SyncRunsLog({ syncing }) {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(null); // id المفتوح
  const [lines, setLines] = useState([]);
  const load = useCallback(() => accounting.syncRuns(30).then((r) => setD(r.data)).catch(() => setD((x) => x || { runs: [], error: true })), []);
  const anyRunning = !!(d && (d.running || (d.runs || []).some((r) => r.status === "running")));
  useEffect(() => { load(); const t = setInterval(load, anyRunning || syncing ? 4000 : 60000); return () => clearInterval(t); }, [load, anyRunning, syncing]);
  useEffect(() => {
    if (!open) return;
    let alive = true; const get = () => accounting.syncRun(open).then((r) => { if (alive) setLines(r.data.run.log || []); }).catch(() => {});
    get(); const run = (d?.runs || []).find((x) => x.id === open); const t = run && run.status === "running" ? setInterval(get, 3000) : null;
    return () => { alive = false; if (t) clearInterval(t); };
  }, [open, d]);
  const runs = d?.runs || [];
  const last = runs[0];
  const th = { padding: "8px 10px", fontSize: 11, color: "#64748b", fontWeight: 800, textAlign: "right", borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap" };
  const td = { padding: "8px 10px", fontSize: 12, borderBottom: "1px solid #f1f5f9", verticalAlign: "top" };
  return (
    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 16, marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
        <div style={{ fontWeight: 900, fontSize: 15, color: "#0f172a" }}>🧾 سجل المزامنات</div>
        {last && <span style={{ padding: "2px 10px", borderRadius: 999, fontSize: 11.5, fontWeight: 800, background: SYNC_ST[last.status]?.[1], color: SYNC_ST[last.status]?.[2] }}>آخر مزامنة: {SYNC_ST[last.status]?.[0]} · {riyadhTime(last.started_at)}</span>}
        {d?.next_auto && <span style={{ fontSize: 11.5, color: "#64748b" }}>⏰ التلقائية التالية: {riyadhTime(d.next_auto)} (يوميًا {(d.auto_hours || []).map((h) => `${h}:00`).join(" و ")} بتوقيت الرياض)</span>}
        <button type="button" onClick={load} style={{ marginInlineStart: "auto", padding: "5px 12px", border: "1px solid #cbd5e1", background: "#fff", borderRadius: 8, fontFamily: "inherit", fontSize: 12, cursor: "pointer" }}>↻ تحديث</button>
      </div>
      {!d ? <div style={{ padding: 16, textAlign: "center", color: "#94a3b8", fontSize: 12 }}>جارٍ التحميل…</div>
      : !runs.length ? <div style={{ padding: 16, textAlign: "center", color: "#94a3b8", fontSize: 12 }}>لا توجد مزامنات مسجلة بعد — يبدأ التسجيل من أول مزامنة بعد هذا التحديث</div>
      : (
        <div style={{ overflowX: "auto", maxHeight: 520, overflowY: "auto", border: "1px solid #f1f5f9", borderRadius: 8 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", direction: "rtl" }}>
            <thead style={{ position: "sticky", top: 0, background: "#f8fafc", zIndex: 1 }}><tr>{["الحالة", "البداية (الرياض)", "المدة", "النوع", "التشغيل", "النتيجة", ""].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>
              {runs.map((r) => { const st = SYNC_ST[r.status] || SYNC_ST.error; const isOpen = open === r.id; return (
                <React.Fragment key={r.id}>
                  <tr onClick={() => { setLines([]); setOpen(isOpen ? null : r.id); }} style={{ cursor: "pointer", background: isOpen ? "#f8fafc" : r.status === "error" ? "#fffbfb" : "#fff" }}>
                    <td style={td}><span style={{ padding: "2px 9px", borderRadius: 999, fontSize: 11, fontWeight: 800, background: st[1], color: st[2], whiteSpace: "nowrap" }}>{st[0]}</span></td>
                    <td style={{ ...td, fontFamily: "monospace", whiteSpace: "nowrap", direction: "ltr", textAlign: "right" }}>{riyadhTime(r.started_at)}</td>
                    <td style={{ ...td, whiteSpace: "nowrap" }}>{r.status === "running" ? "…" : durTxt(r.elapsed_seconds)}</td>
                    <td style={{ ...td, whiteSpace: "nowrap" }}>{SYNC_KIND[r.kind] || r.kind}</td>
                    <td style={{ ...td, whiteSpace: "nowrap" }}>{r.trigger === "auto" ? "🤖 تلقائية" : `👤 ${r.user_name || r.user_email || "يدوية"}`}</td>
                    <td style={{ ...td, color: r.status === "error" ? "#b91c1c" : "#334155" }}>{r.status === "error" ? (r.error || "فشلت") : r.status === "running" ? (r.last_line || "جارية…") : syncSummary(r)}</td>
                    <td style={{ ...td, color: "#64748b", whiteSpace: "nowrap" }}>{isOpen ? "▲" : "▼"} السجل{r.lines ? ` (${r.lines})` : ""}</td>
                  </tr>
                  {isOpen && <tr><td colSpan={7} style={{ padding: 0, borderBottom: "1px solid #e2e8f0" }}>
                    <div style={{ background: "#0f172a", color: "#e2e8f0", fontFamily: "Consolas, monospace", fontSize: 11.5, lineHeight: 1.7, padding: "10px 12px", maxHeight: 320, overflowY: "auto", direction: "ltr", textAlign: "left" }}>
                      {!lines.length ? <div style={{ color: "#94a3b8" }}>{r.status === "running" ? "بانتظار أول سطر…" : "لا توجد سطور محفوظة لهذا التشغيل"}</div>
                        : lines.map((l, i) => <div key={i} dir="auto" style={{ color: l.l === "error" ? "#fca5a5" : l.l === "warn" ? "#fde68a" : "#e2e8f0", whiteSpace: "pre-wrap", wordBreak: "break-word" }}><span style={{ color: "#64748b" }}>{new Date(l.t).toLocaleTimeString("en-GB", { timeZone: "Asia/Riyadh", hour12: false })}</span> {l.m}</div>)}
                    </div>
                  </td></tr>}
                </React.Fragment>); })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
